import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import WeeklyCalendarView, { type CalendarEvent } from "@/components/WeeklyCalendarView";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";
import { CertificationQuizTab } from "@/components/CertificationQuizTab";
import { Legacy360Plan125ReferralLinkNotice } from "@/components/Legacy360Plan125CertGate";
import { AIChatBox, type ToolPartRendererProps } from "@/components/AIChatBox";
import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { isFullRepRecord } from "@/lib/repAuth";
import { resolveS360Name, stripRepCode } from "@/lib/s360Name";
import { withRefCode } from "@/lib/referralCookie";
import { resolveMainShareableLinks } from "@/lib/shareableLinks";
import { toast } from "sonner";
import {
  Users, DollarSign, TrendingUp, Copy, Check, ExternalLink,
  BookOpen, FileText, Video, Link2, Link, Activity, Calculator,
  ChevronRight, ChevronDown, ChevronLeft, Plus, Shield, BarChart3, Award, Zap, Lock,
  Briefcase, Upload, CheckCircle, Clock, XCircle, RefreshCw, Loader2,
  Eye, EyeOff, Settings, LogOut, Home, LayoutGrid, PieChart, FolderOpen, Folder,
  FileSearch, ArrowRight, UserPlus, Users2, BadgeCheck, AlertCircle,
  Megaphone, Bell, Info, AlertTriangle, CalendarCheck, CalendarX, Calendar, Repeat2,
  PauseCircle, Mail, CheckCircle2, Pencil, Pin, Building, MessageSquare, Send,
  History, Trash2, ChevronUp, X, Sparkles, Search, PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocusealForm } from "@docuseal/react";
import { ResourceModulePanel } from "@/components/ResourceModulePanel";
import { AnnouncementMedia } from "@/components/AnnouncementMedia";
import { FlywheelCarousel } from "@/components/FlywheelCarousel";
import { Legacy360PreAppointmentChecklist } from "@/components/Legacy360PreAppointmentChecklist";
import { Share360NextStepsChecklist } from "@/components/Share360NextStepsChecklist";
import {
  LEGACY360_SUBTYPE_CONFIG,
  resolveLegacy360Subtype,
  type Legacy360Subtype,
} from "@shared/legacy360";
import { computeScale360Funnel } from "@shared/scale360";
import AgreementSignature, { canSubmitSignature, isValidPrintedName, isValidSignatureDataUrl } from "@/components/AgreementSignature";
import AgreementViewer from "@/components/AgreementViewer";
import AgreementRevisionNotice from "@/components/AgreementRevisionNotice";
import type { SignatureSource } from "@shared/agreement";

// ─── Constants ─────────────────────────────────────────────────────────────────
// Sidebar agent-card badge artwork by agentLevel — Super Agency/Super Team
// intentionally absent (no dedicated badge exists for those levels yet; the
// sidebar simply shows no badge for them, per client direction 2026-09-01).
const AGENT_LEVEL_BADGE_SRC: Record<string, string> = {
  "Associate": "/associates/associate_badge.jpeg",
  "Senior Associate": "/associates/senior_associate_badge.jpeg",
  "Agency": "/associates/agency_badge.jpeg",
};

const STAGES = [
  { key: "lead_captured", label: "Lead Captured", color: "#1A6FD4" },
  { key: "audit_submitted", label: "Audit Submitted", color: "#C8920A" },
  { key: "follow_up_active", label: "Follow-Up Active", color: "#E8620A" },
  { key: "payment_received", label: "Payment Received", color: "#0FA8A8" },
  { key: "onboarding_submitted", label: "Onboarding Submitted", color: "#7B4FD4" },
  { key: "active_client", label: "Active Client", color: "#84ab4a" },
  { key: "lost_cold", label: "Lost / Cold", color: "#D93025" },
];

const VERTICAL_CONFIG: Record<string, { color: string; domain: string; certRequired: boolean }> = {
  "BizConnect360":      { color: "#374151", domain: "",                     certRequired: false },
  "Scale360":           { color: "#E8620A", domain: "scale360.wibiz.ai",    certRequired: true  },
  "Fundable360":        { color: "#84ab4a", domain: "fundable360.wibiz.ai", certRequired: true  },
  "Share360 Business":  { color: "#1A6FD4", domain: "share360.wibiz.ai",    certRequired: true  },
  "Share360 Nonprofit": { color: "#0FA8A8", domain: "share360.wibiz.ai",    certRequired: true  },
  "Legacy360":          { color: "#C8920A", domain: "legacy360.wibiz.ai",   certRequired: true  },
  "Media360":           { color: "#7B4FD4", domain: "media360.wibiz.ai",    certRequired: false },
};

const VERTICAL_TAGS: Record<string, string> = {
  "BizConnect360":      "Company resources",
  "Scale360":           "Automation & growth systems",
  "Fundable360":        "Business funding",
  "Share360 Business":  "Merchant services",
  "Share360 Nonprofit": "Nonprofit solutions",
  "Legacy360":          "Insurance & financial products",
  "Media360":           "Digital media",
};

const ALL_VERTICALS = Object.keys(VERTICAL_CONFIG);

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function fmtCommission(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function StageBadge({ stage, ghlStageName }: { stage: string; ghlStageName?: string | null }) {
  const s = STAGES.find((x) => x.key === stage);
  // GHL is source of truth for stage names — use ghlStageName when available
  const displayLabel = ghlStageName || s?.label || stage;
  const color = s?.color ?? "#6B7280";
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: `${color}18`, color }}>
      {displayLabel}
    </span>
  );
}

// ─── Consultation Calendar URLs per Vertical ──────────────────────────────────
// Used for "Schedule a Consultation" button in contact records (Fundable360, Share360, Legacy360)
const CONSULTATION_CALENDARS: Record<string, { label: string; url: string; color: string }[]> = {
  "Fundable360": [
    { label: "Fundable360 Consultation",        url: "https://api.wibiz.ai/widget/booking/jN4jUeNAZmQ7BvM1aGZF", color: "#84ab4a" },
  ],
  "Share360": [
    { label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" },
  ],
  "Share360 Business": [
    { label: "Share360 Business Consultation",  url: "https://api.wibiz.ai/widget/booking/xBgDVk2OWeq0DePGpuY8", color: "#1A6FD4" },
  ],
  "Share360 Nonprofit": [
    { label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" },
  ],
  "Legacy360": [
    { label: "Living Trust Consultation",        url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" },
  ],
  // Scale360 uses an audit-based flow — no consultation scheduling CTA for leads.
  "Media360": [
    { label: "Media360 Consultation",           url: "https://api.wibiz.ai/widget/booking/f7Bih3G3zfHl0y4osogZ", color: "#7B4FD4" },
  ],
};

// ─── Legacy360 pipeline sub-type routing ──────────────────────────────────────
// Legacy360 has one verticalName ("Legacy360") shared by several distinct GHL
// pipelines. ghlPipelineId (set whenever a lead is pulled in via "Refresh
// Stages"/CRM sync — see syncPipelineFromGhl in server/routers.ts) is the
// authoritative signal: it's populated straight from GHL with zero custom-field
// configuration required, which is what lets the 125 Plan form use a plain
// native GHL form (Create/Update Opportunity only) instead of the paid "Send
// Data via Webhook" workflow action. l360LeadType (set by /api/legacy360/webhook,
// when that push path IS used) is the fallback for leads with no ghlPipelineId
// yet. Living Trust is the default bucket.
type Legacy360PipelineKey = Legacy360Subtype;
function legacy360PipelineKeyForLead(lead: { l360LeadType?: string | null; l360FormName?: string | null; l360PageUrl?: string | null; ghlPipelineId?: string | null }): Legacy360PipelineKey {
  return resolveLegacy360Subtype(lead) ?? "living_trust";
}

function Legacy360SubtypeBadge({ lead }: { lead: { verticalName?: string | null; l360LeadType?: string | null; l360FormName?: string | null; l360PageUrl?: string | null; ghlPipelineId?: string | null } }) {
  if (lead.verticalName !== "Legacy360") return null;
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#C8920A]/35 bg-[#C8920A]/10 text-[#9A6B00]">
      {legacy360SubtypeLabelForLead(lead)}
    </span>
  );
}

function legacy360SubtypeLabelForLead(lead: { l360LeadType?: string | null; l360FormName?: string | null; l360PageUrl?: string | null; ghlPipelineId?: string | null }): string {
  return LEGACY360_SUBTYPE_CONFIG[legacy360PipelineKeyForLead(lead)].label;
}

// Legacy360's "Schedule a Consultation" calendar, keyed by pipeline sub-type
// (not just verticalName — all 4 Legacy360 pipelines share verticalName
// "Legacy360", so a verticalName-only lookup always resolved to whichever
// calendar happened to be listed first, silently offering Living Trust's
// calendar to 125 Plan/Advantage leads too). 125 Plan's calendar ID was
// supplied by the client 2026-07-31 (Rt2jjmae3NaN5m53YxAi).
const LEGACY360_CONSULTATION_CALENDARS: Record<Legacy360PipelineKey, { label: string; url: string; color: string }[]> = {
  living_trust: [
    { label: "Living Trust Consultation", url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" },
  ],
  "125plan": [
    { label: "Legacy360 - Section 125 Plan Discovery Meeting", url: "https://api.wibiz.ai/widget/booking/Rt2jjmae3NaN5m53YxAi", color: "#C8920A" },
  ],
  // New Agents is a certification roster, not a lead pipeline — no consultation booking applies.
  new_agent: [],
  // TODO: confirm whether Advantage should book its own calendar, or reuse Living Trust intentionally.
  advantage: [],
};

// Resolve the correct consultation-calendar options for a lead, routing
// Legacy360 by pipeline sub-type and every other vertical by verticalName as before.
function consultationCalendarsForLead(lead: { verticalName?: string | null; l360LeadType?: string | null; ghlPipelineId?: string | null }): { label: string; url: string; color: string }[] {
  if (lead.verticalName === "Legacy360") {
    return LEGACY360_CONSULTATION_CALENDARS[legacy360PipelineKeyForLead(lead)] ?? [];
  }
  return CONSULTATION_CALENDARS[lead.verticalName ?? ""] ?? [];
}

// ─── Schedule Consultation button — server-enforced eligibility gate ─────────
// Share360 Business/Nonprofit leads require their client-portal prerequisites
// complete; Legacy360 Living Trust leads require the associate's
// pre-appointment checklist complete (see server consultation.checkEligibility
// router). Every other vertical/pipeline/sub-type (125 Plan, New Agent,
// Advantage, Fundable360, Media360, Scale360) returns gated:false and behaves
// exactly as before — completely unchanged. While a gate is in shadow mode
// (its app_settings key unset), checkEligibility always returns eligible:true
// for that vertical, so this renders identically to the pre-gate link until
// the flag is flipped on.
function ScheduleConsultationButton({ lead, displayName }: { lead: any; displayName: string }) {
  const calOptions = consultationCalendarsForLead(lead);
  const utils = trpc.useUtils();
  const eligibilityQuery = trpc.consultation.checkEligibility.useQuery(
    { leadId: lead.id },
    { enabled: !!lead.id && calOptions.length > 0, staleTime: 30_000 },
  );

  if (calOptions.length === 0) return null;
  const { url, color } = calOptions[0];
  const bookingUrl = `${url}?first_name=${encodeURIComponent(lead.firstName ?? "")}&last_name=${encodeURIComponent(lead.lastName ?? "")}&name=${encodeURIComponent(displayName)}&email=${encodeURIComponent(lead.email ?? "")}&phone=${encodeURIComponent((lead as any).phone ?? "")}&ref=${encodeURIComponent(lead.repCode ?? "")}&ref_code=${encodeURIComponent(lead.repCode ?? "")}&referring_channel=bc360`;

  const data = eligibilityQuery.data;
  if (!data || data.eligible) {
    return (
      <a
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={async (e) => {
          // Ungated vertical, or eligibility hasn't loaded yet: behave exactly
          // like the plain link this replaces — no re-check, no delay.
          if (!data?.gated) return;
          // Gated vertical: re-verify fresh at click time so prerequisites
          // invalidated between page load and click can't slip through once
          // the gate is enforced (closes the stale-render race the plain
          // link had).
          e.preventDefault();
          const fresh = await utils.consultation.checkEligibility.fetch({ leadId: lead.id });
          if (fresh.eligible) {
            window.open(bookingUrl, "_blank", "noopener,noreferrer");
          } else {
            toast.error(`Not ready to schedule yet — missing: ${fresh.missingDocs.join(", ") || "requirements"}`);
            utils.consultation.checkEligibility.invalidate({ leadId: lead.id });
          }
        }}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-[10px] transition-colors border"
        style={{ background: `${color}15`, color, borderColor: `${color}40` }}
      >
        <Calendar className="w-3.5 h-3.5" /> Schedule a Consultation
      </a>
    );
  }

  // Gate enforced and requirements incomplete — shown disabled with what's
  // missing, never silently hidden.
  const missing = data.missingDocs;
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        disabled
        title={`Missing: ${missing.join(", ")}`}
        className="flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-[10px] border cursor-not-allowed opacity-60"
        style={{ background: "#F4EEE3", color: "#9C9285", borderColor: "#EBE2D4" }}
      >
        <Calendar className="w-3.5 h-3.5" /> Not Ready Yet
      </button>
      <div className="text-[10px] text-[#9C9285] text-right max-w-[220px]">Missing: {missing.join(", ")}</div>
    </div>
  );
}

// ─── Agent Pipeline View Config (GHL pipelines visible to agents) ─────────────
// Pipeline B (Funding) is excluded — internalOnly pipelines are hidden from agents
const AGENT_VERTICAL_PIPELINES: Record<string, { pipelineId: string; label: string; fallbackStages: string[]; legacy360Key?: Legacy360PipelineKey }[]> = {
  "Scale360": [
    { pipelineId: "mzgOuQhHmkDuixcpiSbj", label: "Scale360 Partner Funnel", fallbackStages: ["New Lead", "Initial Outreach", "Appointment Booked", "Appointment Completed", "Qualified", "Enrolled", "Onboarding", "Active Client", "Funded", "Graduated", "Inactive / Lost"] },
  ],
  "Fundable360": [
    { pipelineId: "UtddGTWZhyTcaRSWEGKm", label: "Pipeline A — Enrollment Process", fallbackStages: ["New Lead", "Appointment Booked", "Appointment Completed", "Sent Information", "Booked Follow Up", "Enrolled", "Onboarded Credit Building", "Inactive/Lost"] },
  ],
  "Share360 Nonprofit": [
    { pipelineId: "czZqnGQZru1yUcz7vHM6", label: "Pipeline A — Nonprofit Track", fallbackStages: ["New Lead", "Appointment Booked", "Booked Follow Up", "Charity Documents", "Charity Package", "Collateral Review", "Onboarded Receiving Donations", "Inactive"] },
  ],
  "Share360 Business": [
    { pipelineId: "le4RyNh4YBlLMVeVslHt", label: "Pipeline A — Business Track", fallbackStages: ["New Lead", "Appointment Booked", "Statements", "Rate Review", "Merch Info Form", "Application", "Underwriting Review/Request", "VAR/Deployment", "Onboarded Processing", "Declined", "Inactive"] },
  ],
  // New Agents pipeline is intentionally omitted here — admin-only per client
  // decision 2026-07-30 (associates don't see the company-wide certified-agent
  // roster). See VERTICALS["legacy360"] in AdminDashboard.tsx for that view.
  "Legacy360": [
    { pipelineId: "AtnyZ5hxQGhpBwTO8gro", label: "Living Trust", fallbackStages: ["New Lead", "Appointment Booked", "Consultation Completed", "Payment Received", "Documents Drafted", "Documents Signed", "Trust Funded", "Trust Recorded"], legacy360Key: "living_trust" },
    { pipelineId: "hoIcCAyZ8Wg0wPPNzosy", label: "125 Plan", fallbackStages: ["New Lead", "Appointment Booked", "Proposal Presentation", "Onboarding", "Inactive"], legacy360Key: "125plan" },
    // Stage names are a recommendation pending client confirmation in Wibiz — see
    // memory/project_legacy360_three_pipelines.md. Update this array if renamed.
    { pipelineId: "EKY9Qps5kZDkRBbLjL4t", label: "Advantage", fallbackStages: ["Information Submitted", "License & Background Review", "Interview Scheduled", "Contract Offered", "Contracted (Active Agent)", "Not a Fit / Inactive"], legacy360Key: "advantage" },
  ],
  "Media360": [
    { pipelineId: "MKZZiFgADSTL2iNuOuvw", label: "Media360 Pipeline", fallbackStages: ["Form Submitted", "Strategy Session Scheduled", "Strategy Session Completed", "Proposal Sent", "Active Client", "Closed/Lost"] },
  ],
};

// ─── Lead Pipeline Bar ───────────────────────────────────────────────────────
// Fetches live GHL stages for the lead's vertical so the bar always shows the
// real pipeline — not hardcoded fallbacks that can go stale.
function LeadPipelineBar({ verticalName, ghlStageName, color, l360LeadType, ghlPipelineId }: {
  verticalName: string | null | undefined;
  ghlStageName: string | null | undefined;
  color: string;
  l360LeadType?: string | null;
  ghlPipelineId?: string | null;
}) {
  const pipelines = AGENT_VERTICAL_PIPELINES[verticalName ?? ""] ?? [];
  // Legacy360 has 3 pipelines sharing one verticalName — pick the one matching
  // this lead's actual pipeline instead of always defaulting to index 0.
  const activePipeline = verticalName === "Legacy360"
    ? (pipelines.find(p => p.legacy360Key === legacy360PipelineKeyForLead({ l360LeadType, ghlPipelineId })) ?? pipelines[0])
    : pipelines[0];

  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { pipelineId: activePipeline?.pipelineId ?? "" },
    { enabled: !!activePipeline?.pipelineId, staleTime: 5 * 60 * 1000 }
  );

  const stageNames: string[] = (ghlStagesQuery.data && ghlStagesQuery.data.length > 0)
    ? ghlStagesQuery.data.map((s: { id: string; name: string }) => s.name)
    : (activePipeline?.fallbackStages ?? ["New Lead", "Active Client"]);

  const currIdx = ghlStageName ? stageNames.indexOf(ghlStageName) : -1;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold text-[#9C9285] uppercase tracking-wide">Pipeline Stage</div>
        {ghlStageName ? (
          <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${color}18`, color }}>
            {ghlStageName}
          </span>
        ) : (
          <span className="text-[10.5px] text-[#BCB2A4]">Stage not synced</span>
        )}
      </div>
      {ghlStagesQuery.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-[11px] text-[#9C9285]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live stages…
        </div>
      ) : (
        <div className="flex rounded-[10px] overflow-hidden border border-[#EBE2D4]">
          {stageNames.map((stageName, i) => {
            const done = currIdx > -1 && i < currIdx;
            const current = i === currIdx;
            // Show abbreviated label — first word, but avoid duplicate truncations
            // e.g. "Appointment Booked" → "Appt. Booked", "Appointment Completed" → "Appt. Done"
            const shortened = stageName
              .replace("Appointment Booked", "Appt. Booked")
              .replace("Appointment Completed", "Appt. Done")
              .replace("Inactive / Lost", "Inactive")
              .split(" ").slice(0, 2).join(" ");
            return (
              <div
                key={stageName}
                title={stageName}
                className="flex-1 text-center py-2 px-1 text-[9.5px] font-semibold truncate leading-tight"
                style={{
                  background: done ? "#159189" : current ? color : "#F4EEE3",
                  color: (done || current) ? "#fff" : "#9C9285",
                  minWidth: 0,
                }}
              >
                {shortened}
              </div>
            );
          })}
        </div>
      )}
      {!activePipeline && (
        <p className="text-[11px] text-[#BCB2A4] mt-1">No pipeline configured for {verticalName}.</p>
      )}
    </div>
  );
}

function AgentPipelineView({ vertical, leads, color, onLeadClick, repCode }: {
  vertical: string; leads: LeadRow[]; color: string; onLeadClick: (id: number) => void; repCode?: string;
}) {
  const pipelines = AGENT_VERTICAL_PIPELINES[vertical] ?? [];
  const [activePipelineIdx, setActivePipelineIdx] = useState(0);
  const activePipeline = pipelines[activePipelineIdx];
  const utils = trpc.useUtils();
  const syncMutation = trpc.crm.syncPipelineFromGhl.useMutation();
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  // Auto-sync: pull fresh GHL data when the pipeline tab opens (silently, once per 5 min per pipeline).
  const lastAutoSyncRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!activePipeline?.pipelineId) return;
    const key = activePipeline.pipelineId;
    const last = lastAutoSyncRef.current[key] ?? 0;
    const FIVE_MIN = 5 * 60 * 1000;
    if (Date.now() - last < FIVE_MIN) return; // already synced recently
    lastAutoSyncRef.current[key] = Date.now();
    syncMutation.mutateAsync({ pipelineId: activePipeline.pipelineId, verticalName: vertical, callerRepCode: repCode })
      .then(() => {
        utils.lead.list.invalidate();
        utils.lead.getGhlStages.invalidate({ pipelineId: activePipeline.pipelineId });
      })
      .catch(() => {}); // silent — manual "Refresh Stages" still available as fallback
  }, [activePipeline?.pipelineId]);

  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { pipelineId: activePipeline?.pipelineId ?? "" },
    { enabled: !!activePipeline?.pipelineId, staleTime: 5 * 60 * 1000 }
  );

  const stageNames: string[] = (ghlStagesQuery.data && ghlStagesQuery.data.length > 0)
    ? ghlStagesQuery.data.map((s: { id: string; name: string }) => s.name)
    : (activePipeline?.fallbackStages ?? []);

  const getLeadGhlStage = (lead: LeadRow): string => {
    const name = (lead as any).ghlStageName;
    // Only use ghlStageName if it matches a known column — otherwise fall back to first stage.
    // This prevents leads from becoming invisible when GHL moves them to a stage that isn't
    // yet in our local fallbackStages list (e.g. after "Appointment Booked" automation fires).
    if (name && stageNames.includes(name)) return name;
    return stageNames[0] || "New Lead";
  };

  const handleSync = async () => {
    if (!activePipeline) return;
    setSyncMsg(null);
    try {
      const result = await syncMutation.mutateAsync({ pipelineId: activePipeline.pipelineId, verticalName: vertical, callerRepCode: repCode });
      const myCount = repCode ? result.agentLeadCount : result.upserted;
      setSyncMsg(`Synced ${myCount} of your leads (${result.total} total in pipeline)`);
      utils.lead.list.invalidate();
      utils.lead.getGhlStages.invalidate({ pipelineId: activePipeline.pipelineId });
    } catch (e: any) {
      setSyncMsg("Sync failed — try again");
    }
  };

  if (pipelines.length === 0) {
    return <div className="py-8 text-center text-gray-400 text-sm">No pipeline configured for {vertical}.</div>;
  }

  // Legacy360's 3 pipelines share stage names (e.g. "New Lead" appears in both
  // Living Trust and 125 Plan) — filter to the active tab's leads by pipeline
  // identity first, or a lead could show up under the wrong pipeline's matching
  // stage name.
  const pipelineLeads = (vertical === "Legacy360" && activePipeline?.legacy360Key)
    ? leads.filter(l => legacy360PipelineKeyForLead(l as any) === activePipeline.legacy360Key)
    : leads;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2">
          {pipelines.length > 1 && pipelines.map((p, i) => (
            <button
              key={p.pipelineId}
              onClick={() => setActivePipelineIdx(i)}
              className={`text-sm px-4 py-2 rounded-lg font-semibold transition-all ${activePipelineIdx === i ? "text-white shadow-sm" : "bg-white border border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3]"}`}
              style={activePipelineIdx === i ? { background: color } : {}}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#EBE2D4] bg-white text-[#6E665A] hover:bg-[#F4EEE3] disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3 h-3 ${syncMutation.isPending ? "animate-spin" : ""}`} />
          {syncMutation.isPending ? "Syncing..." : "Refresh Stages"}
        </button>
      </div>
      {syncMsg && (
        <div className={`text-xs mb-3 px-3 py-1.5 rounded-lg ${syncMsg.includes("failed") ? "bg-red-50 text-red-600" : "bg-[#EFF4E2] text-[#5F8A2E]"}`}>
          {syncMsg}
        </div>
      )}
      {ghlStagesQuery.isLoading && (
        <div className="flex items-center gap-2 text-xs text-[#9C9285] mb-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live stages from Wibiz...
        </div>
      )}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3" style={{ minWidth: `${stageNames.length * 180}px` }}>
          {stageNames.map((stageName) => {
            const stageLeads = pipelineLeads.filter(l => getLeadGhlStage(l) === stageName);
            return (
              <div key={stageName} className="w-44 flex-shrink-0">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-[11px] font-bold uppercase tracking-wide truncate" style={{ color }}>
                    {stageName}
                  </span>
                  <span className="text-xs font-bold rounded-full px-1.5 py-0.5 ml-1 flex-shrink-0" style={{ background: `${color}15`, color }}>
                    {stageLeads.length}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {stageLeads.map((lead) => (
                    <div
                      key={lead.id}
                      className="bg-white border border-[#EBE2D4] rounded-xl p-2.5 hover:border-[#D4C9B8] hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => onLeadClick(lead.id)}
                    >
                      {(() => {
                        const isBiz = lead.verticalName === "Share360 Business";
                        const isNP  = lead.verticalName === "Share360 Nonprofit";
                        // GHL workflows append " - {repCode}" to company/org names — strip it for display
                        const stripRC = (s: string | null | undefined): string | null => {
                          if (!s) return null;
                          if (lead.repCode && s.endsWith(` - ${lead.repCode}`)) return s.slice(0, -(` - ${lead.repCode}`).length);
                          return s;
                        };
                        const contactFull = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
                        const rawOrgBiz = isBiz ? (lead.sh360BusinessName || lead.businessName)
                                        : isNP  ? (lead.sh360OrgName || lead.businessName)
                                        : null;
                        const cleanedOrg = stripRC(rawOrgBiz);
                        const cleanedContact = stripRC(contactFull) || null;
                        const primaryName = (isBiz || isNP)
                          ? (cleanedOrg || cleanedContact || lead.email?.split("@")[0] || "Unknown")
                          : (cleanedContact || stripRC(lead.businessName) || lead.email?.split("@")[0] || "Unknown");
                        // Secondary:
                        //   Business  → contact person (firstName+lastName), fall back to email
                        //   Nonprofit → sh360BusinessName ("Business Contact Name"), fall back to contact name, then email
                        //   Others    → businessName or email
                        const secondaryLine = isBiz
                          ? (cleanedContact && cleanedContact !== primaryName ? cleanedContact : lead.email)
                          : isNP
                          ? (stripRC(lead.sh360BusinessName) || (cleanedContact && cleanedContact !== primaryName ? cleanedContact : null) || lead.email)
                          : (stripRC(lead.businessName) || lead.email);
                        return (
                          <>
                            <div className="font-semibold text-xs text-[#2C2723] truncate mb-0.5">{primaryName}</div>
                            <div className="text-xs text-[#9C9285] truncate mb-1">{secondaryLine}</div>
                            {lead.repCode && (
                              <span className="text-[10px] font-mono text-[#BCB2A4] block">{lead.repCode}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="bg-[#FBF7F0] border border-dashed rounded-xl p-3 text-center" style={{ borderColor: `${color}35` }}>
                      <div className="text-xs text-[#BCB2A4]">Empty</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3 mt-4">
        <Lock className="w-3.5 h-3.5 flex-shrink-0" />
        Stage updates are managed by your BC360 admin. Contact them to move leads through the pipeline.
      </div>
    </div>
  );
}

// ─── Vertical Reporting View (live GHL stage breakdown) ───────────────────────
function VerticalReportingView({ vertical, leads, color, clientLeads }: {
  vertical: string; leads: LeadRow[]; color: string; clientLeads: LeadRow[];
}) {
  const pipelines = AGENT_VERTICAL_PIPELINES[vertical] ?? [];
  const primaryPipeline = pipelines[0];

  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { pipelineId: primaryPipeline?.pipelineId ?? "" },
    { enabled: !!primaryPipeline?.pipelineId, staleTime: 5 * 60 * 1000 }
  );

  const stageNames: string[] = (ghlStagesQuery.data && ghlStagesQuery.data.length > 0)
    ? ghlStagesQuery.data.map((s: { id: string; name: string }) => s.name)
    : (primaryPipeline?.fallbackStages ?? []);

  const totalLeads = leads.length;
  const activeClients = clientLeads.length;
  const convRate = totalLeads > 0 ? Math.round((activeClients / totalLeads) * 100) : 0;

  // Legacy360's pipelines share stage names — scope the breakdown chart to the
  // primary (first-tab) pipeline's leads only, same reasoning as AgentPipelineView.
  const primaryPipelineLeads = (vertical === "Legacy360" && primaryPipeline?.legacy360Key)
    ? leads.filter(l => legacy360PipelineKeyForLead(l as any) === primaryPipeline.legacy360Key)
    : leads;

  const stageBreakdown = stageNames.map((stageName, idx) => ({
    name: stageName,
    // For the first stage, also count leads with no ghlStageName (they default there)
    count: primaryPipelineLeads.filter(l => {
      const s = (l as any).ghlStageName;
      return s ? s === stageName : idx === 0;
    }).length,
  }));

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-900 mb-2">{vertical} — Reporting</h2>
      <p className="text-sm text-gray-900 mb-5">Performance metrics for your {vertical} pipeline.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Leads", value: totalLeads, c: "#1A6FD4" },
          { label: "Active Clients", value: activeClients, c: color },
          { label: "Conversion Rate", value: `${convRate}%`, c: "#0FA8A8" },
        ].map(({ label, value, c }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="text-3xl font-bold font-mono mb-1" style={{ color: c }}>{value}</div>
            <div className="text-sm font-semibold text-gray-700">{label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-bold text-gray-700">Pipeline Stage Breakdown</div>
          {ghlStagesQuery.isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
        </div>
        {stageBreakdown.length === 0 ? (
          <div className="py-6 text-center text-gray-400 text-sm">No stage data available</div>
        ) : (
          <div className="space-y-3">
            {stageBreakdown.map(s => (
              <div key={s.name} className="flex items-center gap-3">
                <div className="w-40 text-xs text-gray-500 truncate flex-shrink-0">{s.name}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${totalLeads > 0 ? (s.count / totalLeads) * 100 : 0}%`, background: color }}
                  />
                </div>
                <div className="w-6 text-xs font-bold text-right" style={{ color }}>{s.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Per-Vertical Training Calendars ──────────────────────────────────────────
// Calendar IDs per vertical (GHL class booking — open scheduling for agents)
const TRAINING_CALENDARS: { key: string; label: string; calendarId: string; color: string }[] = [
  { key: "general",           label: "BC360 General",    calendarId: "2ImDJVXE23GfW6WmVsMD", color: "#84ab4a" },
  { key: "Fundable360",       label: "Fundable360",      calendarId: "7LTivsApkZroz3LlZSiP", color: "#84ab4a" },
  { key: "Scale360",          label: "Scale360",         calendarId: "BNSDF3gHtSZuzgLMkijr", color: "#E8620A" },
  { key: "Share360",          label: "Share360",         calendarId: "FqI0SWCgqb5629AyScY6", color: "#1A6FD4" },
  { key: "Legacy360",         label: "Legacy360",        calendarId: "MokOx22qfOnONZ4u9BKx", color: "#C8920A" },
  { key: "Media360",          label: "Media360",         calendarId: "PCiM3Ok9GCLeycWuv8UK", color: "#7B4FD4" },
];

function TrainingCalendarTab({
  certifiedVerticals,
  allowedVerticals,
  repFirstName,
  repLastName,
  repEmail,
  repPhone,
}: {
  certifiedVerticals?: string[];
  allowedVerticals?: string[] | null;
  repFirstName?: string;
  repLastName?: string;
  repEmail?: string;
  repPhone?: string;
}) {
  // Certification gate: same logic as sidebar vertical access
  // General is always open; vertical calendars require certification (certRequired=true) AND being in certifiedVerticals
  const isCertifiedForCal = (calKey: string) => {
    if (calKey === "general") return true;
    // Share360 covers both Share360 Business and Share360 Nonprofit
    if (calKey === "Share360") {
      const cfg1 = VERTICAL_CONFIG["Share360 Business"];
      const cfg2 = VERTICAL_CONFIG["Share360 Nonprofit"];
      if (!cfg1?.certRequired && !cfg2?.certRequired) return true;
      if (!certifiedVerticals) return true; // no cert data yet = open
      return certifiedVerticals.some(v => v.startsWith("Share360"));
    }
    const cfg = VERTICAL_CONFIG[calKey];
    if (!cfg?.certRequired) return true; // Media360 has certRequired=false
    if (!certifiedVerticals) return true; // loading state = open
    return certifiedVerticals.includes(calKey);
  };

  // Only show tabs the agent is certified for (General always shown)
  const visibleCalendars = TRAINING_CALENDARS.filter(cal => {
    if (cal.key === "general") return true;
    // Also respect allowedVerticals (admin-assigned verticals)
    if (allowedVerticals) {
      if (cal.key === "Share360") {
        if (!allowedVerticals.some(v => v.startsWith("Share360"))) return false;
      } else if (!allowedVerticals.includes(cal.key) && cal.key !== "general") {
        return false;
      }
    }
    return isCertifiedForCal(cal.key);
  });

  const [activeCalKey, setActiveCalKey] = useState("general");
  const activeCal = visibleCalendars.find(c => c.key === activeCalKey) ?? visibleCalendars[0];
  // Build embed URL with pre-filled contact info so agents don't have to retype their details
  const embedUrl = (() => {
    if (!activeCal) return null;
    const base = `https://api.wibiz.ai/widget/booking/${activeCal.calendarId}`;
    const params = new URLSearchParams();
    if (repFirstName) params.set("first_name", repFirstName);
    if (repLastName) params.set("last_name", repLastName);
    if (repEmail) params.set("email", repEmail);
    if (repPhone) params.set("phone", repPhone);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  })();

  // If active tab was removed (vertical no longer certified), reset to general
  useEffect(() => {
    if (!visibleCalendars.find(c => c.key === activeCalKey)) {
      setActiveCalKey("general");
    }
  }, [visibleCalendars.length]);

  // Count locked verticals for informational message
  const lockedCount = TRAINING_CALENDARS.filter(c => c.key !== "general" && !isCertifiedForCal(c.key)).length;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">BizConnect360 Calendar</h2>
        <p className="text-sm text-gray-900">
          Book upcoming training sessions for your certified verticals.
          {lockedCount > 0 && (
            <span className="ml-1 text-amber-500">
              {lockedCount} vertical{lockedCount > 1 ? "s" : ""} locked — complete certification to unlock.
            </span>
          )}
        </p>
      </div>

      {/* Vertical tabs */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-gray-100 overflow-x-auto">
          {visibleCalendars.map(cal => (
            <button
              key={cal.key}
              onClick={() => setActiveCalKey(cal.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                activeCalKey === cal.key
                  ? "border-b-2 text-gray-900 bg-gray-50"
                  : "border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50/50"
              }`}
              style={activeCalKey === cal.key ? { borderBottomColor: cal.color } : {}}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cal.color }} />
              {cal.label}
            </button>
          ))}
          <div className="ml-auto pb-2">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Open booking · Scheduled by BC360</span>
          </div>
        </div>

        {!embedUrl ? (
          <div className="py-16 text-center text-gray-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No training sessions available.</p>
          </div>
        ) : (
          <iframe
            key={activeCal?.calendarId}
            src={embedUrl}
            style={{ width: "100%", height: "700px", border: "none" }}
            title={`${activeCal?.label} Training Calendar`}
          />
        )}
      </div>
    </div>
  );
}

// Legacy compact wrapper kept for backward compat — not used in main dashboard
function TrainingCalendarTabCompact({ compact = false }: { compact?: boolean }) {
  const settingQuery = trpc.settings.get.useQuery({ key: "ghl_calendar_training" });
  const calendarId = settingQuery.data?.value;
  const embedUrl = calendarId ? `https://api.wibiz.ai/widget/calendar/${calendarId}` : null;

  if (!compact) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#84ab4a]" />
          <span className="text-sm font-semibold text-gray-700">Upcoming Training Sessions</span>
        </div>
        <span className="text-xs text-gray-400">Scheduled by BC360</span>
      </div>
      {settingQuery.isLoading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : !embedUrl ? (
        <div className="py-8 text-center text-gray-400">
          <BookOpen className="w-6 h-6 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No training sessions scheduled yet.</p>
        </div>
      ) : (
        <iframe src={embedUrl} style={{ width: "100%", height: "380px", border: "none" }} title="Training Calendar" />
      )}
    </div>
  );
}

// Placeholder to keep old non-compact path — replaced by new component above
function _OldTrainingCalendarTabNonCompact() {
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Training Calendar</h2>
        <p className="text-sm text-gray-900">View upcoming training sessions scheduled by BC360. Sessions are open to all active agents — no booking required.</p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <BookOpen className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="font-semibold text-amber-800 mb-1">No Training Sessions Scheduled</p>
          <p className="text-sm text-amber-600">BC360 has not published any upcoming training sessions yet. Check back soon or contact your admin.</p>
      </div>
    </div>
  );
}

function _OldTrainingCalendarTabNonCompactWithEmbed({ embedUrl }: { embedUrl: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-[#84ab4a]" />
        <span className="text-sm font-semibold text-gray-700">Upcoming Sessions</span>
        <span className="ml-auto text-xs text-gray-400">Scheduled by BC360 — view only</span>
      </div>
      <iframe
        src={embedUrl}
        style={{ width: "100%", height: "700px", border: "none" }}
        title="Training Calendar"
      />
    </div>
  );
}

// ─── Announcements Section ──────────────────────────────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-700", icon: AlertTriangle, iconColor: "text-red-500", headerBg: "bg-red-50" },
  high:   { label: "High",   bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", icon: Bell, iconColor: "text-amber-500", headerBg: "bg-amber-50" },
  normal: { label: "Info",   bg: "bg-white", border: "border-gray-100", badge: "bg-blue-50 text-blue-600", icon: Info, iconColor: "text-blue-400", headerBg: "bg-gray-50" },
};

// ─── Announcements Section (below calendar) ──────────────────────────────────
function AnnouncementsSection() {
  const { data: announcements = [], isLoading } = trpc.announcements.list.useQuery();
  // Per-item expand: track which items are expanded (default: all collapsed — header acts as preview)
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(() => new Set());

  const toggleItem = (id: string | number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="w-4 h-4 text-[#84ab4a]" />
        <span className="text-sm font-bold text-gray-800">Announcements</span>
        {announcements.length > 0 && (
          <span className="bg-[#84ab4a]/15 text-[#84ab4a] text-xs font-bold px-2 py-0.5 rounded-full">{announcements.length}</span>
        )}
      </div>
      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin opacity-40" />
          Loading announcements...
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
          <Megaphone className="w-8 h-8 mx-auto mb-2 text-gray-200" />
          <p className="text-sm text-gray-400">No announcements at this time.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {announcements.map((ann: any) => {
            const cfg = PRIORITY_CONFIG[ann.priority as keyof typeof PRIORITY_CONFIG] ?? PRIORITY_CONFIG.normal;
            const IconComp = cfg.icon;
            const displayDate = ann.displayDate ?? ann.createdAt;
            const isExpanded = expandedIds.has(ann.id);
            const snippet = ann.body?.length > 80 ? ann.body.slice(0, 80).trim() + "…" : ann.body;
            return (
              <div key={ann.id} className={`rounded-2xl border shadow-sm overflow-hidden ${cfg.bg} ${cfg.border}`}>
                {/* Header — always visible, click to expand/collapse */}
                <button
                  onClick={() => toggleItem(ann.id)}
                  className={`w-full px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-left transition-colors hover:opacity-90 ${cfg.headerBg}`}
                >
                  <IconComp className={`w-4 h-4 flex-shrink-0 ${cfg.iconColor}`} />
                  {ann.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" aria-label="Pinned" />}
                  {/* min-w-[140px] keeps a readable floor — if the panel is too
                      narrow for the badge/date/chevron to share this line with
                      a 140px-wide title, the title wraps onto its own full-width
                      line below instead of every item getting squeezed down to
                      near-zero width (client-reported 2026-09-02: title text
                      was wrapping one word per line at a dragged-narrow panel width). */}
                  <div className="flex-1 min-w-[140px]">
                    <span className="font-semibold text-gray-900 text-sm line-clamp-2">{ann.title}</span>
                    {!isExpanded && snippet && (
                      <span className="text-xs text-gray-500 ml-2 truncate">{snippet}</span>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${cfg.badge}`}>{cfg.label}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{new Date(displayDate).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {/* Body — only when expanded */}
                {isExpanded && (
                  <div className={`px-5 py-4 border-t ${cfg.border}`}>
                    <AnnouncementMedia mediaUrl={ann.mediaUrl} mediaType={ann.mediaType} title={ann.title} enableLightbox />
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{ann.body}</p>
                    <p className="text-xs text-gray-400 mt-2">{new Date(displayDate).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Announcement Bell (top-nav dropdown) ────────────────────────────────────
const ANN_SEEN_KEY = "bc360_ann_last_seen_at";

function AnnouncementBell() {
  const { data: announcements = [] } = trpc.announcements.list.useQuery();
  const [open, setOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<number>(() => {
    try { return parseInt(localStorage.getItem(ANN_SEEN_KEY) ?? "0", 10) || 0; } catch { return 0; }
  });
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = announcements.filter((a: any) => new Date(a.displayDate ?? a.createdAt).getTime() > lastSeenAt).length;

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open) {
      const now = Date.now();
      setLastSeenAt(now);
      try { localStorage.setItem(ANN_SEEN_KEY, String(now)); } catch {}
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        title="Announcements"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 border-2 border-white" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-96 max-h-[520px] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-100 z-50">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-[#84ab4a]" />
            <span className="text-sm font-bold text-gray-900">Announcements</span>
            {announcements.length > 0 && (
              <span className="ml-auto bg-[#84ab4a]/15 text-[#84ab4a] text-xs font-bold px-2 py-0.5 rounded-full">{announcements.length}</span>
            )}
          </div>
          {announcements.length === 0 ? (
            <div className="p-8 text-center">
              <Megaphone className="w-8 h-8 mx-auto mb-2 text-gray-200" />
              <p className="text-sm text-gray-400">No announcements at this time.</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-gray-50">
              {announcements.map((ann: any) => {
                const cfg = PRIORITY_CONFIG[ann.priority as keyof typeof PRIORITY_CONFIG] ?? PRIORITY_CONFIG.normal;
                const IconComp = cfg.icon;
                const displayDate = ann.displayDate ?? ann.createdAt;
                const isNew = new Date(displayDate).getTime() > lastSeenAt;
                return (
                  <div key={ann.id} className={`p-4 ${isNew ? "bg-[#84ab4a]/4" : ""}`}>
                    <div className="flex items-start gap-2.5 mb-1.5">
                      <IconComp className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.iconColor}`} />
                      {ann.pinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />}
                      <span className="font-semibold text-gray-900 text-sm flex-1 leading-snug">{ann.title}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isNew && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${cfg.badge}`}>{cfg.label}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap ml-6">{ann.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1.5 ml-6">{new Date(displayDate).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Referral Milestone Bell (top-nav dropdown) ──────────────────────────────
// One-time confirmations that a referred client reached a trust-building milestone
// (Comprehensive Analysis / Revenue Audit completed). Unlike AnnouncementBell, these
// are server-persisted and dismissed permanently via markSeen — not a per-session
// localStorage timestamp — because each row must be shown exactly once, ever.
const MILESTONE_LABELS: Record<string, string> = {
  comprehensive_analysis: "completed their Comprehensive Analysis",
  revenue_audit: "completed their Revenue Audit",
};

function ReferralMilestoneBell({ repCode }: { repCode: string }) {
  const utils = trpc.useUtils();
  const { data: milestones = [] } = trpc.repMilestone.list.useQuery({ repCode }, { enabled: !!repCode, refetchInterval: 60_000 });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const markSeen = trpc.repMilestone.markSeen.useMutation({
    onSuccess: () => utils.repMilestone.list.invalidate({ repCode }),
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!repCode) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        title="Referral milestones"
      >
        <PartyPopper className="w-5 h-5" />
        {milestones.length > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 border-2 border-white" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-96 max-h-[520px] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-100 z-50">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <PartyPopper className="w-4 h-4 text-[#84ab4a]" />
            <span className="text-sm font-bold text-gray-900">Referral Milestones</span>
          </div>
          {milestones.length === 0 ? (
            <div className="p-8 text-center">
              <PartyPopper className="w-8 h-8 mx-auto mb-2 text-gray-200" />
              <p className="text-sm text-gray-400">No new referral milestones yet.</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-gray-50">
              {milestones.map((m: any) => (
                <div key={m.id} className="p-4 bg-[#84ab4a]/4 flex items-start gap-2.5">
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-semibold">{m.clientName}</span> {MILESTONE_LABELS[m.milestone] ?? "reached a new milestone"} — your referral credit is confirmed.
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">{new Date(m.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                  </div>
                  <button
                    onClick={() => markSeen.mutate({ id: m.id, repCode })}
                    className="text-xs font-semibold text-gray-400 hover:text-gray-700 flex-shrink-0"
                    title="Dismiss"
                  >
                    Dismiss
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

//// ─── Confirmed Training Sessions Section ───────────────────────────────────────────────────────────────────────────────────
function ConfirmedSessionsSection({ repCode }: { repCode: string }) {
  const { data: bookings = [], isLoading } = trpc.trainingBookings.getUpcoming.useQuery(
    { repCode },
    { enabled: !!repCode }
  );

  const CALENDAR_NAMES: Record<string, string> = {
    "2ImDJVXE23GfW6WmVsMD": "BC360 General Training",
    "7LTivsApkZroz3LlZSiP": "Fundable360",
    "BNSDF3gHtSZuzgLMkijr": "Scale360",
    "FqI0SWCgqb5629AyScY6": "Share360",
    "MokOx22qfOnONZ4u9BKx": "Legacy360",
    "PCiM3Ok9GCLeycWuv8UK": "Media360",
  };

  const now = Date.now();
  const upcoming = (bookings as any[]).filter(b => b.startTime > now);
  const past = (bookings as any[]).filter(b => b.startTime <= now);

  if (isLoading) return null;
  if (bookings.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <CalendarCheck className="w-4 h-4 text-[#84ab4a]" />
        <span className="text-sm font-bold text-gray-800">Your Confirmed Training Sessions</span>
        {upcoming.length > 0 && (
          <span className="bg-[#84ab4a]/15 text-[#84ab4a] text-xs font-bold px-2 py-0.5 rounded-full">{upcoming.length} upcoming</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {upcoming.map((b: any) => (
          <div key={b.id} className="bg-white border border-[#84ab4a]/30 rounded-xl px-4 py-3 flex items-center gap-4 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-[#84ab4a]/10 flex flex-col items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-[#84ab4a] uppercase leading-none">
                {new Date(b.startTime).toLocaleDateString("en-US", { month: "short" })}
              </span>
              <span className="text-lg font-bold text-[#84ab4a] leading-none">
                {new Date(b.startTime).getDate()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{b.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {CALENDAR_NAMES[b.calendarId] ?? b.vertical} &bull;{" "}
                {new Date(b.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                {" – "}
                {new Date(b.endTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
            <span className="text-xs font-semibold text-[#84ab4a] bg-[#84ab4a]/10 px-2 py-0.5 rounded-full">Confirmed</span>
          </div>
        ))}
        {past.length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
              {past.length} past session{past.length !== 1 ? "s" : ""}
            </summary>
            <div className="flex flex-col gap-2 mt-2">
              {past.map((b: any) => (
                <div key={b.id} className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-4 opacity-60">
                  <CalendarX className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-600 truncate">{b.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {CALENDAR_NAMES[b.calendarId] ?? b.vertical} &bull;{" "}
                      {new Date(b.startTime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">Completed</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
// ─── Training Dashboard Section (2-column: appointments + announcements) ────────────────────────────────────────────────────────────────────────────────────
const TRAINING_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  confirmed:   { label: "Confirmed",   bg: "bg-green-50",  text: "text-green-700",  dot: "bg-green-500" },
  cancelled:   { label: "Cancelled",   bg: "bg-red-50",    text: "text-red-600",    dot: "bg-red-400" },
  showed:      { label: "Attended",    bg: "bg-blue-50",   text: "text-blue-700",   dot: "bg-blue-500" },
  noshow:      { label: "No Show",     bg: "bg-yellow-50", text: "text-yellow-700", dot: "bg-yellow-400" },
  rescheduled: { label: "Rescheduled", bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-400" },
  invalid:     { label: "Invalid",     bg: "bg-gray-50",   text: "text-gray-400",   dot: "bg-gray-300" },
};

const TRAINING_CAL_NAMES: Record<string, string> = {
  "2ImDJVXE23GfW6WmVsMD": "BC360 General",
  "7LTivsApkZroz3LlZSiP": "Fundable360",
  "BNSDF3gHtSZuzgLMkijr": "Scale360",
  "FqI0SWCgqb5629AyScY6": "Share360",
  "MokOx22qfOnONZ4u9BKx": "Legacy360",
  "PCiM3Ok9GCLeycWuv8UK": "Media360",
};

function TrainingDashboardSection({
  repCode,
}: {
  repCode: string;
  certifiedVerticals?: string[];
  allowedVerticals?: string[] | null;
  repFirstName?: string;
  repLastName?: string;
  repEmail?: string;
  repPhone?: string;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // ── Resizable calendar layout ──────────────────────────────────────────────
  const CAL_HEIGHT_KEY  = "bc360_cal_height";
  const CAL_ANN_W_KEY   = "bc360_ann_width";
  const MIN_CAL_H = 400;
  const MAX_CAL_H = 1400;
  // 200px let a saved/dragged width squeeze the announcement title down to
  // near-zero available space, wrapping it one word per line (client-reported
  // 2026-09-02). 280px keeps room for the icon, badge, and date alongside a
  // readable title.
  const MIN_ANN_W = 280;
  const MAX_ANN_W = 600;

  const [calHeight, setCalHeight] = useState<number>(() => {
    const saved = localStorage.getItem(CAL_HEIGHT_KEY);
    return saved ? Math.max(MIN_CAL_H, Math.min(MAX_CAL_H, parseInt(saved, 10))) : 740;
  });
  const [annWidth, setAnnWidth] = useState<number>(() => {
    const saved = localStorage.getItem(CAL_ANN_W_KEY);
    return saved ? Math.max(MIN_ANN_W, Math.min(MAX_ANN_W, parseInt(saved, 10))) : 320;
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Vertical drag handle (bottom of calendar — resizes height)
  const onHeightDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = calHeight;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(MIN_CAL_H, Math.min(MAX_CAL_H, startH + ev.clientY - startY));
      setCalHeight(next);
    };
    const onUp = (ev: MouseEvent) => {
      const final = Math.max(MIN_CAL_H, Math.min(MAX_CAL_H, startH + ev.clientY - startY));
      setCalHeight(final);
      localStorage.setItem(CAL_HEIGHT_KEY, String(final));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [calHeight]);

  // Horizontal drag handle (between calendar and announcements — resizes ann panel width)
  const onWidthDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = annWidth;
    const onMove = (ev: MouseEvent) => {
      // dragging left increases ann width, right decreases
      const next = Math.max(MIN_ANN_W, Math.min(MAX_ANN_W, startW - (ev.clientX - startX)));
      setAnnWidth(next);
    };
    const onUp = (ev: MouseEvent) => {
      const final = Math.max(MIN_ANN_W, Math.min(MAX_ANN_W, startW - (ev.clientX - startX)));
      setAnnWidth(final);
      localStorage.setItem(CAL_ANN_W_KEY, String(final));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [annWidth]);

  // Fetch shared company calendar events from bc360_events table (published only)
  const { data: events = [], isLoading, refetch } = trpc.bc360Calendar.list.useQuery(
    undefined,
    { refetchInterval: 120_000, staleTime: 60_000 }
  );

  const now = Date.now();
  const upcoming = (events as any[])
    .filter(e => e.endTimeUtc > now)
    .sort((a, b) => a.startTimeUtc - b.startTimeUtc);
  const past = (events as any[])
    .filter(e => e.endTimeUtc <= now)
    .sort((a, b) => b.startTimeUtc - a.startTimeUtc);

  const renderEventCard = (ev: any, isPast: boolean) => {
    const isExpanded = expandedId === ev.id;
    return (
      <div
        key={ev.id}
        className={`border rounded-xl overflow-hidden transition-all ${
          isPast ? "border-gray-100 bg-gray-50/60" : "border-[#84ab4a]/25 bg-white shadow-sm"
        }`}
      >
        <button
          className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50/80 transition-colors"
          onClick={() => setExpandedId(isExpanded ? null : ev.id)}
        >
          {/* Date badge */}
          <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
            isPast ? "bg-gray-100" : "bg-[#84ab4a]/10"
          }`}>
            <span className={`text-[9px] font-bold uppercase leading-none ${isPast ? "text-gray-400" : "text-[#84ab4a]"}`}>
              {new Date(ev.startTimeUtc).toLocaleDateString("en-US", { month: "short" })}
            </span>
            <span className={`text-base font-bold leading-none ${isPast ? "text-gray-400" : "text-[#84ab4a]"}`}>
              {new Date(ev.startTimeUtc).getDate()}
            </span>
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold truncate ${isPast ? "text-[#9C9285]" : "text-[#2C2723]"}`}>
              {ev.title}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              <span className="font-medium text-gray-500">{ev.eventType}</span>
              {" · "}
              {new Date(ev.startTimeUtc).toLocaleString("en-US", {
                weekday: "short", month: "short", day: "numeric",
                hour: "numeric", minute: "2-digit", timeZoneName: "short"
              })}
            </div>
          </div>
          {/* Join button for upcoming events with a link */}
          {!isPast && ev.joinLink && (
            <a
              href={ev.joinLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#84ab4a] text-white text-xs font-semibold hover:bg-[#6d9138] transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> Join
            </a>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-300 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </button>
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-100 text-xs space-y-1.5">
            <div>
              <span className="text-gray-400">Time: </span>
              <span className="font-medium text-gray-700">
                {new Date(ev.startTimeUtc).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                {" – "}
                {new Date(ev.endTimeUtc).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
            {ev.description && (
              <div>
                <span className="text-gray-400">Details: </span>
                <span className="text-gray-700 whitespace-pre-wrap">{ev.description}</span>
              </div>
            )}
            {ev.joinLink && (
              <div>
                <span className="text-gray-400">Join Link: </span>
                <a href={ev.joinLink} target="_blank" rel="noopener noreferrer" className="font-medium text-[#84ab4a] underline underline-offset-2 break-all">{ev.joinLink}</a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const RECURRENCE_LABELS: Record<string, string> = {
    daily: "Repeats daily",
    weekly: "Repeats weekly",
    monthly: "Repeats monthly",
  };

  return (
    <div>
      {/* Event detail dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={v => { if (!v) setSelectedEvent(null); }}>
        <DialogContent className="max-w-md">
          {selectedEvent && (() => {
            const ev = selectedEvent;
            const color =
              ev.eventType === "Training" ? "#84ab4a"
              : ev.eventType === "Webinar" ? "#1A6FD4"
              : ev.eventType === "Workshop" ? "#E8620A"
              : ev.eventType === "Meeting" ? "#C8920A"
              : "#7B4FD4";
            const isRecurring = ev.recurrenceRule && ev.recurrenceRule !== "none";
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5"
                      style={{ background: `${color}18`, border: `1.5px solid ${color}40` }}
                    >
                      <Calendar className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <DialogTitle className="text-base leading-snug">{ev.title}</DialogTitle>
                      {ev.eventType && (
                        <span
                          className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full mt-1"
                          style={{ background: `${color}15`, color }}
                        >
                          {ev.eventType}
                        </span>
                      )}
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-3 pt-1">
                  {/* Date & time */}
                  <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1">
                    <div className="text-xs text-gray-500 font-medium">
                      {new Date(ev.startTimeUtc).toLocaleDateString("en-US", {
                        weekday: "long", month: "long", day: "numeric", year: "numeric",
                      })}
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {new Date(ev.startTimeUtc).toLocaleTimeString("en-US", {
                        hour: "numeric", minute: "2-digit", timeZoneName: "short",
                      })}
                      {" – "}
                      {new Date(ev.endTimeUtc).toLocaleTimeString("en-US", {
                        hour: "numeric", minute: "2-digit",
                      })}
                    </div>
                    {isRecurring && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 pt-0.5">
                        <Repeat2 className="w-3 h-3 flex-shrink-0" />
                        {RECURRENCE_LABELS[ev.recurrenceRule!] ?? "Recurring event"}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {ev.description && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Details</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {ev.description}
                      </p>
                    </div>
                  )}

                  {/* Join link */}
                  {ev.joinLink && (
                    <a
                      href={ev.joinLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#84ab4a] hover:bg-[#6d9138] text-white text-sm font-semibold transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" /> Join Event
                    </a>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">BizConnect360 Calendar</h2>
          <p className="text-xs text-gray-900 mt-0.5">Click any event for details — times shown in your local timezone</p>
        </div>
      </div>

      {/* Mobile: stacked calendar + announcements */}
      <div className="lg:hidden flex flex-col gap-4">
        <div className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <WeeklyCalendarView
            events={(events as any[]) as CalendarEvent[]}
            isLoading={isLoading}
            onEventClick={setSelectedEvent}
          />
        </div>
        <div className="w-full bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
          <AnnouncementsSection />
        </div>
      </div>

      {/* Desktop: resizable side-by-side calendar + announcements */}
      <div ref={containerRef} className="hidden lg:flex items-start select-none" style={{ height: calHeight }}>

        {/* Calendar column */}
        <div className="flex-1 min-w-0 h-full relative">
          <WeeklyCalendarView
            events={(events as any[]) as CalendarEvent[]}
            isLoading={isLoading}
            onEventClick={setSelectedEvent}
          />

          {/* Bottom drag handle — resizes height */}
          <div
            onMouseDown={onHeightDragStart}
            className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize flex items-center justify-center group z-10"
            title="Drag to resize height"
          >
            <div className="w-12 h-1 rounded-full bg-gray-200 group-hover:bg-[#84ab4a]/60 transition-colors" />
          </div>
        </div>

        {/* Horizontal drag handle — resizes announcements width */}
        <div
          onMouseDown={onWidthDragStart}
          className="w-2 h-full flex-shrink-0 cursor-ew-resize flex items-center justify-center group"
          title="Drag to resize panel"
        >
          <div className="w-0.5 h-12 rounded-full bg-gray-200 group-hover:bg-[#84ab4a]/60 transition-colors" />
        </div>

        {/* Announcements column — follows calendar height, scrolls independently */}
        <div
          className="flex-shrink-0 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 overflow-y-auto h-full"
          style={{ width: annWidth }}
        >
          <AnnouncementsSection />
        </div>
      </div>
    </div>
  );
}

// ─── Agent Appointments Tab ────────────────────────────────────────────────────────────────────────────────────
const AGENT_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  confirmed:   { label: "Confirmed",   bg: "bg-green-50",  text: "text-green-700",  dot: "bg-green-500" },
  cancelled:   { label: "Cancelled",   bg: "bg-red-50",    text: "text-red-600",    dot: "bg-red-400" },
  showed:      { label: "Attended",    bg: "bg-blue-50",   text: "text-blue-700",   dot: "bg-blue-500" },
  noshow:      { label: "No Show",     bg: "bg-yellow-50", text: "text-yellow-700", dot: "bg-yellow-400" },
  rescheduled: { label: "Rescheduled", bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-400" },
  invalid:     { label: "Invalid",     bg: "bg-gray-50",   text: "text-gray-400",   dot: "bg-gray-300" },
};

const AGENT_CAL_NAMES: Record<string, string> = {
  "2ImDJVXE23GfW6WmVsMD": "BC360 General Training",
  "7LTivsApkZroz3LlZSiP": "Fundable360 Training",
  "BNSDF3gHtSZuzgLMkijr": "Scale360 Training",
  "FqI0SWCgqb5629AyScY6": "Share360 Training",
  "MokOx22qfOnONZ4u9BKx": "Legacy360 Training",
  "PCiM3Ok9GCLeycWuv8UK": "Media360 Training",
};

function AgentAppointmentsTab({ repCode, allowedVerticals = [] }: {
  repCode: string;
  allowedVerticals?: string[] | null;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fetch client appointments across all verticals for leads attributed to this agent
  const { data: bookings = [], isLoading, refetch } = trpc.ghlAppointments.getForAgentLeads.useQuery(
    { repCode },
    { enabled: !!repCode, refetchInterval: 120_000, staleTime: 60_000 }
  );

  const now = Date.now();
  const upcoming = (bookings as any[]).filter(b => new Date(b.startTime).getTime() > now && b.status !== "cancelled");
  const past     = (bookings as any[]).filter(b => new Date(b.startTime).getTime() <= now || b.status === "cancelled");

  const renderRow = (b: any, isPast: boolean) => {
    const sc = AGENT_STATUS_CONFIG[b.status] ?? AGENT_STATUS_CONFIG.confirmed;
    const isExpanded = expandedId === b.id;
    const startMs = new Date(b.startTime).getTime();
    const endMs   = new Date(b.endTime).getTime();
    const vertColor = VERTICAL_CONFIG[b.vertical]?.color ?? "#84ab4a";
    return (
      <div
        key={b.id}
        className={`border rounded-2xl overflow-hidden transition-all shadow-sm ${
          isPast ? "border-[#EBE2D4] bg-[#FBF7F0]/60" : "border-[#EBE2D4] bg-white"
        }`}
      >
        <button
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#FBF7F0] transition-colors"
          onClick={() => setExpandedId(isExpanded ? null : b.id)}
        >
          {/* Date badge */}
          <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
            isPast ? "bg-[#F4EEE3]" : "bg-[#84ab4a]/10"
          }`}>
            <span className={`text-[9px] font-bold uppercase leading-none ${isPast ? "text-[#9C9285]" : "text-[#84ab4a]"}`}>
              {new Date(startMs).toLocaleDateString("en-US", { month: "short" })}
            </span>
            <span className={`text-lg font-bold leading-none ${isPast ? "text-[#9C9285]" : "text-[#84ab4a]"}`}>
              {new Date(startMs).getDate()}
            </span>
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold truncate ${isPast ? "text-[#9C9285]" : "text-[#2C2723]"}`}>
              {b.clientName || b.contact?.name || b.title || "Client Appointment"}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${vertColor}18`, color: vertColor }}>
                {b.vertical}
              </span>
              <span className="text-xs text-gray-400">
                {new Date(startMs).toLocaleString("en-US", {
                  weekday: "short", month: "short", day: "numeric",
                  hour: "numeric", minute: "2-digit", timeZoneName: "short",
                })}
              </span>
            </div>
          </div>
          {/* Status */}
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${sc.bg} ${sc.text}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${sc.dot}`} />
            {sc.label}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-300 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </button>
        {isExpanded && (
          <div className="px-4 pb-4 pt-1 border-t border-[#F1EADD] grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs text-[#9C9285] block mb-0.5">Time</span>
              <span className="font-medium text-[#2C2723]">
                {new Date(startMs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                {" – "}
                {new Date(endMs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
            {b.contact?.email && (
              <div>
                <span className="text-xs text-[#9C9285] block mb-0.5">Client Email</span>
                <span className="font-medium text-[#2C2723]">{b.contact.email}</span>
              </div>
            )}
            {(b.address || b.location) && (() => {
              const loc = b.address || b.location;
              const isUrl = typeof loc === "string" && loc.startsWith("http");
              return (
                <div>
                  <span className="text-xs text-[#9C9285] block mb-0.5">Location / Join Link</span>
                  {isUrl ? (
                    <a href={loc} target="_blank" rel="noopener noreferrer" className="font-medium text-[#84ab4a] underline underline-offset-2 break-all">{loc}</a>
                  ) : (
                    <span className="font-medium text-[#2C2723]">{loc}</span>
                  )}
                </div>
              );
            })()}
            {(b.cancelledReason || b.notes) && (
              <div className="sm:col-span-2">
                <span className="text-xs text-[#9C9285] block mb-0.5">{b.cancelledReason ? "Cancellation Reason" : "Notes"}</span>
                <span className={`font-medium ${b.cancelledReason ? "text-red-500" : "text-gray-600"}`}>{b.cancelledReason || b.notes}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Group bookings by time proximity
  const _apptNow = Date.now();
  const _week1End = _apptNow + 7 * 24 * 60 * 60 * 1000;
  const _week2End = _apptNow + 14 * 24 * 60 * 60 * 1000;
  const _sortAsc = (a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  const _sortDesc = (a: any, b: any) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
  const _apptBookings = bookings as any[];
  const _thisWeek   = _apptBookings.filter(b => { const t = new Date(b.startTime).getTime(); return t >= _apptNow && t < _week1End && b.status !== "cancelled"; }).sort(_sortAsc);
  const _nextWeek   = _apptBookings.filter(b => { const t = new Date(b.startTime).getTime(); return t >= _week1End && t < _week2End && b.status !== "cancelled"; }).sort(_sortAsc);
  const _comingUp   = _apptBookings.filter(b => { const t = new Date(b.startTime).getTime(); return t >= _week2End && b.status !== "cancelled"; }).sort(_sortAsc);
  const _pastAll    = _apptBookings.filter(b => new Date(b.startTime).getTime() < _apptNow || b.status === "cancelled").sort(_sortDesc);
  const _totalBooked = _apptBookings.length;
  const _thisWeekCount = _thisWeek.length;
  const _nextAppt = upcoming[0];
  const _monthRange = (() => {
    const d = new Date(); const nx = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return `${d.toLocaleDateString("en-US", { month: "long" })} – ${nx.toLocaleDateString("en-US", { month: "long" })} ${d.getFullYear()}`;
  })();

  const renderApptRow = (b: any, isPast = false) => {
    const startMs = new Date(b.startTime).getTime();
    const endMs   = new Date(b.endTime).getTime();
    const timeStr = new Date(startMs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const dayDate = new Date(startMs).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const locRaw  = b.address || b.location || "";
    const channel = locRaw.toLowerCase().includes("zoom") ? "Zoom" : locRaw.toLowerCase().includes("phone") ? "Phone" : null;
    const clientName = b.clientName || b.contact?.name || "Client";
    const apptTitle  = b.title && b.title !== clientName ? b.title : (b.vertical ? `${b.vertical} Consultation` : "Consultation");
    return (
      <div key={b.id} className={`flex items-center gap-4 px-5 py-4 border-b border-[#F0E8DE] last:border-0 hover:bg-[#FBF7F0] transition-colors ${isPast ? "opacity-55" : ""}`}>
        <div className="w-24 flex-shrink-0">
          <div className="text-sm font-bold text-[#2C2723]">{timeStr}</div>
          <div className="text-xs text-[#9C9285] mt-0.5">{dayDate}</div>
        </div>
        <div className="w-px h-8 bg-[#EBE2D4] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#2C2723] text-sm truncate">{clientName}</div>
          <div className="text-xs text-[#9C9285] truncate">{apptTitle}</div>
        </div>
        {channel && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${channel === "Zoom" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-700"}`}>{channel}</span>
        )}
        {b.joinLink && !isPast && (
          <a href={b.joinLink} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-[#E2701F] hover:underline">
            <ExternalLink className="w-3 h-3" /> Join
          </a>
        )}
      </div>
    );
  };

  const SectionLabel = ({ label }: { label: string }) => (
    <div className="px-5 py-2 bg-[#FBF7F0] border-b border-[#F0E8DE]">
      <span className="text-[10px] font-bold text-[#9C9285] uppercase tracking-widest">{label}</span>
    </div>
  );

  return (
    <div className="flex gap-5 items-start">
      {/* Main calendar list */}
      <div className="flex-1 min-w-0 bg-white border border-[#EBE2D4] rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0E8DE]">
          <h2 className="text-lg font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>{_monthRange}</h2>
          <div className="flex items-center gap-1">
            <button className="w-8 h-8 rounded-lg border border-[#EBE2D4] flex items-center justify-center text-[#9C9285] hover:bg-[#F4EEE3] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button className="w-8 h-8 rounded-lg border border-[#EBE2D4] flex items-center justify-center text-[#9C9285] hover:bg-[#F4EEE3] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-[#9C9285]"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /><p className="text-sm">Loading your appointments…</p></div>
        ) : _apptBookings.length === 0 ? (
          <div className="py-16 text-center text-[#9C9285]">
            <Calendar className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-medium text-[#6E665A]">No appointments found</p>
            <p className="text-sm mt-1">Client appointments from your booking links will appear here.</p>
          </div>
        ) : (
          <>
            {_thisWeek.length > 0 && <><SectionLabel label="This Week" />{_thisWeek.map(b => renderApptRow(b))}</>}
            {_nextWeek.length > 0 && <><SectionLabel label="Next Week" />{_nextWeek.map(b => renderApptRow(b))}</>}
            {_comingUp.length > 0 && <><SectionLabel label="Coming Up" />{_comingUp.map(b => renderApptRow(b))}</>}
            {_thisWeek.length === 0 && _nextWeek.length === 0 && _comingUp.length === 0 && (
              <div className="py-10 text-center text-[#9C9285]"><Calendar className="w-9 h-9 mx-auto mb-2 opacity-20" /><p className="font-medium text-[#6E665A]">No upcoming appointments</p></div>
            )}
            {_pastAll.length > 0 && <><SectionLabel label={`Past & Cancelled (${_pastAll.length})`} />{_pastAll.slice(0, 6).map(b => renderApptRow(b, true))}</>}
          </>
        )}

        <div className="px-5 py-3 border-t border-[#F0E8DE] flex justify-end">
          <button onClick={() => refetch()} className="flex items-center gap-1.5 text-xs text-[#9C9285] hover:text-[#6E665A] transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Right sidebar */}
      <div className="flex-shrink-0 w-68 hidden lg:flex flex-col gap-4" style={{ width: "272px" }}>
        <div className="bg-white border border-[#EBE2D4] rounded-2xl shadow-sm p-5">
          <div className="text-base font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>Appointment Stats</div>
          <div className="space-y-3">
            {([
              { label: "This Week",    value: `${_thisWeekCount} meeting${_thisWeekCount !== 1 ? "s" : ""}` },
              { label: "Total Booked", value: `${_totalBooked} session${_totalBooked !== 1 ? "s" : ""}` },
              { label: "Upcoming",     value: String(upcoming.length) },
              ...(_nextAppt ? [{ label: "Next Available", value: new Date(_nextAppt.startTime).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) }] : []),
            ] as { label: string; value: string }[]).map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1 border-b border-[#F4EEE3] last:border-0">
                <span className="text-sm text-[#6E665A]">{label}</span>
                <span className="text-sm font-bold text-[#2C2723]">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vertical Referral Links Tab ────────────────────────────────────────────────────────────────────────────────────
function VerticalReferralLinksTab({ vertical, repCode, color }: { vertical: string; repCode: string; color: string }) {
  const cfg = VERTICAL_CONFIG[vertical];
  const { data: mainBaseUrl } = trpc.verticalLinks.getMainUrl.useQuery({ vertical });
  // Use admin-configured URL if set; fall back to VERTICAL_CONFIG domain
  const mainBase = mainBaseUrl ?? (cfg ? `https://${cfg.domain}/` : null);
  const mainLink = mainBase ? withRefCode(mainBase, repCode) : null;
  const { data: productLinks = [], isLoading } = trpc.verticalLinks.listByVertical.useQuery({ vertical, repCode });
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyLink = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("Referral link copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-gray-900 mb-1">{vertical} — Referral Links</h2>
        <p className="text-sm text-gray-900">Share these links with prospects. Your rep code is automatically embedded so you get credit for every referral.</p>
      </div>

      {/* Main vertical referral link */}
      {mainLink && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-4">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
            <span className="text-sm font-semibold text-gray-700">{vertical} — Main Site</span>
            <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Auto-generated</span>
          </div>
          <div className="px-5 py-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-400 mb-1">Your personal referral link</div>
              <div className="font-mono text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 truncate border border-gray-100">{mainLink}</div>
            </div>
            <button
              onClick={() => copyLink(mainLink, "main")}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border"
              style={copiedId === "main" ? { background: `${color}15`, color, borderColor: `${color}40` } : { background: "white", color: "#6B7280", borderColor: "#E5E7EB" }}
            >
              {copiedId === "main" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedId === "main" ? "Copied!" : "Copy"}
            </button>
            <a href={mainLink} target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 transition-all">
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
          </div>
        </div>
      )}

      {/* Product-specific links */}
      {isLoading ? (
        <div className="py-8 text-center text-gray-400">
          <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin opacity-40" />
          <p className="text-sm">Loading product links...</p>
        </div>
      ) : productLinks.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-8 text-center">
          <Link className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-400 font-medium">No product-specific links yet</p>
          <p className="text-xs text-gray-400 mt-1">Your admin will add product-specific referral links here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Product Links</div>
          {productLinks.map((pl: any) => {
            const linkId = `pl-${pl.id}`;
            if (pl.locked) {
              return (
                <div key={pl.id} className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-500">{pl.productName}</span>
                  </div>
                  <div className="px-5 py-4 text-xs text-gray-400">{pl.lockedReason ?? "Complete the required certification to unlock this link."}</div>
                </div>
              );
            }
            const fullUrl = withRefCode(pl.baseUrl, repCode);
            return (
              <div key={pl.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="text-sm font-semibold text-gray-700">{pl.productName}</span>
                </div>
                {pl.description && (
                  <div className="px-5 pt-3 text-xs text-gray-400">{pl.description}</div>
                )}
                <div className="px-5 py-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 truncate border border-gray-100">{fullUrl}</div>
                  </div>
                  <button
                    onClick={() => copyLink(fullUrl, linkId)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border"
                    style={copiedId === linkId ? { background: `${color}15`, color, borderColor: `${color}40` } : { background: "white", color: "#6B7280", borderColor: "#E5E7EB" }}
                  >
                    {copiedId === linkId ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedId === linkId ? "Copied!" : "Copy"}
                  </button>
                  <a href={fullUrl} target="_blank" rel="noopener noreferrer"
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 transition-all">
                    <ExternalLink className="w-3.5 h-3.5" /> Open
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Trackable Shareable Links (Associate Tools panel) ─────────────────────────────────────────────────────────
// A compact view of the SAME referral links the per-vertical "Referral Links"
// tab shows (VerticalReferralLinksTab above), surfaced on the overview so an
// associate can grab a link without navigating into a vertical first.
//
// Every URL is built from the LOGGED-IN associate's own repCode via
// withRefCode(), from the same two admin-managed sources the full tab reads:
//   - verticalLinks.getAllMainUrls  → the main landing page per vertical
//     (falling back to VERTICAL_CONFIG's domain when an admin has not set one)
//   - verticalLinks.listByVertical  → admin-configured product links, which the
//     server already redacts (baseUrl: "", locked: true) for any link whose
//     certification the associate has not passed. Locked links are skipped
//     here; the full Referral Links tab is where the "why" is explained.
// No URL is hardcoded per associate and no other associate's links can reach
// this panel: repCode is the authenticated rep record this page renders with,
// and every row is derived from it. The dedupe/labelling rules live in
// @/lib/shareableLinks so they can be unit-tested.

function ShareableLinkRow({ label, url, color, isCopied, onCopy }: {
  label: string; url: string; color: string; isCopied: boolean; onCopy: () => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-start gap-2.5 min-w-0 flex-1">
        <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: color }} />
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-[#2C2723] leading-tight">{label}</div>
          <div className="text-[11px] font-mono text-[#9C9285] truncate" title={url}>{url}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-shrink-0">
        <button
          onClick={onCopy}
          className="flex items-center justify-center gap-1.5 text-[12px] font-semibold py-1.5 px-3 rounded-xl bg-[#E2701F] text-white hover:bg-[#C75E12] transition-colors"
        >
          {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {isCopied ? "Copied!" : "Copy Link"}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 text-[12px] font-semibold py-1.5 px-3 rounded-xl border border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3] transition-colors"
        >
          Open Link <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

// One vertical's rows: its main landing page (when resolveMainShareableLinks
// gave this vertical the row, rather than merging it into an earlier one)
// followed by its unlocked product links. Kept as its own component so each
// vertical owns its own listByVertical query - the parent cannot call hooks in
// a loop over a list whose length changes between renders.
function VerticalShareableLinkRows({ vertical, repCode, color, mainUrl, mainLabel, copiedKey, onCopy }: {
  vertical: string; repCode: string; color: string;
  mainUrl: string | null; mainLabel: string;
  copiedKey: string | null; onCopy: (url: string, key: string, label: string) => void;
}) {
  const { data: productLinks = [] } = trpc.verticalLinks.listByVertical.useQuery({ vertical, repCode }, { enabled: !!repCode });
  const unlocked = (productLinks as any[]).filter(pl => !pl.locked && pl.baseUrl);
  return (
    <>
      {mainUrl && (
        <ShareableLinkRow
          label={mainLabel}
          url={mainUrl}
          color={color}
          isCopied={copiedKey === `main-${vertical}`}
          onCopy={() => onCopy(mainUrl, `main-${vertical}`, mainLabel)}
        />
      )}
      {unlocked.map((pl: any) => {
        const url = withRefCode(pl.baseUrl, repCode);
        const key = `product-${pl.id}`;
        const label = `${vertical} (${pl.productName})`;
        return (
          <ShareableLinkRow
            key={key}
            label={label}
            url={url}
            color={color}
            isCopied={copiedKey === key}
            onCopy={() => onCopy(url, key, label)}
          />
        );
      })}
    </>
  );
}

function TrackableShareableLinks({ repCode, verticals, onViewAll }: {
  repCode: string | null | undefined; verticals: string[]; onViewAll: (() => void) | null;
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: mainUrls, isLoading } = trpc.verticalLinks.getAllMainUrls.useQuery(undefined, { enabled: !!repCode });

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  const handleCopy = (url: string, key: string, label: string) => {
    navigator.clipboard.writeText(url);
    setCopiedKey(key);
    toast.success(`${label} link copied!`);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopiedKey(null), 2000);
  };

  // BizConnect360 has no landing page of its own, so it never yields a row -
  // resolveMainShareableLinks drops it on the empty domain. It stays in the
  // list passed down so product links configured under it would still show.
  const shareableVerticals = verticals.filter(v => v !== "BizConnect360");
  const mainLinks = resolveMainShareableLinks(
    shareableVerticals,
    (mainUrls ?? {}) as Record<string, string | undefined>,
    Object.fromEntries(Object.entries(VERTICAL_CONFIG).map(([v, cfg]) => [v, cfg.domain])),
    repCode,
  );
  const mainByVertical = new Map(mainLinks.map(l => [l.vertical, l]));

  const header = (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[14px] font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>Trackable Shareable Links</h2>
      {onViewAll && (
        <button onClick={onViewAll} className="text-[12px] font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
          View all <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  const emptyState = (title: string, detail: string) => (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-6 bg-white border border-dashed border-[#EBE2D4] rounded-2xl">
      <Link className="w-9 h-9 mx-auto mb-2 text-[#BCB2A4]" />
      <p className="text-sm font-medium text-[#6E665A]">{title}</p>
      <p className="text-xs text-[#9C9285] mt-0.5">{detail}</p>
    </div>
  );

  // No rep code means there is nothing trackable to build. Never fall back to a
  // plain landing-page URL here - an untracked link looks like it works while
  // silently losing the associate their referral credit.
  if (!repCode) {
    return (
      <div className="mt-5 pt-5 border-t border-[#EBE2D4] flex-1 flex flex-col">
        {header}
        {emptyState("Links unavailable", "Your associate code has not been assigned yet. Contact support and your links will appear here.")}
      </div>
    );
  }

  return (
    <div className="mt-5 pt-5 border-t border-[#EBE2D4] flex-1 flex flex-col">
      {header}
      {isLoading ? (
        <div className="py-6 text-center text-[#9C9285]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          <p className="text-sm">Loading your links…</p>
        </div>
      ) : shareableVerticals.length === 0 ? (
        emptyState("No shareable links yet", "Links appear here once you have access to a vertical.")
      ) : (
        <div className="flex-1 bg-white border border-[#EBE2D4] rounded-2xl px-4 py-3 divide-y divide-[#EBE2D4] overflow-y-auto max-h-[320px]">
          {shareableVerticals.map(v => (
            <VerticalShareableLinkRows
              key={v}
              vertical={v}
              repCode={repCode}
              color={VERTICAL_CONFIG[v]?.color ?? "#E2701F"}
              mainUrl={mainByVertical.get(v)?.url ?? null}
              mainLabel={mainByVertical.get(v)?.label ?? v}
              copiedKey={copiedKey}
              onCopy={handleCopy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Account Settings Tab ──────────────────────────────────────────────────────────────────────────────────────
function AccountSettingsTab({ repCode, username, repId, profilePictureUrl, repFirstName, repLastName, repEmail, agentLevel, ghlContactId }: { repCode: string; username: string; repId?: number; profilePictureUrl?: string | null; repFirstName?: string | null; repLastName?: string | null; repEmail?: string | null; agentLevel?: string | null; ghlContactId?: string | null }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profilePictureUrl ?? null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const changePasswordMutation = trpc.rep.changePassword.useMutation({
    onSuccess: () => {
      toast.success("Password changed successfully!");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    },
    onError: (e) => toast.error(e.message),
  });

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !repId) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/upload/profile-picture?repId=${repId}`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Upload failed");
      setAvatarUrl(data.url);
      toast.success("Profile picture updated!");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    }
    setAvatarUploading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { toast.error("All fields are required"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    changePasswordMutation.mutate({ repCode, currentPassword: currentPw, newPassword: newPw });
  };

  return (
    <div className="max-w-4xl">
      {/* Top row: Profile form + Notifications/Integrations */}
      <div className="flex flex-col lg:flex-row gap-5 items-start mb-6">
        {/* Profile card */}
        <div className="flex-1 min-w-0 bg-white border border-[#EBE2D4] rounded-2xl shadow-sm p-6">
          <h3 className="text-base font-bold text-[#2C2723] mb-5" style={{ fontFamily: "'Spectral', serif" }}>Profile</h3>
          {/* Avatar upload */}
          <div className="flex items-center gap-4 mb-5">
            <div className="relative group flex-shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="w-16 h-16 rounded-2xl object-cover border border-[#EBE2D4]" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-[#FBEEE1] border border-[#E2701F]/25 flex items-center justify-center text-lg font-bold text-[#E2701F]">
                  {(repFirstName ?? username)[0]}{repLastName?.[0] ?? ""}
                </div>
              )}
              {repId && (
                <label className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  {avatarUploading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Pencil className="w-4 h-4 text-white" />}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarChange} disabled={avatarUploading} />
                </label>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2C2723]">{repFirstName ? `${repFirstName} ${repLastName ?? ""}`.trim() : username}</p>
              <p className="text-xs text-[#9C9285]">{repCode}</p>
              {repId && <p className="text-[10px] text-[#BCB2A4] mt-0.5">Click photo to update</p>}
            </div>
          </div>
          {/* Profile fields (display) */}
          <div className="space-y-3 mb-5">
            {[
              { label: "Full Name", value: repFirstName ? `${repFirstName} ${repLastName ?? ""}`.trim() : username },
              { label: "Rep Code",  value: repCode },
              { label: "Email",     value: repEmail ?? "—" },
              { label: "Level",     value: agentLevel ?? "Associate" },
            ].map(({ label, value }) => (
              <div key={label}>
                <label className="block text-xs font-semibold text-[#9C9285] mb-1">{label}</label>
                <div className="w-full bg-[#FBF7F0] border border-[#EBE2D4] rounded-xl px-3 py-2.5 text-sm text-[#2C2723]">{value}</div>
              </div>
            ))}
          </div>
          {/* Password change */}
          <div className="border-t border-[#F0E8DE] pt-5">
            <p className="text-xs font-bold text-[#9C9285] uppercase tracking-widest mb-4">Change Password</p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <input type={showCurrent ? "text" : "password"} className="w-full bg-[#FBF7F0] border border-[#EBE2D4] rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E2701F]/30 focus:border-[#E2701F]" placeholder="Current password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
                <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#BCB2A4] hover:text-[#6E665A]">{showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <div className="relative">
                <input type={showNew ? "text" : "password"} className="w-full bg-[#FBF7F0] border border-[#EBE2D4] rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E2701F]/30 focus:border-[#E2701F]" placeholder="New password (min 6 chars)" value={newPw} onChange={e => setNewPw(e.target.value)} />
                <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#BCB2A4] hover:text-[#6E665A]">{showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <input type="password" className="w-full bg-[#FBF7F0] border border-[#EBE2D4] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E2701F]/30 focus:border-[#E2701F]" placeholder="Confirm new password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
              <button type="submit" disabled={changePasswordMutation.isPending} className="w-full py-2.5 bg-[#E2701F] hover:bg-[#C75E12] disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors">
                {changePasswordMutation.isPending ? "Updating…" : "Update Password"}
              </button>
            </form>
          </div>
        </div>

        {/* Right: Notifications + Integrations */}
        <div className="w-full lg:w-[320px] lg:flex-shrink-0 flex flex-col gap-4">
          <div className="bg-white border border-[#EBE2D4] rounded-2xl shadow-sm p-6">
            <h3 className="text-base font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>Notifications</h3>
            <div className="space-y-4">
              {[
                { label: "New lead alerts",       desc: "Email me when a referral link converts",   on: true  },
                { label: "Appointment reminders", desc: "1 hour before each booking",               on: true  },
                { label: "Commission payouts",    desc: "When earnings are processed",              on: true  },
                { label: "Weekly digest",         desc: "Monday morning pipeline summary",          on: false },
              ].map(({ label, desc, on }) => (
                <div key={label} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#2C2723]">{label}</div>
                    <div className="text-xs text-[#9C9285] mt-0.5">{desc}</div>
                  </div>
                  <div className={`flex-shrink-0 w-10 h-6 rounded-full transition-colors relative ${on ? "bg-[#E2701F]" : "bg-[#EBE2D4]"}`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${on ? "left-5" : "left-1"}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#EBE2D4] rounded-2xl shadow-sm p-6">
            <h3 className="text-base font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>Integrations</h3>
            <div className={`rounded-xl px-4 py-3 ${ghlContactId ? "bg-[#EFF4E2] border border-[#7FA63E]/30" : "bg-[#FBF7F0] border border-[#EBE2D4]"}`}>
              <div className="flex items-center gap-2">
                <CheckCircle className={`w-4 h-4 flex-shrink-0 ${ghlContactId ? "text-[#5F8A2E]" : "text-[#BCB2A4]"}`} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${ghlContactId ? "text-[#5F8A2E]" : "text-[#6E665A]"}`}>Wibiz {ghlContactId ? "Calendar connected" : "not linked"}</div>
                  <div className="text-xs text-[#9C9285] truncate" title={ghlContactId ? repEmail ?? repCode ?? undefined : undefined}>{ghlContactId ? repEmail ?? repCode : "Contact admin to link your Wibiz account"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Team Member Row (shared by hierarchy views) ─────────────────────────────
// ─── Generic Team Tree Builder ────────────────────────────────────────────────
// Builds an arbitrary-depth forest from a flat list of reps using uplineRepCode
// pointers, so any rep (Agency, Senior Associate, or Associate) that recruited
// someone else in the same result set renders as an expandable parent — not
// just SA rows. The flat list itself is already scoped server-side to the
// caller's own authorized hierarchy (getTeamHierarchy/getAgencyTeam), so
// building the tree client-side from it introduces no new data exposure.
export type TeamTreeNode = { rep: any; children: TeamTreeNode[] };

// My Team search - matches a team member by first+last name or email,
// case-insensitive and trimmed on both sides (a query of "  bob " must match
// "Bob"). Exported/pure so it can be unit tested the same way buildTeamForest
// is, without rendering RepDashboard.
export function filterTeamMembersBySearch(members: any[], query: string): any[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return [];
  return members.filter((r: any) => {
    const name = `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim().toLowerCase();
    const email = (r.email ?? "").trim().toLowerCase();
    return name.includes(normalized) || email.includes(normalized);
  });
}

export function buildTeamForest(rows: any[]): TeamTreeNode[] {
  const byCode = new Map<string, TeamTreeNode>();
  for (const r of rows) byCode.set(r.repCode, { rep: r, children: [] });
  const roots: TeamTreeNode[] = [];
  for (const r of rows) {
    const node = byCode.get(r.repCode)!;
    const parent = r.uplineRepCode ? byCode.get(r.uplineRepCode) : undefined;
    // Defensive cycle guard: uplineRepCode should never point back to a
    // descendant, but if data is ever corrupted, treat the node as a root
    // rather than recursing into an infinite loop.
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

function TeamMemberRow({
  rep, leadCount, depth = 0, hasChildren = false, isExpanded = false, onToggleExpand,
  upgradeStatus, onNominate, onViewLeads, isSelected, showLeads = true, showJoined = true,
}: {
  rep: any;
  leadCount: number;
  depth?: number;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  upgradeStatus?: any;
  onNominate?: (rep: any) => void;
  onViewLeads?: (rep: any) => void;
  isSelected?: boolean;
  showLeads?: boolean;
  showJoined?: boolean;
}) {
  const hasPending  = upgradeStatus?.status === "pending";
  const wasApproved = upgradeStatus?.status === "approved";
  const isSA = rep.agentLevel === "Senior Associate";

  const nameCell = (
    <div className="inline-block">
      <div className="font-semibold text-gray-900 text-sm">{rep.firstName} {rep.lastName}</div>
      <div className="text-xs text-gray-400">{rep.email}</div>
    </div>
  );

  return (
    <tr className={`border-t border-gray-50 ${isSelected ? "bg-[#1A6FD4]/5" : isSA ? "bg-blue-50/30 hover:bg-blue-50/50" : "hover:bg-gray-50/50"}`}>
      <td className="px-5 py-3" style={depth > 0 ? { paddingLeft: `${1.25 + depth * 1.25}rem` } : undefined}>
        {onToggleExpand ? (
          <button onClick={onToggleExpand} className="flex items-center gap-2 text-left w-full">
            {depth > 0 && <span className="text-gray-300 text-xs">└</span>}
            {hasChildren
              ? isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              : <span className="w-3.5 h-3.5 flex-shrink-0" />}
            {nameCell}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {depth > 0 && <span className="text-gray-300 text-xs">└</span>}
            {nameCell}
          </div>
        )}
      </td>
      <td className="px-5 py-3 font-mono text-xs text-gray-600">{rep.repCode}</td>
      <td className="px-5 py-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          isSA ? "bg-blue-100 text-blue-700"
          : "bg-[#84ab4a]/10 text-[#84ab4a]"
        }`}>{rep.agentLevel}</span>
      </td>
      <td className="px-5 py-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          rep.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
        }`}>{rep.status}</span>
      </td>
      {showLeads && (
        <td className="px-5 py-3 text-xs font-mono">
          {onViewLeads ? (
            <button
              onClick={() => onViewLeads(rep)}
              className={`px-2 py-0.5 rounded-lg transition-colors font-mono text-xs ${isSelected ? "bg-[#1A6FD4] text-white" : "text-gray-500 hover:bg-gray-100"}`}
            >
              {leadCount}
            </button>
          ) : (
            <span className="text-gray-500">{leadCount}</span>
          )}
        </td>
      )}
      {showJoined && (
        <td className="px-5 py-3 text-xs text-gray-400">
          {rep.createdAt ? new Date(rep.createdAt).toLocaleDateString() : "—"}
        </td>
      )}
      {onNominate !== undefined && (
        <td className="px-4 py-3 text-right">
          {hasPending ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {isSA ? "Pending Agency" : "Pending SA"}
            </span>
          ) : wasApproved ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              {isSA ? "Agency Approved" : "SA Approved"}
            </span>
          ) : (rep.agentLevel === "Associate" || isSA) ? (
            <button
              onClick={() => onNominate(rep)}
              className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-[#1A6FD4]/10 text-[#1A6FD4] hover:bg-[#1A6FD4]/20 transition-colors border border-[#1A6FD4]/20"
            >
              {isSA ? "Promote to Agency" : "Nominate SA"}
            </button>
          ) : null}
        </td>
      )}
    </tr>
  );
}

// ─── Recursive Team Tree Rows ─────────────────────────────────────────────────
// Renders a TeamTreeNode and (if expanded) its children at arbitrary depth.
// Must return a flat list of <tr> — React.Fragment, not nested <tr>s — since
// this lives directly under a <tbody>.
function TeamTreeRows({
  node, depth, expandedRepCodes, onToggleExpand, leadCountMap, upgradeStatusMap,
  onNominate, onViewLeads, selectedRepCode, showLeads = true, showJoined = true, columnCount = 7,
}: {
  node: TeamTreeNode;
  depth: number;
  expandedRepCodes: Set<string>;
  onToggleExpand: (repCode: string) => void;
  leadCountMap: Record<string, number>;
  upgradeStatusMap: Record<string, any>;
  onNominate?: (rep: any) => void;
  onViewLeads?: (rep: any) => void;
  selectedRepCode?: string | null;
  showLeads?: boolean;
  showJoined?: boolean;
  columnCount?: number;
}) {
  const { rep, children } = node;
  const isExpanded = expandedRepCodes.has(rep.repCode);
  return (
    <React.Fragment>
      <TeamMemberRow
        rep={rep}
        depth={depth}
        hasChildren={children.length > 0}
        isExpanded={isExpanded}
        onToggleExpand={() => onToggleExpand(rep.repCode)}
        leadCount={leadCountMap[rep.repCode] ?? 0}
        upgradeStatus={upgradeStatusMap[rep.repCode]}
        onNominate={onNominate}
        onViewLeads={onViewLeads}
        isSelected={selectedRepCode === rep.repCode}
        showLeads={showLeads}
        showJoined={showJoined}
      />
      {isExpanded && children.length === 0 && (
        <tr>
          <td colSpan={columnCount} className="px-5 py-2 text-xs text-gray-400 italic" style={{ paddingLeft: `${2.5 + depth * 1.25}rem` }}>
            No team members under this rep yet.
          </td>
        </tr>
      )}
      {isExpanded && children.map(child => (
        <TeamTreeRows
          key={child.rep.id}
          node={child}
          depth={depth + 1}
          expandedRepCodes={expandedRepCodes}
          onToggleExpand={onToggleExpand}
          leadCountMap={leadCountMap}
          upgradeStatusMap={upgradeStatusMap}
          onNominate={onNominate}
          onViewLeads={onViewLeads}
          selectedRepCode={selectedRepCode}
          showLeads={showLeads}
          showJoined={showJoined}
          columnCount={columnCount}
        />
      ))}
    </React.Fragment>
  );
}

// ─── Agency Drill-Down Row (for Super Agency / Super Team / Agency views) ────
// Recursive: a sub-agency can itself have its own sub-agency at unbounded
// depth (Agency reps can recruit further Agency-level reps), so each row
// fetches its own children via getAgencyTeam and renders any returned
// sub-agencies as further AgencyDrillDownRows, not just SAs/Associates.
function AgencyDrillDownRow({ agency, depth = 0 }: { agency: any; depth?: number }) {
  const [open, setOpen] = useState(false);
  const [expandedRepCodes, setExpandedRepCodes] = useState<Set<string>>(new Set());
  const { data, isLoading } = trpc.agentSelfReg.getAgencyTeam.useQuery(
    { agencyRepCode: agency.repCode },
    { enabled: open }
  );
  const subAgencies = data?.agencies ?? [];
  const seniorAssociates = data?.seniorAssociates ?? [];
  const associates = data?.associates ?? [];
  // Arbitrary-depth forest (SA-under-SA or Associate-under-Associate all nest
  // correctly) built from the same flat, server-scoped result set.
  const forest = useMemo(() => buildTeamForest([...seniorAssociates, ...associates]), [seniorAssociates, associates]);

  const toggleExpand = (repCode: string) => {
    setExpandedRepCodes(prev => {
      const next = new Set(prev);
      next.has(repCode) ? next.delete(repCode) : next.add(repCode);
      return next;
    });
  };

  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50/50">
        <td className="px-5 py-3" style={depth > 0 ? { paddingLeft: `${1.25 + depth * 1.25}rem` } : undefined}>
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-2 text-left w-full"
          >
            {depth > 0 && <span className="text-gray-300 text-xs">└</span>}
            {open
              ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
            <div>
              <div className="font-semibold text-gray-900 text-sm">{agency.firstName} {agency.lastName}</div>
              <div className="text-xs text-gray-400">{agency.email}</div>
            </div>
          </button>
        </td>
        <td className="px-5 py-3 font-mono text-xs text-gray-600">{agency.repCode}</td>
        <td className="px-5 py-3">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{agency.agentLevel}</span>
        </td>
        <td className="px-5 py-3">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${agency.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{agency.status}</span>
        </td>
      </tr>
      {open && (
        isLoading ? (
          <tr className="border-t border-gray-50">
            <td colSpan={4} className="px-5 py-3 text-xs text-gray-400 pl-14">Loading team...</td>
          </tr>
        ) : (subAgencies.length === 0 && forest.length === 0) ? (
          <tr className="border-t border-gray-50">
            <td colSpan={4} className="px-5 py-2 text-xs text-gray-400 italic pl-14">No team members yet.</td>
          </tr>
        ) : (
          <>
            {subAgencies.map((a: any) => (
              <AgencyDrillDownRow key={a.id} agency={a} depth={depth + 1} />
            ))}
            {forest.map(node => (
              <TeamTreeRows
                key={node.rep.id}
                node={node}
                depth={depth + 1}
                expandedRepCodes={expandedRepCodes}
                onToggleExpand={toggleExpand}
                leadCountMap={{}}
                upgradeStatusMap={{}}
                showLeads={false}
                showJoined={false}
                columnCount={4}
              />
            ))}
          </>
        )
      )}
    </>
  );
}

// ─── My Team Tab ─────────────────────────────────────────────────────────────
function MyTeamTab({
  repCode, agentLevel, uplineRepCode, inviteLink, maxLevel, downline, pendingRequests, upgradeRequests,
  teamVisibility, isLoading, onUpgradeRequestSent,
}: {
  repCode: string;
  agentLevel: string;
  uplineRepCode: string | null;
  inviteLink: string;
  maxLevel: string;
  downline: any[];
  pendingRequests: any[];
  upgradeRequests: any[];
  teamVisibility: { agentLevel: string; agencies: any[]; seniorAssociates: any[]; associates: any[] } | null;
  isLoading: boolean;
  onUpgradeRequestSent: () => void;
}) {
  // Derive team data from props first (needed by hooks below)
  const seniorAssociates = teamVisibility?.seniorAssociates ?? [];
  const allAssociates    = teamVisibility?.associates ?? [];
  const seniorAssociateDirectTeam = [...seniorAssociates, ...allAssociates];

  const [copied, setCopied] = useState(false);
  const [upgradeNotes, setUpgradeNotes] = useState("");
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [expandedRepCodes, setExpandedRepCodes] = useState<Set<string>>(new Set());
  const [teamSearch, setTeamSearch] = useState("");

  // Agency: nominate associate for SA upgrade
  const [nominateTarget, setNominateTarget] = useState<any | null>(null);
  const [nominateNotes, setNominateNotes] = useState("");

  // Agency: limited lead view for a selected team member
  const [selectedMember, setSelectedMember] = useState<{ repCode: string; name: string } | null>(null);
  const teamMemberLeadsQuery = trpc.agentSelfReg.getTeamMemberLimitedLeads.useQuery(
    { repCode, targetRepCode: selectedMember?.repCode ?? "" },
    { enabled: agentLevel === "Agency" && !!selectedMember },
  );

  const submitUpgrade = trpc.repUpgrade.request.useMutation({
    onSuccess: () => {
      toast.success("Upgrade request submitted! Admin will review it shortly.");
      setShowUpgradeForm(false);
      setUpgradeNotes("");
      onUpgradeRequestSent();
    },
    onError: (e: any) => toast.error(e.message || "Failed to submit upgrade request."),
  });

  // Agency: nominate an associate for Senior Associate
  const associateRepCodes = allAssociates.map((a: any) => a.repCode);
  const upgradeStatusQuery = trpc.repUpgrade.getStatusForRepCodes.useQuery(
    { repCodes: associateRepCodes, requesterRepCode: repCode },
    { enabled: agentLevel === "Agency" && associateRepCodes.length > 0 },
  );
  const upgradeStatusMap: Record<string, any> = upgradeStatusQuery.data ?? {};
  const nominateMutation = trpc.repUpgrade.nominateAssociate.useMutation({
    onSuccess: (_, vars) => {
      toast.success(`Nomination submitted for ${nominateTarget?.firstName ?? vars.associateRepCode}!`);
      setNominateTarget(null);
      setNominateNotes("");
      upgradeStatusQuery.refetch();
    },
    onError: (e: any) => toast.error(e.message || "Failed to submit nomination."),
  });
  // Agency: nominate a Senior Associate for promotion to Agency (separate
  // mutation, same request/approve pipeline — see repUpgrade.approve).
  const nominateSAMutation = trpc.repUpgrade.nominateSeniorAssociate.useMutation({
    onSuccess: (_, vars) => {
      toast.success(`Promotion request submitted for ${nominateTarget?.firstName ?? vars.seniorAssociateRepCode}!`);
      setNominateTarget(null);
      setNominateNotes("");
      upgradeStatusQuery.refetch();
    },
    onError: (e: any) => toast.error(e.message || "Failed to submit promotion request."),
  });

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const agencies = teamVisibility?.agencies ?? [];

  // Compute all visible rep codes for the leads query
  const teamRepCodes: string[] = teamVisibility
    ? [
        ...agencies.map((r: any) => r.repCode),
        ...teamVisibility.seniorAssociates.map((r: any) => r.repCode),
        ...teamVisibility.associates.map((r: any) => r.repCode),
      ]
    : [];

  const teamLeadsQuery = trpc.agentSelfReg.getTeamLeads.useQuery(
    { repCodes: teamRepCodes, requesterRepCode: repCode },
    { enabled: teamRepCodes.length > 0 },
  );

  // Build a lead count map: repCode → count
  const leadCountMap = (teamLeadsQuery.data ?? []).reduce((acc: Record<string, number>, lead: any) => {
    if (lead.repCode) acc[lead.repCode] = (acc[lead.repCode] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Exclude pending requests where an active rep with the same email/repId already exists in downline
  const activeDownlineEmailSet = new Set(downline.filter((r: any) => r.status === "active").map((r: any) => (r.email ?? "").toLowerCase()));
  const activeDownlineRepIdSet = new Set(downline.filter((r: any) => r.status === "active").map((r: any) => r.id));
  const trulyPendingRequests = pendingRequests.filter((r: any) =>
    r.status === "pending"
    && !activeDownlineEmailSet.has((r.email ?? "").toLowerCase())
    && !activeDownlineRepIdSet.has(r.repId)
  );
  const pendingCount = trulyPendingRequests.length;
  const approvedCount = pendingRequests.filter((r: any) => r.status === "approved").length;
  const totalTeamLeads = Object.values(leadCountMap).reduce((a, b) => a + b, 0);

  const statsItems: { label: string; value: number; color: string; icon: any }[] =
    agentLevel === "Super Agency" || agentLevel === "Super Team"
      ? [
          { label: agentLevel === "Super Agency" ? "Super Teams" : "Agencies", value: agencies.length, color: "#7B4FD4", icon: Building },
          { label: "Team Leads", value: totalTeamLeads, color: "#C8920A", icon: Activity },
          { label: "Pending Applications", value: pendingCount, color: "#1A6FD4", icon: Clock },
        ]
      : agentLevel === "Agency"
      ? [
          { label: "Senior Associates", value: seniorAssociates.length, color: "#1A6FD4", icon: BadgeCheck },
          { label: "Total Associates", value: allAssociates.length, color: "#84ab4a", icon: Users2 },
          { label: "Team Leads", value: totalTeamLeads, color: "#C8920A", icon: Activity },
        ]
      : agentLevel === "Senior Associate"
      ? [
          { label: "Active Team Members", value: seniorAssociateDirectTeam.length, color: "#84ab4a", icon: BadgeCheck },
          { label: "Pending Applications", value: pendingCount, color: "#C8920A", icon: Clock },
          { label: "Team Leads", value: totalTeamLeads, color: "#1A6FD4", icon: Activity },
        ]
      : [
          { label: "My Team", value: allAssociates.length, color: "#84ab4a", icon: BadgeCheck },
          { label: "Pending Applications", value: pendingCount, color: "#C8920A", icon: Clock },
          { label: "Team Leads", value: totalTeamLeads, color: "#1A6FD4", icon: Activity },
        ];

  // Arbitrary-depth forest built from ALL SAs + Associates in one flat set —
  // any rep (SA or Associate) that recruited another rep in this set becomes
  // an expandable parent, so Associate-recruited-Associate chains nest under
  // their real recruiter instead of flattening into the Agency's direct list.
  const agencyForest = useMemo(() => buildTeamForest([...seniorAssociates, ...allAssociates]), [seniorAssociates, allAssociates]);

  // ── My Team search: name/email, case-insensitive, trimmed ──────────────────
  // Filters the same already-scoped data teamVisibility already returned for
  // this rep (agencies + seniorAssociates + allAssociates) - never fetches or
  // exposes anything the tree view below couldn't already show. This is a
  // fully-loaded, unpaginated list, so a small memoized client-side filter is
  // the correct approach (no server round-trip needed). Note: sub-agency
  // members nested under an unexpanded AgencyDrillDownRow are loaded lazily on
  // click and are not part of teamVisibility, so they aren't searchable until
  // that row is expanded - same visibility boundary the tree view already has.
  const normalizedTeamSearch = teamSearch.trim().toLowerCase();
  const teamSearchResults = useMemo(
    () => filterTeamMembersBySearch([...agencies, ...seniorAssociates, ...allAssociates], teamSearch),
    [teamSearch, agencies, seniorAssociates, allAssociates],
  );

  const toggleExpand = (repCode: string) => {
    setExpandedRepCodes(prev => {
      const next = new Set(prev);
      next.has(repCode) ? next.delete(repCode) : next.add(repCode);
      return next;
    });
  };

  const handleNominate = (rep: any) => {
    if (rep.agentLevel === "Senior Associate") {
      nominateSAMutation.mutate({ seniorAssociateRepCode: rep.repCode, nominatedByRepCode: repCode, requestNotes: nominateNotes || undefined });
    } else {
      nominateMutation.mutate({ associateRepCode: rep.repCode, nominatedByRepCode: repCode, requestNotes: nominateNotes || undefined });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">My Team</h2>
        <p className="text-sm text-gray-400">You can invite new Associates to join your team.</p>
      </div>

      {/* Invite Link Card */}
      <div className="bg-[#0D1E35] border border-[#84ab4a]/25 rounded-2xl p-6 mb-6 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-48 h-48 bg-[#84ab4a]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-5 h-5 text-[#84ab4a]" />
            <span className="text-white font-semibold text-sm">Your Associate Invite Link</span>
            <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#84ab4a]/15 text-[#84ab4a]">
              Associate Only
            </span>
          </div>
          <p className="text-white/50 text-xs mb-4">
            Share this link to invite people to join as sales associates under your organization. They will be a part of your team.
          </p>
          {isLoading ? (
            <div className="h-10 bg-white/5 rounded-xl animate-pulse" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white/70 font-mono truncate">
                {inviteLink || "Loading..."}
              </div>
              <button
                onClick={handleCopy}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#84ab4a] hover:bg-[#6e9038] text-white text-xs font-semibold transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          )}
        </div>
      </div>


      {/* ── Stats Row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        {statsItems.map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4" style={{ color }} />
              <span className="text-xs font-semibold text-gray-500">{label}</span>
            </div>
            <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── My Team Search - name or email, case-insensitive, live-as-typed ── */}
      {!isLoading && (
        <div className="mb-5">
          <label htmlFor="my-team-search" className="sr-only">Search your team by name or email</label>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 max-w-[340px] shadow-sm focus-within:border-[#84ab4a] focus-within:ring-1 focus-within:ring-[#84ab4a]/20 transition-all">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              id="my-team-search"
              type="text"
              className="flex-1 bg-transparent outline-none text-[13.5px] text-gray-800 placeholder-gray-400"
              placeholder="Search your team by name or email…"
              value={teamSearch}
              onChange={e => setTeamSearch(e.target.value)}
              aria-label="Search your team by name or email"
            />
            {teamSearch && (
              <button
                type="button"
                onClick={() => setTeamSearch("")}
                aria-label="Clear team search"
                className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Hierarchical Team View ── */}
      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm py-12 text-center text-gray-400 text-sm mb-5">
          Loading team...
        </div>
      ) : normalizedTeamSearch ? (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">
              Search Results - {teamSearchResults.length} match{teamSearchResults.length !== 1 ? "es" : ""}
            </span>
          </div>
          {teamSearchResults.length === 0 ? (
            <div className="py-12 text-center">
              <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-400 mb-1">No team members match "{teamSearch.trim()}"</p>
              <p className="text-xs text-gray-300">Try a different name or email. Members inside a collapsed sub-agency aren't searchable until that row is expanded.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Joined</th>
                </tr>
              </thead>
              <tbody>
                {teamSearchResults.map((r: any) => (
                  <TeamMemberRow key={r.id} rep={r} leadCount={leadCountMap[r.repCode] ?? 0} showLeads={false} />
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      ) : (agentLevel === "Super Agency" || agentLevel === "Super Team") ? (
        <>
          {agencies.length === 0 && seniorAssociates.length === 0 && allAssociates.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm py-12 text-center mb-5">
              <Users2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-400 mb-1">No team members yet</p>
            </div>
          ) : (
            <>
              {/* Agencies / Super Teams table — drillable */}
              {agencies.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
                  <div className="px-5 py-3.5 border-b border-gray-100">
                    <span className="text-sm font-bold text-gray-700">
                      {agentLevel === "Super Agency" ? "Teams / Agencies" : "Agencies"} — {agencies.length} member{agencies.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/50">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencies.map((a: any) => (
                        <AgencyDrillDownRow key={a.id} agency={a} />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
              {/* Direct SAs + Associates (transition: promoted from Agency, retains existing downline) */}
              {(seniorAssociates.length > 0 || allAssociates.length > 0) && (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
                  <div className="px-5 py-3.5 border-b border-gray-100">
                    <span className="text-sm font-bold text-gray-700">
                      Direct Team — {seniorAssociates.length} Senior Associate{seniorAssociates.length !== 1 ? "s" : ""}, {allAssociates.length} Associate{allAssociates.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/50">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyForest.map(node => (
                        <TeamTreeRows
                          key={node.rep.id}
                          node={node}
                          depth={0}
                          expandedRepCodes={expandedRepCodes}
                          onToggleExpand={toggleExpand}
                          leadCountMap={leadCountMap}
                          upgradeStatusMap={{}}
                          showLeads={false}
                          showJoined
                          columnCount={5}
                        />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : agentLevel === "Agency" ? (
        <>
          {/* Agency: SAs with nested Associates */}
          {agencies.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-4">
              <div className="px-5 py-3.5 border-b border-gray-100">
                <span className="text-sm font-bold text-gray-700">Sub-Agencies — {agencies.length} Agency{agencies.length !== 1 ? " reps" : " rep"}</span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {agencies.map((a: any) => (
                    <AgencyDrillDownRow key={a.id} agency={a} />
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
          {seniorAssociates.length === 0 && allAssociates.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm py-12 text-center mb-5">
              <Users2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-400 mb-1">No team members yet</p>
              <p className="text-xs text-gray-300">Share your invite link to start building your agency.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
              <div className="px-5 py-3.5 border-b border-gray-100">
                <span className="text-sm font-bold text-gray-700">
                  Agency Overview — {seniorAssociates.length} Senior Associate{seniorAssociates.length !== 1 ? "s" : ""}, {allAssociates.length} Associate{allAssociates.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Leads</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Joined</th>
                    <th className="text-right px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">SA Upgrade</th>
                  </tr>
                </thead>
                <tbody>
                  {agencyForest.map(node => (
                    <TeamTreeRows
                      key={node.rep.id}
                      node={node}
                      depth={0}
                      expandedRepCodes={expandedRepCodes}
                      onToggleExpand={toggleExpand}
                      leadCountMap={leadCountMap}
                      upgradeStatusMap={upgradeStatusMap}
                      onNominate={setNominateTarget}
                      onViewLeads={(r) => setSelectedMember(selectedMember?.repCode === r.repCode ? null : { repCode: r.repCode, name: `${r.firstName} ${r.lastName}` })}
                      selectedRepCode={selectedMember?.repCode ?? null}
                      showLeads
                      showJoined
                      columnCount={7}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* ── Agency: Limited Lead View for Selected Member ── */}
          {selectedMember && (() => {
            const limitedLeads = teamMemberLeadsQuery.data ?? [];
            const isLoadingLeads = teamMemberLeadsQuery.isLoading;

            // Group by verticalName
            const byVertical: Record<string, typeof limitedLeads> = {};
            for (const lead of limitedLeads) {
              const v = lead.verticalName ?? "Other";
              if (!byVertical[v]) byVertical[v] = [];
              byVertical[v].push(lead);
            }
            const verticals = Object.keys(byVertical).sort();

            const getDisplayName = (lead: (typeof limitedLeads)[0]) => {
              if (lead.businessName) return lead.businessName;
              return [lead.firstName, lead.lastName].filter(Boolean).join(" ") || "Unknown";
            };
            const getStageLabel = (lead: (typeof limitedLeads)[0]) => {
              if (lead.ghlStageName) return lead.ghlStageName;
              return (lead.stage ?? "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
            };

            return (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-5">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-gray-800">Leads — {selectedMember.name}</span>
                    {!isLoadingLeads && (
                      <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        {limitedLeads.length} active
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {isLoadingLeads ? (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">Loading leads...</div>
                ) : limitedLeads.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">No active leads for this member.</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {verticals.map(v => (
                      <div key={v} className="px-5 py-3">
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{v}</div>
                        <ul className="space-y-1.5">
                          {byVertical[v].map(lead => (
                            <li key={lead.id} className="flex items-center gap-2">
                              <span className="text-sm text-gray-800 font-medium">{getDisplayName(lead)}</span>
                              <span className="text-gray-300">—</span>
                              <span className="text-xs text-gray-500">{getStageLabel(lead)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      ) : (
        /* Senior Associate or Associate: flat list */
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-700">
              {agentLevel === "Senior Associate"
                ? `Direct Team (${seniorAssociateDirectTeam.length})`
                : `Team Associates (${allAssociates.length})`}
            </span>
          </div>
          {(agentLevel === "Senior Associate" ? seniorAssociateDirectTeam : allAssociates).length === 0 ? (
            <div className="py-12 text-center">
              <Users2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-400 mb-1">
                {agentLevel === "Senior Associate" ? "No team members yet" : "No associates yet"}
              </p>
              <p className="text-xs text-gray-300">Share your invite link to start building your team.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Leads</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Joined</th>
                </tr>
              </thead>
              <tbody>
                {(agentLevel === "Senior Associate" ? seniorAssociateDirectTeam : allAssociates).map((r: any) => (
                  <TeamMemberRow key={r.id} rep={r} leadCount={leadCountMap[r.repCode] ?? 0} />
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* ── Agency: SA Nomination Modal ── */}
      {nominateTarget && (() => {
        const isSAPromotion = nominateTarget.agentLevel === "Senior Associate";
        const isPending = isSAPromotion ? nominateSAMutation.isPending : nominateMutation.isPending;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="mb-4">
              <h3 className="text-base font-bold text-gray-900">{isSAPromotion ? "Promote to Agency" : "Nominate for Senior Associate"}</h3>
              <p className="text-sm text-gray-500 mt-1">
                You are {isSAPromotion ? "promoting" : "nominating"} <span className="font-semibold text-gray-800">{nominateTarget.firstName} {nominateTarget.lastName}</span> ({nominateTarget.repCode}) {isSAPromotion ? "to Agency" : "for a Senior Associate upgrade"}. This request will be reviewed by BC360 Admin, and your existing team members stay attached to {isSAPromotion ? "them" : "your agency"} throughout.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Why are you {isSAPromotion ? "promoting" : "nominating"} them? <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={nominateNotes}
                onChange={e => setNominateNotes(e.target.value)}
                rows={3}
                maxLength={800}
                placeholder="Describe their performance, activity, or readiness for this role..."
                className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#84ab4a]/50 resize-none"
              />
              <div className="text-[10px] text-gray-400 mt-1">{nominateNotes.length}/800</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setNominateTarget(null); setNominateNotes(""); }}
                disabled={isPending}
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleNominate(nominateTarget)}
                disabled={isPending}
                className="flex-1 px-4 py-2 rounded-xl bg-[#1A6FD4] hover:bg-[#1559b0] disabled:opacity-50 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-1.5"
              >
                {isPending && <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {isSAPromotion ? "Submit Promotion Request" : "Submit Nomination"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Pending Applications — only show truly pending (not yet approved/active) */}
      {/* Cross-reference against active reps by email: if a pending request's email matches
          an active rep in the downline, the person was already activated (possibly by a
          separate admin action) and should not appear here as "pending". */}
      {(() => {
        const activeDownlineEmails = new Set(
          downline.filter((r: any) => r.status === "active").map((r: any) => (r.email ?? "").toLowerCase())
        );
        const activeDownlineRepIds = new Set(
          downline.filter((r: any) => r.status === "active").map((r: any) => r.id)
        );
        const trulyPending = pendingRequests.filter((r: any) =>
          r.status === "pending"
          && !activeDownlineEmails.has((r.email ?? "").toLowerCase())
          && !activeDownlineRepIds.has(r.repId)
        );
        return trulyPending.length > 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">Pending Applications from Your Invite Link ({trulyPending.length})</span>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Applied</th>
              </tr>
            </thead>
            <tbody>
              {trulyPending.map((r: any) => (
                <tr key={r.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="px-5 py-3 font-semibold text-gray-900">{r.firstName} {r.lastName}</td>
                  <td className="px-5 py-3 text-xs text-gray-500">{r.email}</td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">pending</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-400">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        ) : null;
      })()}
    </div>
  );
}

// ─── Rep Login Screen ─────────────────────────────────────────────────────────
function RepLogin({ onLogin }: { onLogin: (code: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);
  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  // Reset password states — auto-detect ?resetToken= in URL
  const resetToken = new URLSearchParams(window.location.search).get("resetToken") ?? null;
  const [resetMode] = useState(!!resetToken);
  const [newPassword, setNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const loginMutation = trpc.rep.login.useMutation();
  const requestResetMutation = trpc.rep.requestPasswordReset.useMutation({
    onSuccess: () => setForgotSent(true),
    onError: (e: any) => setError(e.message),
  });
  const resetPasswordMutation = trpc.rep.resetPassword.useMutation({
    onSuccess: () => setResetDone(true),
    onError: (e: any) => setError(e.message),
  });

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) { setError("Enter your username and password"); return; }
    setLoading(true);
    setError("");
    setIsPending(false);
    try {
      const result = await loginMutation.mutateAsync({ username: username.trim(), password });
      onLogin(result.repCode);
    } catch (e: any) {
      if (e?.message === "PENDING_VERIFICATION") {
        setIsPending(true);
      } else {
        setError(e?.message ?? "Invalid username or password.");
      }
    }
    setLoading(false);
  };

  // ── Reset password screen (arrived from email link) ──
  if (resetMode) {
    if (resetDone) return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 rounded-2xl bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Password Updated</h1>
          <p className="text-white/50 text-sm mb-6">Your password has been reset. You can now sign in with your new password.</p>
          <button onClick={() => window.location.href = window.location.pathname} className="text-[#84ab4a] text-sm font-semibold hover:underline">
            Back to Login
          </button>
        </div>
      </div>
    );
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BC360" className="h-14 object-contain mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-1">Set New Password</h1>
            <p className="text-sm text-white/40">Choose a new password for your account.</p>
          </div>
          <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#84ab4a] uppercase tracking-widest mb-2">New Password</label>
              <input
                type="password"
                className="w-full bg-[#0A1628] border border-white/10 text-white placeholder-white/25 rounded-xl px-4 py-3 focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 transition-all"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
              />
            </div>
            {error && <p className="text-xs text-red-400 text-center">{error}</p>}
            <button
              onClick={() => {
                if (!newPassword || newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
                resetPasswordMutation.mutate({ token: resetToken!, newPassword });
              }}
              disabled={resetPasswordMutation.isPending}
              className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60"
            >
              {resetPasswordMutation.isPending ? "Updating..." : "Set New Password"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Forgot password screen ──
  if (forgotMode) {
    if (forgotSent) return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#84ab4a]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Check Your Email</h1>
          <p className="text-white/50 text-sm mb-6">If an account exists for <span className="text-white/80">{forgotEmail}</span>, a password reset link has been sent.</p>
          <button onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }} className="text-white/30 hover:text-white/60 text-xs transition-colors">
            &larr; Back to login
          </button>
        </div>
      </div>
    );
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BC360" className="h-14 object-contain mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-1">Reset Password</h1>
            <p className="text-sm text-white/40">Enter your email address and we'll send a reset link.</p>
          </div>
          <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#84ab4a] uppercase tracking-widest mb-2">Email Address</label>
              <input
                type="email"
                className="w-full bg-[#0A1628] border border-white/10 text-white placeholder-white/25 rounded-xl px-4 py-3 focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 transition-all"
                placeholder="your@email.com"
                value={forgotEmail}
                onChange={(e) => { setForgotEmail(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && requestResetMutation.mutate({ email: forgotEmail })}
              />
            </div>
            {error && <p className="text-xs text-red-400 text-center">{error}</p>}
            <button
              onClick={() => requestResetMutation.mutate({ email: forgotEmail })}
              disabled={requestResetMutation.isPending}
              className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60"
            >
              {requestResetMutation.isPending ? "Sending..." : "Send Reset Link"}
            </button>
            <button onClick={() => { setForgotMode(false); setError(""); }} className="w-full text-white/30 hover:text-white/60 text-xs transition-colors py-1">
              &larr; Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-20 h-20 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
            <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BC360" className="w-full h-full object-contain p-2" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Account Pending Approval</h1>
          <p className="text-white/50 text-sm mb-6">Your account is awaiting identity verification and admin approval. You will receive an email with your login credentials once your account is approved.</p>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 text-left space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-400 text-xs font-bold">1</span>
              </div>
              <div>
                <p className="text-amber-300 text-sm font-semibold">Identity Verification</p>
                <p className="text-white/40 text-xs">Check your email for a verification link to upload your ID</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-400 text-xs font-bold">2</span>
              </div>
              <div>
                <p className="text-amber-300 text-sm font-semibold">Admin Review</p>
                <p className="text-white/40 text-xs">Our team will review your documents and approve your account</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-400 text-xs font-bold">3</span>
              </div>
              <div>
                <p className="text-amber-300 text-sm font-semibold">Access Granted</p>
                <p className="text-white/40 text-xs">You'll receive your login credentials by email once approved</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => { setIsPending(false); setUsername(""); setPassword(""); }}
            className="text-white/30 hover:text-white/60 text-xs transition-colors"
          >
            &larr; Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BC360" className="h-14 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Agent Portal</h1>
          <p className="text-sm text-white/40">BizConnect360 — Secure Agent Login</p>
        </div>
        <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#84ab4a] uppercase tracking-widest mb-2">Username</label>
            <input
              autoComplete="username"
              className="w-full bg-[#0A1628] border border-white/10 text-white placeholder-white/25 rounded-xl px-4 py-3 focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 transition-all"
              placeholder="your.username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#84ab4a] uppercase tracking-widest mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="w-full bg-[#0A1628] border border-white/10 text-white placeholder-white/25 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 transition-all"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-xs text-red-400 text-center">{error}</p>}
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60"
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
          <button type="button" onClick={() => { setForgotMode(true); setError(""); }} className="w-full text-white/30 hover:text-white/60 text-xs transition-colors py-1">
            Forgot password?
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Submit Lead Form ─────────────────────────────────────────────────────────
function SubmitLeadForm({ repCode, allowedVerticals, onClose, onSuccess, defaultVertical }: { repCode: string; allowedVerticals: string[] | null; onClose: () => void; onSuccess: () => void; defaultVertical?: string }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", businessName: "", verticalName: defaultVertical ?? "", plan: "" as "" | "lite" | "standard" | "pro", notes: "" });
  const availableVerticals = allowedVerticals === null
    ? Object.entries(VERTICAL_CONFIG).map(([v]) => ({ value: v, label: v }))
    : Object.entries(VERTICAL_CONFIG).filter(([v]) => allowedVerticals.includes(v)).map(([v]) => ({ value: v, label: v }));
  const createLead = trpc.lead.create.useMutation({
    onSuccess: () => { toast.success("Lead submitted successfully!"); onSuccess(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim()) { toast.error("First name required"); return; }
    createLead.mutate({ ...form, repCode, plan: form.plan || undefined, source: "rep_submission" });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">First Name *</label>
          <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} placeholder="John" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Last Name</label>
          <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} placeholder="Smith" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Email</label>
          <input type="email" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Phone</label>
          <input type="tel" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+1 555-000-0000" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Business Name</label>
        <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.businessName} onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))} placeholder="Acme Corp" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Vertical</label>
          <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.verticalName} onChange={(e) => setForm((f) => ({ ...f, verticalName: e.target.value }))}>
            <option value="">Select vertical</option>
            {availableVerticals.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Plan</label>
          <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a]" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value as any }))}>
            <option value="">Select plan</option>
            <option value="lite">Lite</option>
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Notes</label>
        <textarea className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a] resize-none" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Any notes about this lead..." />
      </div>
      <div className="flex gap-3 pt-1">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={createLead.isPending} className="flex-1 bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0">
          {createLead.isPending ? "Submitting..." : "Submit Lead"}
        </Button>
      </div>
    </form>
  );
}

/// ─── Share360 Enrollment Gate ────────────────────────────────────────────────
const S360_ACK_KEY = "bc360_s360_enrollment_ack";

function Share360EnrollmentGate({ repId, onEnrolled, repFirstName, repLastName, repEmail }: { repId: number; onEnrolled: () => void; repFirstName?: string | null; repLastName?: string | null; repEmail?: string | null }) {
  const statusQuery = trpc.share360.getStatus.useQuery({ repId }, { enabled: !!repId, refetchInterval: 30_000 });
  const createEsignSessionMutation = trpc.share360.createEsignSession.useMutation({
    onError: (e) => toast.error(e.message),
  });
  const submitDocsMutation = trpc.share360.submitDocs.useMutation({
    onSuccess: () => { statusQuery.refetch(); toast.success("Application submitted! Proceed to payment."); },
    onError: (e) => toast.error(e.message),
  });

  // Blocking acknowledgement modal — shown once per session before any enrollment action
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return sessionStorage.getItem(S360_ACK_KEY) === "1"; } catch { return false; }
  });

  // ── e-Signature state ──────────────────────────────────────────────────────
  const [esignDialogOpen, setEsignDialogOpen] = useState(false);
  const [esignStep, setEsignStep] = useState<"enrollment" | "w9">("enrollment");
  const [enrollmentEmbedSrc, setEnrollmentEmbedSrc] = useState<string | null>(null);
  const [w9EmbedSrc, setW9EmbedSrc] = useState<string | null>(null);
  const [enrollmentSubmissionId, setEnrollmentSubmissionId] = useState<number | null>(null);
  const [w9SubmissionId, setW9SubmissionId] = useState<number | null>(null);
  const [esignComplete, setEsignComplete] = useState(false);
  const [esignDocUrl, setEsignDocUrl] = useState<string | null>(null);

  // Stable callbacks — useCallback prevents DocuSeal's internal useEffect from
  // removing and re-adding the "completed" DOM listener on every parent re-render.
  // Order: W-9 first → Enrollment Packet second.

  const handleW9Complete = useCallback(() => {
    setEnrollmentEmbedSrc((currentEnrollment) => {
      if (currentEnrollment) {
        setEsignStep("enrollment");
        toast.success("W-9 signed! Please complete the Enrollment Packet next.");
      } else {
        setEsignComplete(true);
        setEsignDialogOpen(false);
        toast.success("W-9 signed successfully!");
      }
      return currentEnrollment;
    });
  }, []);

  const handleEnrollmentComplete = useCallback((data: any) => {
    const signedUrl = (data?.documents?.[0]?.url as string | undefined) ?? "docuseal-signed";
    setEsignDocUrl(signedUrl);
    setEsignComplete(true);
    setEsignDialogOpen(false);
    toast.success("W-9 & Enrollment Packet signed successfully!");
  }, []);

  // ── Document upload state ──────────────────────────────────────────────────

  // These hooks must be declared before any early return (React rules of hooks).
  // The server decides which safe recovery surface to open. Live
  // subscriptions go to the Customer Portal for an in-place payment-method
  // update; terminal subscriptions go to an idempotent restart checkout.
  const share360Utils = trpc.useUtils();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  async function handleOpenPortal() {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const result = await share360Utils.share360.getBillingPortalUrl.fetch({ repId });
      if (!result?.url) {
        setPortalError(
          result?.reason === "fee_waived"
            ? "Your monthly support fee is waived. Refresh the page to restore access."
            : "Secure billing is unavailable right now. Please try again shortly or contact support@bizconnect360.com."
        );
        return;
      }
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch {
      setPortalError("Could not open the billing portal. Please contact support@bizconnect360.com.");
    } finally {
      setPortalLoading(false);
    }
  }

  const status = statusQuery.data?.share360EnrollmentStatus ?? "not_started";

  // Build payment URLs (both need prefilled rep data).
  const paymentUrlParams = new URLSearchParams();
  if (repFirstName) paymentUrlParams.set("firstName", repFirstName);
  if (repLastName) paymentUrlParams.set("lastName", repLastName);
  if (repEmail) paymentUrlParams.set("email", repEmail);
  const _payQs = paymentUrlParams.toString() ? "?" + paymentUrlParams.toString() : "";
  // $49.95 one-time application fee link, used in enrollment Step 2.
  //
  // NOTE: there is deliberately no monthly-fee payment link here any more.
  // The $24.95/month product is a RECURRING payment link: opening it does not
  // update the card on an associate's existing subscription, it creates a
  // brand-new second subscription on the same Stripe customer. That is the
  // root cause of the duplicate-subscription/double-charge reports of
  // 2026-08-28 (Lester Pearson, Brigit Kennedy, Marian Rey). Updating a
  // payment method must always go through the Stripe Customer Portal, which
  // edits the existing subscription in place.
  const applicationFeeUrl = `https://api.wibiz.ai/payment-link/69b6d407f35c540e1483296f${_payQs}`;

  if (status === "suspended") {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-5">
          <PauseCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Share360 Access Suspended</h2>
        <p className="text-sm text-gray-500 max-w-sm mb-8">
          Your Share360 RA access has been suspended due to a missed subscription payment.
          Update your payment method below and your access is restored automatically once
          the payment goes through.
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <Button
            className="w-full bg-[#1A6FD4] hover:bg-[#1558b0] text-white"
            disabled={portalLoading}
            onClick={handleOpenPortal}
          >
            {portalLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening secure billing...</>
            ) : (
              <><ExternalLink className="w-4 h-4 mr-2" />Restore Share360 Access</>
            )}
          </Button>
          {portalError && <p className="text-xs text-red-600">{portalError}</p>}
          <p className="text-xs text-gray-400 mt-1">
            You can update your payment method or restart a cancelled subscription here.
            Access is restored automatically after your payment succeeds.
          </p>
        </div>
      </div>
    );
  }

  // Open the DocuSeal signing session — creates enrollment + W-9 submissions in parallel
  const handleStartEsign = async () => {
    try {
      const result = await createEsignSessionMutation.mutateAsync({
        repId,
        email: repEmail ?? "",
        firstName: repFirstName ?? undefined,
        lastName: repLastName ?? undefined,
      });
      setEnrollmentEmbedSrc(result.enrollmentEmbedSrc);
      setW9EmbedSrc(result.w9EmbedSrc);
      setEnrollmentSubmissionId(result.enrollmentSubmissionId);
      setW9SubmissionId(result.w9SubmissionId);
      setEsignStep("w9"); // W-9 first, Enrollment Packet second
      setEsignDialogOpen(true);
    } catch { /* error already shown via onError */ }
  };

  // Submit application after e-signing both documents.
  // Passes submission IDs so the server can fetch real signed PDF URLs from DocuSeal API.
  const handleSubmitApplication = async () => {
    if (!esignComplete) {
      toast.error("Please sign the W-9 and Enrollment Packet first.");
      return;
    }
    try {
      await submitDocsMutation.mutateAsync({
        repId,
        docUrl: esignDocUrl ?? "docuseal-signed",
        docKey: "docuseal-signed",
        enrollmentSubmissionId: enrollmentSubmissionId ?? undefined,
        w9SubmissionId: w9SubmissionId ?? undefined,
      });
    } catch (e: any) { toast.error(e.message); }
  };

  if (statusQuery.isLoading) return <div className="py-20 text-center text-gray-400">Loading...</div>;
  if (status === "approved") { onEnrolled(); return null; }

  const STEPS = [
    {
      key: "apply",
      label: "W-9 & Enrollment Packet",
      desc: "Sign your W-9 tax form first, then the Share360 Agent Enrollment Document",
    },
    {
      key: "payment",
      label: "$49.95 Application + Background Check Fee",
      desc: "One-time fee to process your Share360 RA application and background check",
    },
    {
      key: "background",
      label: "Background Check",
      desc: "BC360 will run a background check (2–5 business days) — unlocked alongside Step 4",
    },
    {
      key: "certification",
      label: "Share360 Certification",
      desc: "Pass the Share360 certification quiz — available immediately after payment",
    },
    {
      key: "approval",
      label: "Admin Approval",
      desc: "Final review and account activation by BC360 admin",
    },
  ];

  // Step completion logic — "background_check" means BG done (cert still pending);
  // "certified" means cert done (BG still pending). Both must be true for pending_approval.
  const stepDone = (key: string): boolean => {
    const s = status as string;
    if (key === "apply")         return ["docs_submitted","payment_pending","payment_confirmed","background_check","certified","pending_approval","approved"].includes(s);
    if (key === "payment")       return ["payment_confirmed","background_check","certified","pending_approval","approved"].includes(s);
    if (key === "background")    return ["background_check","pending_approval","approved"].includes(s); // "certified" = BG not yet done
    if (key === "certification") return ["certified","pending_approval","approved"].includes(s);         // "background_check" = cert not yet done
    if (key === "approval")      return s === "approved";
    return false;
  };

  // Unlock logic — BG check and certification both unlock simultaneously after payment
  const stepUnlocked = (key: string): boolean => {
    const s = status as string;
    if (key === "apply") return true;
    if (key === "payment") return ["docs_submitted","payment_pending","payment_confirmed","background_check","certified","pending_approval","approved"].includes(s);
    if (key === "background") return ["payment_confirmed","docs_submitted","background_check","certified","pending_approval","approved"].includes(s);
    // docs_submitted included so cert is accessible after doc upload regardless of BG check order
    if (key === "certification") return ["payment_confirmed","docs_submitted","background_check","certified","pending_approval","approved"].includes(s);
    if (key === "approval") return ["pending_approval","approved"].includes(s);
    return false;
  };


  // Blocking acknowledgement modal
  if (!acknowledged) {
    return (
      <div className="flex items-center justify-center py-20">
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent
            className="max-w-lg"
            onPointerDownOutside={e => e.preventDefault()}
            onEscapeKeyDown={e => e.preventDefault()}
          >
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-[#1A6FD4]/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#1A6FD4]" />
                </div>
                <DialogTitle className="text-base">Before You Continue</DialogTitle>
              </div>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div className="space-y-3 text-sm text-gray-700">
                <p>BizConnect360 is a free ecosystem, and the Fundable360, Legacy360, Scale360, and Media360 platforms are offered and fully supported for all associates at no cost. This includes full tracking, client management systems, and ongoing marketing support.</p>
                <p>The Share360 program requires additional processing due to its financial integrations, compliance requirements, and background verification standards.</p>
                <p>To enroll in Share360, there is a <strong>$49.95 application and background check processing fee</strong>, along with a <strong>$24.95 monthly agent support fee</strong>.</p>
                <p>The monthly support fee is <strong>waived indefinitely</strong> once you successfully onboard <strong>three (3) merchant accounts</strong>.</p>
                <p>This structure ensures the integrity of the platform, proper support for your accounts, and a high standard of service across all merchant relationships.</p>
              </div>
              <Button
                className="w-full bg-[#1A6FD4] hover:bg-[#1558b0] text-white border-0"
                onClick={() => {
                  setAcknowledged(true);
                  try { sessionStorage.setItem(S360_ACK_KEY, "1"); } catch {}
                }}
              >
                I Acknowledge — Proceed to Application
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-10">
      {/* DocuSeal e-Signature overlay
          NOTE: intentionally NOT using Radix <Dialog> here.
          Radix Dialog's FocusTrap intercepts focus events from cross-origin iframes,
          which prevents DocuSeal's "Complete" button click from registering.
          A plain fixed overlay has no focus trapping and lets the iframe work normally. */}
      {esignDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" style={{ backdropFilter: "blur(2px)" }}>
          <div className="max-w-4xl w-[95vw] h-[92vh] flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0 bg-white">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-900">
                    {esignStep === "w9" ? "W-9 Tax Form" : "Share360 Enrollment Packet"}
                  </p>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    Step {esignStep === "w9" ? "1" : "2"} of {enrollmentEmbedSrc ? "2" : "1"}
                  </span>
                  {esignStep === "w9" && enrollmentEmbedSrc && (
                    <span className="text-[11px] text-gray-400">— Enrollment Packet follows after this</span>
                  )}
                </div>
                <p className="text-xs text-gray-700 mt-0.5">Read each section carefully, fill in every highlighted field, then sign at the bottom of each page.</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-[#1A6FD4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                ESIGN / UETA Compliant
              </div>
            </div>

            {/* Field legend — visible above the form */}
            <div className="flex items-center gap-4 px-5 py-2 bg-blue-50 border-b border-blue-100 flex-shrink-0 flex-wrap">
              <span className="text-xs font-semibold text-blue-800">How to complete this form:</span>
              <span className="flex items-center gap-1.5 text-xs text-blue-700">
                <span className="inline-block w-4 h-4 rounded bg-yellow-200 border border-yellow-400 flex-shrink-0" />
                Yellow = required text field — click and type
              </span>
              <span className="flex items-center gap-1.5 text-xs text-blue-700">
                <span className="inline-block w-4 h-4 rounded-full bg-blue-200 border border-blue-400 flex-shrink-0" />
                Circle = signature — click to draw or type your signature
              </span>
              <span className="flex items-center gap-1.5 text-xs text-blue-700">
                <span className="inline-block w-4 h-4 rounded bg-green-200 border border-green-400 flex-shrink-0" />
                Green = date field — auto-filled
              </span>
            </div>

            {/* Signing form */}
            <div className="flex-1 overflow-auto bg-gray-50">
              {esignStep === "enrollment" && enrollmentEmbedSrc && (
                <DocusealForm
                  key={enrollmentEmbedSrc}
                  src={enrollmentEmbedSrc}
                  withTitle={false}
                  withDownloadButton={false}
                  autoscrollFields={true}
                  onComplete={handleEnrollmentComplete}
                />
              )}
              {esignStep === "w9" && w9EmbedSrc && (
                <DocusealForm
                  key={w9EmbedSrc}
                  src={w9EmbedSrc}
                  withTitle={false}
                  withDownloadButton={false}
                  autoscrollFields={true}
                  onComplete={handleW9Complete}
                />
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-white flex items-center justify-between">
              <p className="text-[11px] text-gray-400">By signing you agree this constitutes a legal electronic signature under ESIGN &amp; UETA.</p>
              <button
                onClick={() => setEsignDialogOpen(false)}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "#1A6FD415" }}>
          <Shield className="w-8 h-8 text-[#1A6FD4]" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Share360 RA Application</h3>
        <p className="text-sm text-gray-500">Complete the steps below to become a Share360 Retail Associate.</p>
        {status === "rejected" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
            <strong>Application Rejected</strong> — Please contact your admin for details.
          </div>
        )}
      </div>

      <div className="space-y-3">
        {STEPS.map((step, i) => {
          const done = stepDone(step.key);
          const unlocked = stepUnlocked(step.key);
          const active = !done && unlocked;
          return (
            <div
              key={step.key}
              className={`rounded-xl border p-4 transition-all ${
                done
                  ? "bg-green-50 border-green-200"
                  : active
                    ? "bg-blue-50 border-blue-200"
                    : "bg-gray-50 border-gray-200 opacity-60"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${done ? "bg-green-500 text-white" : active ? "bg-[#1A6FD4] text-white" : "bg-gray-300 text-gray-600"}`}>
                  {done ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900">{step.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>

                  {/* ── Step 1: e-Sign Enrollment Packet & W-9 ── */}
                  {active && step.key === "apply" && (
                    <div className="mt-3 space-y-3">
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-2">
                          Review and sign both the enrollment agreement and W-9 tax form in the secure signing window. Your information is pre-filled automatically.
                        </p>
                        {esignComplete ? (
                          <div className="flex items-center gap-2 text-green-700 text-xs font-semibold">
                            <CheckCircle className="w-4 h-4" /> Signed — enrollment packet & W-9 complete
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-gray-900 hover:bg-black text-white border-0 text-xs"
                            onClick={handleStartEsign}
                            disabled={createEsignSessionMutation.isPending}
                          >
                            {createEsignSessionMutation.isPending
                              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Opening...</>
                              : <><FileText className="w-3 h-3 mr-1" /> Open Enrollment Packet & W-9</>
                            }
                          </Button>
                        )}
                      </div>

                      {esignComplete && (
                        <Button
                          size="sm"
                          className="w-full bg-[#1A6FD4] hover:bg-[#1558b0] text-white border-0"
                          onClick={handleSubmitApplication}
                          disabled={submitDocsMutation.isPending}
                        >
                          {submitDocsMutation.isPending
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Submitting...</>
                            : <><CheckCircle className="w-3 h-3 mr-1" /> Submit Application</>
                          }
                        </Button>
                      )}
                    </div>
                  )}

                  {/* ── Step 2: Payment ── */}
                  {active && step.key === "payment" && (
                    <div className="mt-3">
                      {status === "docs_submitted" ? (
                        <>
                          <p className="text-xs text-gray-600 mb-2">
                            Your documents were received. Pay the one-time $49.95 fee below. Payment is confirmed automatically — no manual action needed after paying.
                          </p>
                          <a
                            href={applicationFeeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
                          >
                            Pay $49.95 Now
                          </a>
                        </>
                      ) : (
                        <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" /> Payment processing — confirming automatically...
                          <div className="mt-2">
                            <a href={applicationFeeUrl} target="_blank" rel="noopener noreferrer" className="text-[#1A6FD4] underline text-xs">
                              Pay again / view receipt
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Step 3: Background Check (concurrent with Step 4) ── */}
                  {active && step.key === "background" && (
                    <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" /> Background check in progress — typically 2–5 business days. You will be notified when it clears.
                      <p className="mt-1.5 text-amber-600">You can complete Step 4 (Certification Quiz) while this runs.</p>
                    </div>
                  )}

                  {/* ── Step 4: Certification Quiz (concurrent with Step 3) ── */}
                  {active && step.key === "certification" && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600 mb-2">
                        Your certification quiz is now unlocked. Go to <strong>Certifications</strong> in the sidebar and complete the Share360 quiz. You don't need to wait for the background check.
                      </p>
                    </div>
                  )}

                  {/* ── Step 5: Admin Approval ── */}
                  {active && step.key === "approval" && (
                    <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                      <Clock className="w-3 h-3 inline mr-1" /> Awaiting final admin approval — you'll be notified once approved.
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Share360 Monthly Fee Card ────────────────────────────────────────────────
//
// This card used to carry a "Pay early" link to the $24.95/month Wibiz
// RECURRING payment link. That link does not take a one-off payment against
// an existing subscription: it opens a new order form and creates a SECOND
// monthly subscription on the same Stripe customer. An associate in perfectly
// good standing could double their own billing by clicking it, with nothing
// in BC360 aware the second subscription existed. See
// decideShare360SubscriptionReconciliation in server/db.ts for the full
// root-cause writeup of the 2026-08-28 duplicate reports.
//
// The monthly fee is collected automatically by the subscription, so there is
// nothing for an associate to "pay" here. What they may legitimately want is
// to see invoices or change their card, and that goes through the Stripe
// Customer Portal, which edits the subscription in place.
function Share360MonthlyFeeCard({ repId, repCode, repFirstName, repLastName, repEmail }: {
  repId: number;
  repCode: string;
  repFirstName?: string | null;
  repLastName?: string | null;
  repEmail?: string | null;
}) {
  const feeQuery = trpc.share360.getMonthlyFeeStatus.useQuery(
    { repId, repCode },
    { staleTime: 60_000, refetchInterval: 120_000 }
  );

  const feeCardUtils = trpc.useUtils();
  const [feeCardPortalLoading, setFeeCardPortalLoading] = useState(false);
  async function openFeeCardPortal() {
    setFeeCardPortalLoading(true);
    try {
      const result = await feeCardUtils.share360.getBillingPortalUrl.fetch({ repId });
      if (result?.url) window.open(result.url, "_blank", "noopener,noreferrer");
      else toast.error("The billing portal is unavailable right now. Please contact support@bizconnect360.com.");
    } catch {
      toast.error("Could not open the billing portal. Please contact support@bizconnect360.com.");
    } finally {
      setFeeCardPortalLoading(false);
    }
  }

  if (feeQuery.isLoading) return null;
  const { waived, merchantCount, nextDueAt, paidUp, billingActive } = feeQuery.data ?? { waived: false, merchantCount: 0, nextDueAt: null, paidUp: false, billingActive: false };

  // Waived: ≥3 onboarded merchants. Never show a payment obligation or due date.
  if (waived) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-green-50 border border-green-200">
        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold text-green-800">Monthly support fee waived</span>
          <span className="text-xs text-green-600 ml-2">You have {merchantCount} onboarded merchant account{merchantCount !== 1 ? "s" : ""} — fee waived indefinitely once you reach 3+.</span>
        </div>
      </div>
    );
  }

  // Billing not active (accidental subscription or inactive state): no payment obligation shown.
  if (!billingActive) return null;

  if (paidUp && nextDueAt) {
    const dueDate = new Date(nextDueAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    return (
      <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-blue-50 border border-blue-200">
        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold text-blue-800">Monthly fee paid</span>
          <span className="text-xs text-blue-600 ml-2">Next due: {dueDate}</span>
        </div>
        <button
          type="button"
          onClick={openFeeCardPortal}
          disabled={feeCardPortalLoading}
          className="text-xs text-blue-500 underline underline-offset-2 flex-shrink-0 disabled:text-blue-300"
        >
          {feeCardPortalLoading ? "Opening..." : "Manage billing"}
        </button>
      </div>
    );
  }

  // Fee is due or has never been paid — show status message only, no pay button
  return (
    <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-amber-50 border border-amber-200">
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-sm font-semibold text-amber-800">$24.95 monthly support fee due</span>
        <span className="text-xs text-amber-700 ml-2">
          Waived after 3 onboarded merchants. You currently have {merchantCount}.
        </span>
      </div>
    </div>
  );
}

// ─── Vertical Bucket Content ──────────────────────────────────────────────────
type LeadRow = {
  id: number;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  businessName?: string | null;
  phone?: string | null;
  verticalName?: string | null;
  stage: string;
  ghlStageName?: string | null;
  repCode?: string | null;
  plan?: string | null;
  setupFee?: any;
  monthlyFee?: any;
  // Vertical intake fields — present at runtime from SELECT * even if not in this type
  f360BusinessName?: string | null; f360Industry?: string | null; f360Revenue?: string | null;
  f360MainGoal?: string | null; f360AdditionalInfo?: string | null; f360CreditStatus?: string | null;
  f360TimeInBusiness?: string | null; f360AffiliateRefId?: string | null; f360AgentId?: string | null;
  f360RefId?: string | null; f360ReferrerAgentId?: string | null;
  l360LeadType?: string | null; l360State?: string | null; l360TrustGoal?: string | null;
  l360PlanningType?: string | null; l360FormName?: string | null; l360ExistingPlan?: string | null;
  l360RealEstate?: string | null; l360AssetValue?: string | null; l360HasBeneficiaries?: string | null;
  l360Timeline?: string | null; l360PreferredContact?: string | null; l360QuestionsComments?: string | null;
  l360ConsentToContact?: string | null; l360PageTitle?: string | null; l360PageUrl?: string | null;
  l360Plan125NumW2Employees?: string | null; l360Plan125PayrollProvider?: string | null;
  l360Plan125HasHealthIns?: string | null; l360Plan125PayrollReportUrl?: string | null;
  s360AuditScore?: string | null; s360AuditStatus?: string | null; s360PlanName?: string | null;
  s360PlanDesc?: string | null; s360PlanPrice?: string | null; s360LossPerYear?: string | null;
  s360LossPerMonth?: string | null; s360SavedHours?: string | null; s360Roi?: string | null;
  s360IndustryName?: string | null; s360EconomyTier?: string | null; s360PdfLink?: string | null;
  s360AnalysisPdfUrl?: string | null; s360AnalysisSessionId?: string | null;
  // Additional Comprehensive Analysis completion signals — some leads carry only the
  // report filename (scale360_analysis_report) and no PDF URL. See computeScale360Funnel.
  s360AnalysisReportFileName?: string | null; s360AnalysisPlanName?: string | null;
  sh360Role?: string | null; sh360BusinessName?: string | null; sh360OrgName?: string | null;
  sh360Industry?: string | null; sh360JobTitle?: string | null; sh360State?: string | null;
  sh360InterestType?: string | null; sh360PrimaryGoal?: string | null;
  ghlContactId?: string | null;
  ghlPipelineId?: string | null;
  metadata?: Record<string, string> | null;
};
// Scale360 referral funnel status lives in @shared/scale360 so it stays in step with the
// pipeline stage order the server uses. It reads fields already scoped to the rep's own
// leads (leadsQuery is filtered by repCode server-side — see leadRouter.list).
// The step shape ({ key, label, done }[]) is kept vertical-agnostic so other direct-funnel
// verticals can reuse the ReferralFunnelBadges renderer with their own milestone flags.

function ReferralFunnelBadges({ steps }: { steps: { key: string; label: string; done: boolean }[] }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {steps.map(s => (
        <span
          key={s.key}
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${s.done ? "bg-[#84ab4a]/15 text-[#84ab4a]" : "bg-gray-100 text-gray-400"}`}
        >
          {s.done ? "✓ " : ""}{s.label}
        </span>
      ))}
    </div>
  );
}

function VerticalBucketContent({
  vertical, subTab, repCode, agentLevel, leads, allLeads, onLeadClick, onSubmitLead, materialsQuery, foldersQuery, certifiedVerticals, repId, repFirstName, repLastName, repEmail, share360EnrollmentStatus, share360FeeBlocked
}: {
  vertical: string;
  subTab: "clients" | "leads" | "pipeline" | "documents" | "reporting" | "referrallinks";
  repCode: string;
  agentLevel: string;
  leads: LeadRow[];
  allLeads: LeadRow[];
  onLeadClick: (id: number) => void;
  onSubmitLead: () => void;
  materialsQuery: any;
  foldersQuery: any;
  certifiedVerticals: string[];
  repId?: number;
  repReferralLink?: string;
  repFirstName?: string | null;
  repLastName?: string | null;
  repEmail?: string | null;
  share360EnrollmentStatus?: string;
  share360FeeBlocked?: boolean;
}) {
  const cfg = VERTICAL_CONFIG[vertical];
  const color = cfg?.color ?? "#84ab4a";
  const isCertified = !cfg?.certRequired || certifiedVerticals.includes(vertical) ||
    ((vertical === "Share360 Business" || vertical === "Share360 Nonprofit") && certifiedVerticals.includes("Share360"));

  // Filter leads for this vertical.
  //
  // For Share360 sub-verticals, sh360Role is the authoritative routing signal — it maps
  // directly from the form's submission_type field and is also stored on the GHL contact as
  // {{ contact.sh360_role }}. This ensures the agent tab always matches GHL, even when the
  // stored verticalName is generic "Share360" or temporarily wrong due to pipeline sync order.
  const verticalLeads = leads.filter(l => {
    const sh360Role = (l as any).sh360Role as string | null | undefined;
    const isShare360Family = (l.verticalName ?? "").startsWith("Share360") || sh360Role === "nonprofit" || sh360Role === "business";

    if (isShare360Family) {
      // sh360Role is the primary authority for Business vs Nonprofit routing
      if (sh360Role === "nonprofit") {
        return vertical === "Share360 Nonprofit" || vertical === "Share360";
      }
      if (sh360Role === "business") {
        return vertical === "Share360 Business" || vertical === "Share360";
      }
      // sh360Role absent — fall back to verticalName, then form name heuristic
      const formName = ((l as any).sh360FormName ?? "").toLowerCase();
      const isNonprofitByName = formName.includes("nonprofit") || formName.includes("non profit");
      if (vertical === "Share360") return true;
      if (vertical === "Share360 Nonprofit") return l.verticalName === "Share360 Nonprofit" || (l.verticalName === "Share360" && isNonprofitByName);
      if (vertical === "Share360 Business")  return l.verticalName === "Share360 Business"  || (l.verticalName === "Share360" && !isNonprofitByName);
      return false;
    }

    // Non-Share360 verticals: exact match
    return l.verticalName === vertical;
  });
  const clientLeads = verticalLeads.filter(l => ["payment_received", "onboarding_submitted", "active_client"].includes(l.stage));
  const activeLeads = verticalLeads.filter(l => !["active_client", "lost_cold"].includes(l.stage));

  // ─── Legacy360 sub-type filter (Living Trust / 125 Plan / Advantage) ──────────
  // Legacy360's Leads and Clients tabs otherwise show every pipeline's leads mixed
  // together with no way to tell them apart — see legacy360PipelineKeyForLead above.
  // New Agents is intentionally excluded: it's sourced from the certifications
  // table (admin-only), not from lead/opportunity records — see AdminDashboard.tsx.
  const legacy360SubType = (lead: LeadRow): string => legacy360SubtypeLabelForLead(lead);
  const [legacy360Filter, setLegacy360Filter] = useState<string>("all");
  const legacy360FilterOptions = ["all", "Living Trust", "125 Plan", "Advantage / Expansion"];
  const verticalLeadsFiltered = (vertical === "Legacy360" && legacy360Filter !== "all")
    ? verticalLeads.filter(l => legacy360SubType(l) === legacy360Filter)
    : verticalLeads;
  const legacy360TypeFilterBar = vertical === "Legacy360" ? (
    <div className="flex items-center gap-1.5 mb-4 flex-wrap">
      {legacy360FilterOptions.map(opt => (
        <button
          key={opt}
          onClick={() => setLegacy360Filter(opt)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${legacy360Filter === opt ? "text-white shadow-sm" : "bg-white border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3]"}`}
          style={legacy360Filter === opt ? { background: color, borderColor: color } : {}}
        >
          {opt === "all" ? "All Legacy360" : opt}
        </button>
      ))}
    </div>
  ) : null;

  // Portal link state
  const [portalLinks, setPortalLinks] = useState<Record<number, string>>({});
  const [generatingFor, setGeneratingFor] = useState<number | null>(null);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState<string | null>(null);
  async function handleOpenBillingPortal() {
    setBillingPortalLoading(true);
    setBillingPortalError(null);
    try {
      const res = await fetch(`/api/billing/share360/billing-portal?repCode=${encodeURIComponent(repCode)}`);
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error ?? "Could not load billing portal");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setBillingPortalError(e.message ?? "Something went wrong. Please contact support.");
    } finally {
      setBillingPortalLoading(false);
    }
  }
  const [selectedModule, setSelectedModule] = useState<any | null>(null);
  // Reset open module when vertical changes so stale module from another vertical isn't shown
  useEffect(() => { setSelectedModule(null); }, [vertical]);
  const [expandedMaterials, setExpandedMaterials] = useState<Record<number, boolean>>({});
  const toggleMaterial = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedMaterials(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const generateLink = trpc.clientPortal.generateLink.useMutation({
    onSuccess: (data, vars) => {
      setPortalLinks(prev => ({ ...prev, [vars.leadId]: data.portalUrl }));
      navigator.clipboard.writeText(data.portalUrl);
      if (data.ghlUpdateStatus === "failed") {
        toast.warning("Portal link generated. Wibiz contact update failed — share the link manually as the client may not receive an automated email.");
      } else {
        toast.success("Portal link copied to clipboard!");
      }
      setGeneratingFor(null);
    },
    onError: (e) => { toast.error(e.message); setGeneratingFor(null); },
  });

  // Share360 bypasses the cert gate — the enrollment gate handles all access control
  const isShare360 = vertical.startsWith("Share360");

  // Vertical header card — shown on every tab
  const vHeaderCard = (
    <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-6 mb-5 relative overflow-hidden flex items-center gap-5">
      <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[18px]" style={{ background: color }} />
      <div className="w-14 h-14 rounded-[16px] flex items-center justify-center text-2xl font-bold text-white flex-shrink-0" style={{ fontFamily: "'Spectral',serif", background: color, boxShadow: `0 4px 14px ${color}40` }}>
        {vertical.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[24px] font-semibold text-[#2C2723] leading-tight" style={{ fontFamily: "'Spectral',serif" }}>{vertical}</div>
        <div className="text-[13px] text-[#6E665A] mt-0.5">{VERTICAL_TAGS[vertical] ?? ""}</div>
      </div>
      <div className="flex gap-8 flex-shrink-0">
        {[
          { v: clientLeads.length, l: "Clients" },
          { v: verticalLeads.length, l: "Leads" },
          { v: fmt(((leads ?? []) as any[]).filter((c: any) => c.verticalName === vertical && new Date(c.createdAt ?? 0).getTime() >= new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()).reduce((s: number) => s, 0)), l: "MTD Comm." },
        ].map(({ v, l }) => (
          <div key={l} className="text-center">
            <div className="text-[24px] font-semibold text-[#2C2723] leading-none" style={{ fontFamily: "'Spectral',serif" }}>{v}</div>
            <div className="text-[11px] font-semibold text-[#9C9285] uppercase tracking-wide mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // Pill-style tab bar for desktop
  const SUB_TAB_DEFS = vertical === "BizConnect360"
    ? [{ key: "documents", label: "Resources" }]
    : [
        { key: "documents",    label: "Resources" },
        { key: "clients",      label: "Clients" },
        { key: "leads",        label: "Leads" },
        { key: "pipeline",     label: "Pipeline" },
        { key: "referrallinks",label: "Referral Links" },
        { key: "reporting",    label: "Reporting" },
      ];

  // Resources (documents) are visible before certification; referral links and pipeline require cert
  if (!isCertified && !isShare360 && subTab !== "documents") {
    return (
      <div>
        {vHeaderCard}
        <div className="flex items-center justify-center py-20 bg-white border border-[#EBE2D4] rounded-[18px]">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-[16px] flex items-center justify-center mx-auto mb-4 bg-[#F4EEE3]">
              <Lock className="w-7 h-7 text-[#9C9285]" />
            </div>
            <h3 className="text-[16px] font-bold text-[#2C2723] mb-2">Certification Required</h3>
            <p className="text-[13px] text-[#9C9285] leading-relaxed max-w-[380px]">Complete the {vertical} certification to unlock client management, referral links and commissions for this vertical.</p>
            <div className="mt-4 text-xs text-[#BCB2A4]">Go to <strong className="text-[#6E665A]">Certifications</strong> in the sidebar to take the quiz.</div>
          </div>
        </div>
      </div>
    );
  }
  // ── Share360 Monthly Fee Gate ──
  // Blocks access when the $24.95 monthly support fee is overdue and not waived.
  // Documents tab remains accessible so associates can still view their materials.
  if (isShare360 && share360FeeBlocked && subTab !== "documents") {
    return (
      <div>
        {vHeaderCard}
        <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-8 text-center max-w-md mx-auto">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-amber-900 mb-2">Share360 Access Suspended</h3>
          <p className="text-sm text-amber-700 mb-4">
            Your $24.95 monthly support fee is past due. Share360 access is suspended until your payment is processed.
            Access will be automatically restored once payment is received.
          </p>
          <button
            onClick={handleOpenBillingPortal}
            disabled={billingPortalLoading}
            className="inline-flex items-center justify-center gap-2 w-full max-w-xs mx-auto px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-sm font-semibold transition-colors mb-3"
          >
            {billingPortalLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Opening payment portal…</>
            ) : (
              <><ExternalLink className="w-4 h-4" />Update Payment Method</>
            )}
          </button>
          {billingPortalError && (
            <p className="text-xs text-red-600 mb-3">{billingPortalError}</p>
          )}
          <p className="text-xs text-amber-600">
            Questions? Contact{" "}
            <a href="mailto:support@bizconnect360.com" className="underline font-medium">
              support@bizconnect360.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ── Share360 RA Enrollment Gate (only for Share360 verticals) ──
  // Resources (documents) tab is always accessible — same rule as the cert gate above
  if (isShare360 && repId && share360EnrollmentStatus !== "approved" && subTab !== "documents") {
    return (
      <div>
        {vHeaderCard}
        <Share360EnrollmentGate repId={repId} onEnrolled={() => {}} repFirstName={repFirstName} repLastName={repLastName} repEmail={repEmail} />
      </div>
    );
  }

  // Shared tab bar rendered at top of every tab pane
  const tabBar = (
    <div className="flex gap-1 bg-[#F4EEE3] rounded-[13px] p-1 mb-5 flex-wrap">
      {SUB_TAB_DEFS.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => {
            // propagate tab change up via the sidebar navigation helper (available in parent closure via prop)
            // We trigger this via a custom event so VerticalBucketContent stays stateless
            window.dispatchEvent(new CustomEvent("bc360-subtab", { detail: { vertical, key } }));
          }}
          className={`px-4 py-2 rounded-[10px] text-[13px] font-semibold transition-all ${subTab === key ? "bg-white text-[#C75E12] shadow-sm" : "text-[#6E665A] hover:text-[#2C2723]"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // ── CLIENTS sub-tab ───
  if (subTab === "clients") {
    return (
      <div>
        {vHeaderCard}
        {tabBar}
        {legacy360TypeFilterBar}
        <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-[#F1EADD] flex items-center justify-between">
            <span className="text-sm font-semibold text-[#2C2723]">All {vertical} Leads &amp; Clients</span>
          </div>
          <div className="divide-y divide-[#F1EADD]">
            {verticalLeadsFiltered.length === 0 && (
              <div className="py-12 text-center text-[#9C9285]">
                <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No {vertical} leads yet. Share your referral link to get started!</p>
              </div>
            )}
            {verticalLeadsFiltered.map(lead => {
              const stageColors: Record<string, string> = { lead_captured: "#6B7280", audit_submitted: "#3B82F6", follow_up_active: "#F59E0B", payment_received: "#8B5CF6", onboarding_submitted: "#EC4899", active_client: "#10B981", lost_cold: "#EF4444" };
              const stageLabels: Record<string, string> = { lead_captured: "Inquiry", audit_submitted: "Audit", follow_up_active: "In Review", payment_received: "Payment", onboarding_submitted: "Onboarding", active_client: "Active", lost_cold: "Closed" };
              const sc = stageColors[lead.stage] ?? color;
              const portalUrl = portalLinks[lead.id];
              const isGenerating = generatingFor === lead.id;
              return (
                <div key={lead.id} className="px-5 py-3.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                    {lead.firstName[0]}{lead.lastName?.[0] ?? ""}
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onLeadClick(lead.id)}>
                    {(() => {
                      const contactName = stripRepCode([lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown", lead.repCode) ?? "Unknown";
                      const isS360 = (lead.verticalName ?? "").startsWith("Share360");
                      const meta = (lead as any).metadata as Record<string, unknown> | null | undefined;
                      const metaOrg = meta ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || meta["Org Name"] || "").trim() : "";
                      const orgName = isS360
                        ? (stripRepCode((lead as any).sh360OrgName || (lead as any).sh360BusinessName || metaOrg || null, lead.repCode) ?? (metaOrg || null))
                        : (stripRepCode((lead as any).f360BusinessName || lead.businessName || null, lead.repCode) ?? null);
                      return (<>
                        <div className="font-semibold text-sm text-gray-900 hover:text-[#84ab4a] transition-colors truncate">{contactName}</div>
                        <div className="text-xs text-gray-400 truncate">{orgName ?? lead.email ?? "No contact info"}</div>
                      </>);
                    })()}
                  </div>
                  {vertical === "Legacy360" && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 bg-[#C8920A]/10 text-[#C8920A]">
                      {legacy360SubType(lead)}
                    </span>
                  )}
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: `${sc}15`, color: sc }}>
                    {(lead as any).ghlStageName || stageLabels[lead.stage] || lead.stage}
                  </span>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {portalUrl ? (
                      // Link already generated this session — offer copy/open (no re-email)
                      // plus a "Re-send" option that notifies the client again
                      <>
                        <button onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copied!"); }} className="text-xs font-semibold text-[#84ab4a] border border-[#84ab4a]/30 bg-[#84ab4a]/5 rounded-lg px-2.5 py-1 hover:bg-[#84ab4a]/10 transition-colors flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Copy Link
                        </button>
                        <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Open portal in new tab"><ExternalLink className="w-3.5 h-3.5" /></a>
                        <button
                          onClick={() => { setGeneratingFor(lead.id); generateLink.mutate({ leadId: lead.id, notifyClient: true }); }}
                          disabled={isGenerating || !lead.email}
                          title="Generate a new link and re-notify the client by email"
                          className="text-xs font-semibold text-gray-500 border border-gray-200 bg-white rounded-lg px-2 py-1 hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                          {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Re-send
                        </button>
                      </>
                    ) : (
                      // No link generated yet — show both primary "Send Portal" and silent "Get Link"
                      <>
                        <button
                          onClick={() => { setGeneratingFor(lead.id); generateLink.mutate({ leadId: lead.id, notifyClient: true }); }}
                          disabled={isGenerating || !lead.email}
                          title="Generate a portal link and notify the client by email"
                          className="text-xs font-semibold text-white rounded-lg px-2.5 py-1 transition-colors flex items-center gap-1 disabled:opacity-50"
                          style={{ background: color }}
                        >
                          {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          {isGenerating ? "Generating..." : "Send Portal"}
                        </button>
                        <button
                          onClick={() => { setGeneratingFor(lead.id); generateLink.mutate({ leadId: lead.id, notifyClient: false }); }}
                          disabled={isGenerating || !lead.email}
                          title="Generate a portal link without emailing the client"
                          className="text-xs font-semibold text-gray-500 border border-gray-200 bg-white rounded-lg px-2 py-1 hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                          <Link className="w-3 h-3" /> Get Link
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">Portal links are valid for 30 days. Each lead can only access their own documents and progress.</p>
      </div>
    );
  }

  // ── LEADS sub-tab ──
  if (subTab === "leads") {
    return (
      <div>
        {vHeaderCard}
        {tabBar}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[17px] font-bold text-[#2C2723]">{vertical} — Leads</h2>
            <p className="text-sm text-gray-900">All leads in the {vertical} vertical tagged to your referral ID.</p>
          </div>

        </div>
        {legacy360TypeFilterBar}
        <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F1EADD] bg-[#FBF7F0]">
                <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Lead</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Business</th>
                {vertical === "Legacy360" && (
                  <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Type</th>
                )}
                <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Stage</th>
                {vertical === "Scale360" && (
                  <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Referral Progress</th>
                )}
                <th className="text-left px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Plan</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-[#9C9285] uppercase tracking-wide">Commission Est.</th>
              </tr>
            </thead>
            <tbody>
              {verticalLeadsFiltered.map((lead) => {
                const levelRate = agentLevel === "Agency" ? 0.40 : agentLevel === "Senior Associate" ? 0.25 : 0.20;
                const setupFee = Number(lead.setupFee ?? 0);
                const monthlyFee = Number(lead.monthlyFee ?? 0);
                const commEst = (setupFee + monthlyFee) * levelRate;
                return (
                  <tr key={lead.id} className="border-b border-[#F4EEE3] last:border-0 hover:bg-[#FBF7F0] cursor-pointer" onClick={() => onLeadClick(lead.id)}>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const contactName = stripRepCode([lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown", lead.repCode) ?? "Unknown";
                        return (<>
                          <div className="font-semibold text-[#2C2723] hover:text-[#E2701F] transition-colors">{contactName}</div>
                          <div className="text-xs text-[#9C9285]">{lead.email}</div>
                        </>);
                      })()}
                    </td>
                    <td className="px-5 py-3.5 text-[#6E665A]">
                      {(() => {
                        const isS360 = (lead.verticalName ?? "").startsWith("Share360");
                        const meta = (lead as any).metadata as Record<string, unknown> | null | undefined;
                        const metaOrg = meta ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || meta["Org Name"] || "").trim() : "";
                        const raw = isS360
                          ? ((lead as any).sh360OrgName || (lead as any).sh360BusinessName || metaOrg || null)
                          : ((lead as any).f360BusinessName || lead.businessName || null);
                        const orgName = raw ? (stripRepCode(raw, lead.repCode) ?? raw) : null;
                        return orgName ?? "—";
                      })()}
                    </td>
                    {vertical === "Legacy360" && (
                      <td className="px-5 py-3.5">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C8920A]/10 text-[#C8920A]">
                          {legacy360SubType(lead)}
                        </span>
                      </td>
                    )}
                    <td className="px-5 py-3.5">
                      <StageBadge stage={lead.stage} ghlStageName={(lead as any).ghlStageName} />
                    </td>
                    {vertical === "Scale360" && (
                      <td className="px-5 py-3.5">
                        <ReferralFunnelBadges steps={computeScale360Funnel(lead)} />
                      </td>
                    )}
                    <td className="px-5 py-3.5 capitalize text-[#6E665A]">{lead.plan ?? "—"}</td>
                    <td className="px-5 py-3.5 text-right font-mono font-bold text-[#84ab4a]">{commEst > 0 ? fmt(commEst) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {verticalLeadsFiltered.length === 0 && (
            <div className="py-16 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-[#BCB2A4]" />
              <p className="font-semibold text-[#6E665A]">No {vertical} leads yet</p>
              <p className="text-sm text-[#9C9285] mt-1">Share your referral link to capture leads through your pipeline</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── PIPELINE sub-tab ──
  if (subTab === "pipeline") {
    return (
      <div>
        {vHeaderCard}
        {tabBar}
        <h2 className="text-[17px] font-bold text-[#2C2723] mb-1">{vertical} — Pipeline</h2>
        <p className="text-sm text-gray-900 mb-5">Live pipeline stages synced from Wibiz. Stage updates are managed by your admin.</p>
        <AgentPipelineView vertical={vertical} leads={verticalLeads} color={color} onLeadClick={onLeadClick} repCode={repCode} />
      </div>
    );
  }

  // ── DOCUMENTS sub-tab ──
  if (subTab === "documents") {
    const verticalMaterials = (materialsQuery.data ?? []).filter((m: any) =>
      !m.vertical || m.vertical === vertical || m.vertical?.includes(vertical.split(" ")[0])
    );
    const verticalFolders = (foldersQuery.data ?? []).filter((f: any) =>
      f.vertical === vertical || f.vertical === vertical.split(" ")[0]
    );

    // Group by folderId
    const inFolder: Record<number, any[]> = {};
    const unfolderedItems: any[] = [];
    for (const m of verticalMaterials) {
      const fid = m.folderId;
      if (fid && verticalFolders.find((f: any) => f.id === fid)) {
        if (!inFolder[fid]) inFolder[fid] = [];
        inFolder[fid].push(m);
      } else {
        unfolderedItems.push(m);
      }
    }

    const typeColors: Record<string, string> = { pdf: "#D93025", video: "#1A6FD4", link: "#0FA8A8", template: "#7B4FD4", pptx: "#E8620A" };

    const MODULE_TYPE_LABELS: Record<string, string> = {
      training:   "Training",
      scripts:    "Scripts",
      pricing:    "Pricing",
      onboarding: "Quick Start",
      general:    "Resources",
    };

    // Flat card for unfoldered / fallback items
    const MaterialCard = ({ m }: { m: any }) => {
      const mc = typeColors[m.type] ?? color;
      const isExpanded = expandedMaterials[m.id] ?? false;
      const hasLongDesc = (m.description ?? "").length > 80;
      return (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all group">
          <a href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${mc}15` }}>
              <FileText className="w-5 h-5" style={{ color: mc }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 group-hover:text-[#84ab4a] transition-colors line-clamp-2 text-sm">{m.title}</div>
              <div className={`text-xs text-gray-700 mt-0.5 whitespace-pre-wrap ${isExpanded ? "" : "line-clamp-2"}`}>{(m.description ?? "").replace(/<br\s*\/?>/gi, "\n")}</div>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mt-2 inline-block" style={{ background: `${mc}15`, color: mc }}>{m.type}</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-0.5 transition-colors" />
          </a>
          {hasLongDesc && (
            <button
              onClick={(e) => toggleMaterial(m.id, e)}
              className="w-full flex items-center justify-center gap-1 px-4 pb-2.5 text-[11px] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? (
                <><ChevronUp className="w-3 h-3" /> Show less</>
              ) : (
                <><ChevronDown className="w-3 h-3" /> View more</>
              )}
            </button>
          )}
        </div>
      );
    };

    // If a module is open, delegate to ResourceModulePanel
    if (selectedModule) {
      const moduleMaterials = inFolder[selectedModule.id] ?? [];
      return (
        <div>
          {vHeaderCard}
          {tabBar}
          <ResourceModulePanel
            folder={selectedModule}
            materials={moduleMaterials}
            color={color}
            onBack={() => setSelectedModule(null)}
          />
        </div>
      );
    }

    return (
      <div>
        {vHeaderCard}
        {tabBar}
        <h2 className="text-[17px] font-bold text-[#2C2723] mb-1">{vertical} — Resources</h2>
        <p className="text-sm text-gray-900 mb-5">Training materials, templates, and resources for the {vertical} vertical.</p>

        {verticalMaterials.length === 0 ? (
          <div className="bg-[#FBF7F0] border border-[#EBE2D4] rounded-[18px] p-10 text-center">
            <FolderOpen className="w-10 h-10 text-[#BCB2A4] mx-auto mb-3" />
            <p className="font-semibold text-[#6E665A] mb-1">Resources coming soon</p>
            <p className="text-sm text-[#9C9285]">Your admin will publish {vertical} training materials here.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Module cards grid */}
            {verticalFolders.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {verticalFolders.map((folder: any) => {
                  const count = (inFolder[folder.id] ?? []).length;
                  const typeLabel = MODULE_TYPE_LABELS[folder.moduleType ?? "general"] ?? "Resources";
                  return (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedModule(folder)}
                      className="group text-left bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-gray-200 transition-all"
                    >
                      {/* Cover image or gradient placeholder */}
                      <div
                        className="aspect-video relative overflow-hidden"
                        style={{
                          background: folder.coverImageUrl
                            ? "transparent"
                            : `linear-gradient(135deg, ${color}12, ${color}28)`,
                        }}
                      >
                        {folder.coverImageUrl ? (
                          <img
                            src={folder.coverImageUrl}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <BookOpen className="w-10 h-10 opacity-20" style={{ color }} />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-white/90 text-gray-700 shadow-sm">
                            {typeLabel}
                          </span>
                        </div>
                      </div>
                      {/* Card body */}
                      <div className="p-4">
                        <div className="font-bold text-[#2C2723] text-sm group-hover:opacity-80 transition-opacity line-clamp-1">
                          {folder.name}
                        </div>
                        {/* Fixed 2-line height keeps all card thumbnails the same size */}
                        <div className="mt-1 h-8 overflow-hidden">
                          {folder.description ? (
                            <p className="text-xs text-gray-700 line-clamp-2 whitespace-pre-wrap">{folder.description.replace(/<br\s*\/?>/gi, "\n")}</p>
                          ) : (
                            <p className="text-xs text-gray-300">{count} resource{count !== 1 ? "s" : ""}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-gray-400">{count} item{count !== 1 ? "s" : ""}</span>
                          <span className="text-xs font-semibold transition-colors" style={{ color }}>
                            Open →
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Unfoldered materials (flat cards, existing behavior preserved) */}
            {unfolderedItems.length > 0 && (
              <div>
                {verticalFolders.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-gray-500">Other Resources</span>
                    <span className="text-xs text-gray-300">({unfolderedItems.length})</span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {unfolderedItems.map((m: any) => <MaterialCard key={m.id} m={m} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── REFERRAL LINKS sub-tab ──
  if (subTab === "referrallinks") {
    return (
      <div>
        {vHeaderCard}
        {tabBar}
        {vertical === "Legacy360" && <Legacy360Plan125ReferralLinkNotice repCode={repCode} />}
        <VerticalReferralLinksTab vertical={vertical} repCode={repCode} color={color} />
      </div>
    );
  }

  // ── REPORTING sub-tab ──
  if (subTab === "reporting") {
    return (
      <div>
        {vHeaderCard}
        {tabBar}
        <VerticalReportingView vertical={vertical} leads={verticalLeads} color={color} clientLeads={clientLeads} />
      </div>
    );
  }
}

// ─── Segmented Commissions Vieww ──────────────────────────────────────────────────────────────────────────────

// Commission rates per level for each vertical (mirrors server/ghl.ts COMMISSION_GRID)
const SCALE360_RATES: Record<string, { enrollment: string; subscription: string }> = {
  "Super Agency":     { enrollment: "40%", subscription: "40%" },
  "Super Team":       { enrollment: "40%", subscription: "40%" },
  "Agency":           { enrollment: "30%", subscription: "30%" },
  "Senior Associate": { enrollment: "25%", subscription: "25%" },
  "Associate":        { enrollment: "20%", subscription: "20%" },
};
// Per-program Fundable360 commission display (client request, Hudson 2026-08-29).
// The old shape was a single flat `enrollment` figure per level, which showed
// Elite's numbers for all three programs and had no concept of the financed or
// subscription models. Associates could not tell what a given sale would pay.
//
// Foundation moved to an ongoing subscription: $195/month plus a one-time $100
// setup fee, both commissionable at the associate's normal rate, paid every
// month the subscription stays active (no 6-month cap - it is a subscription,
// not a payment plan). Growth and Elite are deliberately unchanged.
//
// Dollar figures mirror server/ghl.ts: paid-in-full uses COMMISSION_GRID
// flatEnrollmentAmount, financed uses the FUNDABLE360_FINANCING_SCHEDULE totals
// (which sum to the same total as paid-in-full), and Foundation uses
// FUNDABLE360_SUBSCRIPTION_COMMISSION.
//
// Foundation is deliberately NOT pct x price. The client rounded the monthly
// payouts up to $40/$50/$60 for clean accounting (Hudson, 2026-08-31), where a
// strict 20/25/30 percent of $195 would give $39.00/$48.75/$58.50. The pct
// label still shows because that is how associates think about their level, but
// the dollar figure is what actually gets paid.
//
// Keep these in sync with that file - it is the payout authority, this is display.
const FUNDABLE360_RATES: Record<string, {
  pct: string;
  split: string;
  splitNote: string;
  foundationSetup: string;
  foundationMonthly: string;
  growthPaidInFull: string;
  growthFinancedMonthly: string;
  growthFinancedTotal: string;
  elitePaidInFull: string;
  eliteFinancedMonthly: string;
  eliteFinancedTotal: string;
}> = {
  "Agency": {
    pct: "30%", split: "30%", splitNote: "of the funding split paid by lending partners",
    foundationSetup: "$30", foundationMonthly: "$60",
    growthPaidInFull: "$600", growthFinancedMonthly: "$100", growthFinancedTotal: "$600",
    elitePaidInFull: "$900", eliteFinancedMonthly: "$150", eliteFinancedTotal: "$900",
  },
  "Senior Associate": {
    pct: "25%", split: "25%", splitNote: "of the funding split paid by lending partners",
    foundationSetup: "$25", foundationMonthly: "$50",
    growthPaidInFull: "$500", growthFinancedMonthly: "$83.33", growthFinancedTotal: "$500",
    elitePaidInFull: "$750", eliteFinancedMonthly: "$125", eliteFinancedTotal: "$750",
  },
  "Associate": {
    pct: "20%", split: "20%", splitNote: "of the funding split paid by lending partners",
    foundationSetup: "$20", foundationMonthly: "$40",
    growthPaidInFull: "$400", growthFinancedMonthly: "$66", growthFinancedTotal: "$396",
    elitePaidInFull: "$600", eliteFinancedMonthly: "$100", eliteFinancedTotal: "$600",
  },
};
const LEGACY360_LIVING_TRUST_RATES: Record<string, { pct: string; flat: string }> = {
  "Agency":           { pct: "30%", flat: "$900" },
  "Senior Associate": { pct: "25%", flat: "$750" },
  "Associate":        { pct: "20%", flat: "$600" },
};

// Scale360 pays commission on two distinct fees per lead (see COMMISSION_GRID
// in server/ghl.ts: separate enrollmentPct/recurringPct rates) — a "setup" row
// for the one-time enrollment fee and a "monthly" row for the recurring
// subscription, created as two separate commissions rows (server/db.ts). This
// label surfaces that distinction so an associate can see what a Scale360
// commission was actually paid on, instead of one undifferentiated amount.
// Deliberately does NOT relabel "bonus"/"volume_bonus" (override) rows — an
// agency override pools the setup-override and monthly-override amounts into
// ONE combined row at creation time (server/db.ts, overridePool), so it can't
// be cleanly attributed back to a single fee type after the fact.
const SCALE360_FEE_TYPE_LABELS: Record<string, string> = {
  setup: "One-Time Setup Fee Commission",
  monthly: "Monthly Subscription Commission",
};

function VerticalCommissionHistory({ repCode, verticalName }: { repCode: string; verticalName: string }) {
  const q = trpc.commission.list.useQuery({ repCode, verticalName }, { enabled: !!repCode });
  const s = trpc.commission.summary.useQuery({ repCode, verticalName }, { enabled: !!repCode });
  const isScale360 = verticalName === "Scale360";
  // Only Fundable360 has a plan/payment-model breakdown to show. Other
  // verticals would render a permanently empty column, and Scale360 already
  // carries its fee type in the Commission column beside this one.
  const isFundable360 = verticalName === "Fundable360";

  // Fee-type subtotals — computed from the same rows driving the table below,
  // so they always agree with what's shown. Only "setup"/"monthly" rows count
  // toward these two buckets (see SCALE360_FEE_TYPE_LABELS comment above for
  // why override/bonus rows are excluded); they still count toward the
  // Total/Pending/Paid cards from the summary endpoint.
  const scale360FeeTotals = useMemo(() => {
    if (!isScale360 || !q.data) return null;
    let setup = 0, monthly = 0;
    for (const c of q.data as any[]) {
      if (c.type === "setup") setup += Number(c.amount) || 0;
      else if (c.type === "monthly") monthly += Number(c.amount) || 0;
    }
    return { setup, monthly };
  }, [isScale360, q.data]);

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total Earned", value: s.data?.total ?? 0, color: "#84ab4a" },
          { label: "Pending",      value: s.data?.pending ?? 0, color: "#C8920A" },
          { label: "Paid Out",     value: s.data?.paid ?? 0,   color: "#0FA8A8" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className="text-2xl font-bold font-mono mb-0.5" style={{ color }}>{fmtCommission(value)}</div>
            <div className="text-xs font-semibold text-gray-600">{label}</div>
          </div>
        ))}
      </div>
      {/* Scale360-only: what the total above is actually paid on */}
      {isScale360 && scale360FeeTotals && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className="text-xl font-bold font-mono mb-0.5 text-gray-900">{fmtCommission(scale360FeeTotals.setup)}</div>
            <div className="text-xs font-semibold text-gray-600">One-Time Setup Fee Commission</div>
          </div>
          <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className="text-xl font-bold font-mono mb-0.5 text-gray-900">{fmtCommission(scale360FeeTotals.monthly)}</div>
            <div className="text-xs font-semibold text-gray-600">Monthly Subscription Commission</div>
          </div>
        </div>
      )}
      {/* History table */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">{verticalName} Commission History</span>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Client Name</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Business / Description</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Commission</th>
              {isFundable360 && (
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Plan / Details</th>
              )}
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Name</th>
              <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Amount</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Date</th>
              <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Period</th>
              <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map((c: any) => {
              const isOverride = c.notes === "override_commission";
              const isManualCommission = c.isManualCommission === true;
              // Scale360 direct (non-override) rows: show what fee this commission was
              // paid on. Override rows keep the existing generic label — see
              // SCALE360_FEE_TYPE_LABELS comment for why they can't be split by fee type.
              const feeTypeLabel = isScale360 && !isOverride ? SCALE360_FEE_TYPE_LABELS[c.type] : undefined;
              const badgeLabel = isManualCommission
                ? "Manual Commission"
                : (feeTypeLabel ?? (isOverride ? "Override Commission" : "Direct Commission"));

              // Renders one visual row for this commission — the shared markup
              // used both for a normal row and for each itemized override line
              // below. `amount`/`label`/`key` can be overridden so one stored
              // commission row can render as two itemized lines without
              // changing anything about the underlying data.
              const row = (key: string, label: string, amount: number) => (
                <tr key={key} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-5 py-3.5 text-gray-700 font-medium">{c.clientName ?? "-"}</td>
                  <td className="px-5 py-3.5 text-gray-700 font-medium">
                    {isManualCommission ? (
                      <div>
                        <div>{c.businessName ?? "Not tied to a client"}</div>
                        {c.notes && <div className="mt-0.5 text-xs font-normal text-gray-400">{c.notes}</div>}
                      </div>
                    ) : (c.businessName ?? "-")}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isOverride || isManualCommission ? "bg-purple-100 text-purple-700" : "bg-blue-50 text-blue-700"}`}>
                      {label}
                    </span>
                  </td>
                  {/* What this commission was actually paid for. An amount on
                      its own gave the associate no way to tell whether it was an
                      enrollment, a setup fee, or one instalment of six. */}
                  {isFundable360 && (
                  <td className="px-5 py-3.5">
                    {c.planLabel ? (
                      <div>
                        <div className="text-gray-700 font-medium">{c.planLabel}</div>
                        {c.planDetail && <div className="text-xs text-gray-400 mt-0.5">{c.planDetail}</div>}
                      </div>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  )}
                  <td className="px-5 py-3.5 text-gray-700 font-medium">{c.sellerRepName ?? c.repName ?? "—"}</td>
                  <td className="px-5 py-3.5 text-right font-mono font-bold text-gray-900">{fmtCommission(amount)}</td>
                  <td className="px-5 py-3.5 text-gray-500 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                      {c.isManualTrigger && (
                        <span title="Manually triggered by an admin — this date is when the commission was recorded, which may lag the actual transaction date.">
                          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-center text-gray-500 font-mono text-xs">{c.periodMonth ?? "—"}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      c.status === "paid" ? "bg-green-100 text-green-700" :
                      c.status === "approved" ? "bg-blue-100 text-blue-700" :
                      "bg-yellow-100 text-yellow-700"
                    }`}>{c.status}</span>
                  </td>
                </tr>
              );

              // Scale360 override rows: itemize into up to two display-only lines
              // using the read-only feeBreakdown the server computes for display
              // (see getCommissions) — the single stored row, its amount, and its
              // status are completely unchanged; only how it's shown here differs.
              if (isScale360 && isOverride && c.feeBreakdown) {
                const { setup, monthly } = c.feeBreakdown;
                const parts = [
                  setup > 0 ? row(`${c.id}-setup`, "One-Time Setup Fee Commission (Override)", setup) : null,
                  monthly > 0 ? row(`${c.id}-monthly`, "Monthly Subscription Commission (Override)", monthly) : null,
                ].filter(Boolean);
                if (parts.length > 0) return parts;
              }

              return row(c.id, badgeLabel, Number(c.amount));
            })}
          </tbody>
        </table>
        </div>
        {(!q.data || q.data.length === 0) && (
          <div className="py-10 text-center text-gray-400">
            <DollarSign className="w-7 h-7 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No {verticalName} commissions recorded yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SegmentedCommissionsView({
  repCode, agentLevel, agentVerticals, isVerticalAllowed,
}: {
  repCode: string;
  agentLevel: string;
  agentVerticals: string[];
  isVerticalAllowed: (v: string) => boolean;
}) {
  const _commAllQuery = trpc.commission.list.useQuery({ repCode }, { staleTime: 60_000 });
  const _commAll = (_commAllQuery.data ?? []) as any[];

  // Share360 has no standard rate card, but it must appear when an admin has
  // assigned this associate a manual Share360 commission.
  const COMMISSION_VERTICALS = ["Scale360", "Fundable360", "Legacy360"];
  const NO_COMMISSION_VERTICALS = ["Share360", "Share360 Business", "Share360 Nonprofit", "Media360"];
  const tabs = agentVerticals.filter(v => COMMISSION_VERTICALS.includes(v));
  const otherVerticals = agentVerticals.filter(v => !COMMISSION_VERTICALS.includes(v) && !NO_COMMISSION_VERTICALS.includes(v));
  const manualVerticals = _commAll
    .filter((commission) => commission.isManualCommission && commission.verticalName)
    .map((commission) => commission.verticalName as string);
  const allTabs = Array.from(new Set([...tabs, ...otherVerticals, ...manualVerticals]));
  const [activeTab, setActiveTab] = useState<string>(allTabs[0] ?? "Scale360");
  useEffect(() => {
    if (allTabs.length > 0 && !allTabs.includes(activeTab)) setActiveTab(allTabs[0]);
  }, [activeTab, allTabs.join("|")]);

  // Map all agent levels to a rate key — Super Agency/Super Team share Agency rates
  const levelKey = ["Super Agency", "Super Team", "Agency"].includes(agentLevel) ? "Agency"
    : agentLevel === "Senior Associate" ? "Senior Associate"
    : "Associate";
  const scale360Rate = SCALE360_RATES[levelKey];
  const fundable360Rate = FUNDABLE360_RATES[levelKey];
  const legacyRate = LEGACY360_LIVING_TRUST_RATES[levelKey];

  const verticalColor = VERTICAL_CONFIG[activeTab]?.color ?? "#84ab4a";

  // Commission summary cards data
  const _commNow = new Date();
  const _commCurPeriod = `${_commNow.getFullYear()}-${String(_commNow.getMonth() + 1).padStart(2, "0")}`;
  const _commYtdStart  = `${_commNow.getFullYear()}-01`;
  const _commMTD     = _commAll.filter(c => c.periodMonth === _commCurPeriod).reduce((s, c) => s + Number(c.amount), 0);
  const _commYTD     = _commAll.filter(c => c.periodMonth >= _commYtdStart).reduce((s, c) => s + Number(c.amount), 0);
  const _commPending  = _commAll.filter(c => c.status === "pending").reduce((s, c) => s + Number(c.amount), 0);
  const _commPendingCount = _commAll.filter(c => c.status === "pending").length;
  const _prevMoNum  = _commNow.getMonth() === 0 ? 12 : _commNow.getMonth();
  const _prevYrNum  = _commNow.getMonth() === 0 ? _commNow.getFullYear() - 1 : _commNow.getFullYear();
  const _prevPeriod = `${_prevYrNum}-${String(_prevMoNum).padStart(2, "0")}`;
  const _commPrevMo  = _commAll.filter(c => c.periodMonth === _prevPeriod).reduce((s, c) => s + Number(c.amount), 0);
  const _commDelta   = _commMTD - _commPrevMo;

  return (
    <div>
      {/* Summary header cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-7">
        {/* This month — dark card */}
        <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2E2722 0%, #3A2F25 50%, #46342A 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 90% -20%, rgba(226,112,31,0.30) 0%, transparent 60%)" }} />
          <div className="relative">
            <div className="text-[10px] text-white/50 uppercase tracking-widest font-semibold mb-1">Commission · This Month</div>
            <div className="text-3xl font-bold text-white" style={{ fontFamily: "'Spectral', serif" }}>{fmtCommission(_commMTD)}</div>
            {_commDelta !== 0 && (
              <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${_commDelta > 0 ? "text-[#84ab4a]" : "text-red-400"}`}>
                <TrendingUp className="w-3 h-3" />
                {_commDelta > 0 ? "+" : ""}{fmtCommission(Math.abs(_commDelta))} vs last month
              </div>
            )}
          </div>
        </div>
        {/* YTD */}
        <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 shadow-sm">
          <div className="text-[10px] text-[#9C9285] uppercase tracking-widest font-semibold mb-1">Commission · Year to Date</div>
          <div className="text-3xl font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>{fmtCommission(_commYTD)}</div>
          <div className="text-xs text-[#9C9285] mt-1.5">{_commNow.getFullYear()} total earnings</div>
        </div>
        {/* Pending */}
        <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 shadow-sm">
          <div className="text-[10px] text-[#9C9285] uppercase tracking-widest font-semibold mb-1">Pending Payouts</div>
          <div className="text-3xl font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>{fmtCommission(_commPending)}</div>
          <div className="text-xs text-[#9C9285] mt-1.5">{_commPendingCount} payout{_commPendingCount !== 1 ? "s" : ""} in review</div>
        </div>
      </div>

      {/* Vertical tabs */}
      {allTabs.length > 1 && (
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {allTabs.map(v => (
            <button
              key={v}
              onClick={() => setActiveTab(v)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                activeTab === v
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
              }`}
              style={activeTab === v ? { background: VERTICAL_CONFIG[v]?.color ?? "#84ab4a", borderColor: VERTICAL_CONFIG[v]?.color ?? "#84ab4a" } : {}}
            >
              {v}
            </button>
          ))}
        </div>
      )}

      {/* Scale360 view */}
      {activeTab === "Scale360" && isVerticalAllowed("Scale360") && (
        <div className="space-y-5">
          {/* Rate card */}
          <div className="bg-[#0D1E35] border border-[#E8620A]/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full bg-[#E8620A]" />
              <span className="text-xs font-bold text-[#E8620A] uppercase tracking-widest">Scale360 — Commission Structure</span>
            </div>
            <p className="text-xs text-white/40 mb-4">Commissions are paid on enrollment fees and recurring monthly subscriptions generated through your Scale360 referral link.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-[#84ab4a] font-mono">{scale360Rate.enrollment}</div>
                <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wide">Enrollment Fee</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-[#84ab4a] font-mono">{scale360Rate.subscription}</div>
                <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wide">Monthly Subscription</div>
              </div>
            </div>
          </div>
          <VerticalCommissionHistory repCode={repCode} verticalName="Scale360" />
        </div>
      )}

      {/* Fundable360 view */}
      {activeTab === "Fundable360" && isVerticalAllowed("Fundable360") && (
        <div className="space-y-5">
          {/* Rate card with funding split clarification */}
          <div className="bg-[#0D1E35] border border-[#84ab4a]/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full bg-[#84ab4a]" />
              <span className="text-xs font-bold text-[#84ab4a] uppercase tracking-widest">Fundable360 — Commission Structure</span>
            </div>
            {/* How commissions work */}
            <div className="bg-[#84ab4a]/10 border border-[#84ab4a]/25 rounded-xl px-4 py-3 mb-4">
              <div className="text-[10px] font-bold text-[#84ab4a] uppercase tracking-widest mb-1">How Fundable360 Commissions Work</div>
              <p className="text-xs text-white/70 leading-relaxed mb-2">
                You earn <span className="font-semibold text-white">{fundable360Rate.pct}</span> at your level on every Fundable360 program.
                What that pays depends on which program the client enrolls in and how they choose to pay.
              </p>
              <p className="text-xs text-white/70 leading-relaxed">
                <span className="font-semibold text-white">Funding Splits</span> are separate and paid to Fundable360 by lending partners upon a successful loan funding.
                Your commission is a percentage of that funding split, <span className="font-semibold text-white">not the total loan amount</span>.
              </p>
            </div>

            {/* Per-program breakdown */}
            <div className="space-y-3">
              {/* Foundation - subscription */}
              <div className="bg-white/5 rounded-xl p-4 border border-[#84ab4a]/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-white">Fundable360 Foundation</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#84ab4a]/20 text-[#84ab4a] uppercase tracking-wide">Subscription</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Every Month</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.foundationMonthly}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">Every month the client stays subscribed</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">One-Time Setup</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.foundationSetup}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">Paid once, on the $100 setup fee</div>
                  </div>
                </div>
              </div>

              {/* Growth - paid in full or financed */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-white">Fundable360 Growth</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/60 uppercase tracking-wide">Paid in Full or Financed</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Paid in Full</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.growthPaidInFull}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Financed</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.growthFinancedTotal}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{fundable360Rate.growthFinancedMonthly}/mo across 6 payments</div>
                  </div>
                </div>
              </div>

              {/* Elite - paid in full or financed */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-white">Fundable360 Elite</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/60 uppercase tracking-wide">Paid in Full or Financed</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Paid in Full</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.elitePaidInFull}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Financed</div>
                    <div className="text-2xl font-bold text-[#84ab4a] font-mono">{fundable360Rate.eliteFinancedTotal}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{fundable360Rate.eliteFinancedMonthly}/mo across 6 payments</div>
                  </div>
                </div>
              </div>

              {/* Funding split */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Funding Split Commission</div>
                <div className="text-3xl font-bold text-[#84ab4a] font-mono mb-1">{fundable360Rate.split}</div>
                <div className="text-[10px] text-white/40 leading-relaxed">{fundable360Rate.splitNote}</div>
              </div>
            </div>
          </div>
          <VerticalCommissionHistory repCode={repCode} verticalName="Fundable360" />
        </div>
      )}

      {/* Legacy360 view */}
      {activeTab === "Legacy360" && isVerticalAllowed("Legacy360") && (
        <div className="space-y-5">
          {/* Living Trust Program rate card */}
          <div className="bg-[#0D1E35] border border-[#C8920A]/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-[#C8920A]" />
              <span className="text-xs font-bold text-[#C8920A] uppercase tracking-widest">Legacy360 — Living Trust Program</span>
            </div>
            <p className="text-[10px] text-white/35 mb-4">Commission structure below applies exclusively to the Living Trust Program enrollment fee ($2,995). Additional Legacy360 products may have separate structures.</p>
            {/* Rate table — filtered by hierarchy: users only see their own level and below */}
            {(() => {
              const visibleRates = [
                { level: "Agency",           pct: "30%", flat: "$900" },
                { level: "Senior Associate",  pct: "25%", flat: "$750" },
                { level: "Associate",         pct: "20%", flat: "$600" },
              ].filter(({ level }) => {
                if (agentLevel === "Agency") return true;
                if (agentLevel === "Senior Associate") return level === "Senior Associate" || level === "Associate";
                return level === "Associate";
              });
              return (
            <div className="mb-4">
              <div className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Commission Rates by Level</div>
              <div className={`grid gap-2 ${visibleRates.length === 1 ? "grid-cols-1 max-w-xs" : visibleRates.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                {visibleRates.map(({ level, pct, flat }) => (
                  <div
                    key={level}
                    className={`rounded-xl p-3 text-center border ${
                      level === agentLevel || (agentLevel !== "Agency" && agentLevel !== "Senior Associate" && level === "Associate")
                        ? "bg-[#C8920A]/15 border-[#C8920A]/40"
                        : "bg-white/5 border-transparent"
                    }`}
                  >
                    <div className="text-[9px] text-white/40 uppercase tracking-widest mb-1">{level}</div>
                    <div className="text-2xl font-bold text-[#C8920A] font-mono">{pct}</div>
                    <div className="text-sm font-bold text-white/80 font-mono mt-0.5">{flat}</div>
                    <div className="text-[9px] text-white/30 mt-0.5">per enrollment</div>
                  </div>
                ))}
              </div>
            </div>
              );
            })()}
            {/* Your rate highlight */}
            <div className="bg-[#C8920A]/10 border border-[#C8920A]/25 rounded-xl px-4 py-3">
              <div className="text-[10px] text-[#C8920A] font-bold uppercase tracking-widest mb-1">Your Rate ({agentLevel})</div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#C8920A] font-mono">{legacyRate.pct}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">of Enrollment</div>
                </div>
                <div className="text-white/30 text-xl">=</div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white font-mono">{legacyRate.flat}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">per Living Trust</div>
                </div>
              </div>
            </div>
          </div>
          <VerticalCommissionHistory repCode={repCode} verticalName="Legacy360" />
        </div>
      )}

      {/* Other verticals (Share360, Media360, etc.) */}
      {!(["Scale360", "Fundable360", "Legacy360"].includes(activeTab)) && (
        <div className="space-y-5">
          <div className="bg-[#0D1E35] border rounded-2xl p-5" style={{ borderColor: `${verticalColor}40` }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: verticalColor }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: verticalColor }}>{activeTab} — Commission Structure</span>
            </div>
            <p className="text-xs text-white/40">Commission details for {activeTab} will be displayed here once configured.</p>
          </div>
          <VerticalCommissionHistory repCode={repCode} verticalName={activeTab} />
        </div>
      )}
    </div>
  );
}

// ─── BC360 Assistant Chat Panel ──────────────────────────────────────────────
const QUICK_QUERIES = [
  { label: "Show my leads",       query: "show me my leads" },
  { label: "My appointments",     query: "my upcoming appointments" },
  { label: "My commissions",      query: "what are my commissions" },
  { label: "This week",           query: "what's happening this week" },
  { label: "Share360 training",   query: "training for Share360" },
];

/** Grace tool renderer — shows a friendly fetching pill while the tool runs,
 *  hides raw JSON output entirely (Grace's text response covers it). */
function graceToolRenderer({ toolName, state, errorText }: ToolPartRendererProps) {
  const TOOL_LABELS: Record<string, string> = {
    get_my_leads:         "Fetching your leads…",
    get_my_commissions:   "Checking your commissions…",
    get_my_appointments:  "Looking up your appointments…",
    get_upcoming_events:  "Checking the calendar…",
    search_knowledge:     "Searching knowledge base…",
    get_my_certifications:"Checking your certifications…",
  };

  if (state === "input-streaming" || state === "input-available") {
    return (
      <div className="flex items-center gap-2 my-1.5 text-xs text-[#84ab4a]">
        <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
        <span>{TOOL_LABELS[toolName] ?? "Fetching data…"}</span>
      </div>
    );
  }
  if (state === "output-error") {
    return (
      <div className="my-1 text-xs text-red-500">{errorText ?? "Tool error"}</div>
    );
  }
  // output-available: return empty fragment — Grace's text covers the data
  return <></>;
}

// Generates a stable UUID for this browser tab session (survives re-renders, clears on tab close)
function useSessionId(repCode: string): string {
  return useMemo(() => {
    const key = `grace_session_${repCode}`;
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(key, id);
    }
    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repCode]);
}

function GraceChatHistoryPanel({
  repCode,
  onClose,
}: {
  repCode: string;
  onClose: () => void;
}) {
  const sessionsQuery = trpc.grace.getChatSessions.useQuery({ repCode, limit: 10 });
  const deleteMutation = trpc.grace.deleteChatSession.useMutation({
    onSuccess: () => sessionsQuery.refetch(),
    onError: () => toast.error("Failed to delete conversation"),
  });

  const sessions = sessionsQuery.data ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
        <History className="w-4 h-4 text-[#84ab4a]" />
        <span className="text-sm font-semibold text-gray-800">Past Conversations</span>
        <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600 p-1 rounded">
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 px-3 py-2 space-y-3">
        {sessionsQuery.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}
        {!sessionsQuery.isLoading && sessions.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-8">No saved conversations yet.</p>
        )}
        {sessions.map(session => (
          <div key={session.sessionId} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-gray-400">
                {new Date(session.startedAt).toLocaleDateString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                  hour: "numeric", minute: "2-digit",
                })}
              </span>
              <button
                onClick={() => deleteMutation.mutate({ repCode, sessionId: session.sessionId })}
                className="text-gray-300 hover:text-red-400 transition-colors p-0.5 rounded"
                title="Delete this conversation"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {session.messages.map((msg, i) => (
                <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg ${
                  msg.role === "user"
                    ? "bg-[#84ab4a] text-white ml-4"
                    : "bg-white text-gray-700 mr-4 border border-gray-100"
                }`}>
                  {msg.content.length > 180 ? msg.content.slice(0, 180) + "…" : msg.content}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssistantChatPanel({ repCode }: { repCode: string }) {
  const chatId = `grace-${repCode}`;
  const sessionId = useSessionId(repCode);
  const [showHistory, setShowHistory] = useState(false);
  const saveMutation = trpc.grace.saveChatMessages.useMutation();
  // Thumbs up/down is reviewer-only for now (react-query dedupes this against
  // any identical rep.getByCode query already running elsewhere on the page).
  const reviewerQuery = trpc.rep.getByCode.useQuery({ repCode }, { enabled: !!repCode });
  const isQaReviewer = !!(reviewerQuery.data as any)?.isQaReviewer;
  // Reuses the existing Voice QA flag queue (Admin → Voice Command Center →
  // Training/QA) — flags without a twilioCallSid simply render without an
  // audio pane there. No schema change needed for Dawn chat feedback.
  const rateMutation = trpc.voiceQa.rate.useMutation({
    onError: () => toast.error("Couldn't send feedback — please try again."),
  });
  const handleFeedback = useCallback((params: { userMessage: string; assistantMessage: string; rating: "up" | "down" }) => {
    rateMutation.mutate({
      conversationId: sessionId,
      agentLabel: "Dawn",
      question: params.userMessage.slice(0, 2000),
      actualResponse: params.assistantMessage.slice(0, 2000),
      rating: params.rating,
    });
    if (params.rating === "down") {
      toast.success("Thanks — this response has been flagged for admin review.");
    }
  }, [rateMutation, sessionId, repCode]);
  // Stable empty array — must not be an inline [] literal or it creates a new reference on
  // every parent render, triggering the AIChatBox sync-effect and wiping the thread.
  const stableInitialMessages = useMemo(() => [], []);

  const handleFinish = useCallback((messages: any[]) => {
    const toSave = messages
      .filter(m => m.role === "user" || m.role === "assistant")
      .map(m => ({
        role: m.role as "user" | "assistant",
        // Extract plain text from parts array (AI SDK UIMessage format)
        content: (m.parts ?? [])
          .filter((p: any) => p.type === "text")
          .map((p: any) => p.text)
          .join("") || (typeof m.content === "string" ? m.content : ""),
      }))
      .filter(m => m.content.trim().length > 0);

    if (toSave.length > 0) {
      saveMutation.mutate({ repCode, sessionId, messages: toSave });
    }
  }, [repCode, sessionId, saveMutation]);

  const DAWN_SUGGESTIONS = [
    "Summarize my pipeline for this week",
    "Which leads should I follow up with today?",
    "What are my commissions this month?",
    "How am I tracking against my commission goal?",
    "What events are coming up in BizConnect360?",
  ];

  return (
    <div className="flex gap-5 items-start" style={{ height: "calc(100vh - 160px)" }}>
      {/* Main chat panel */}
      <div className="flex-1 min-w-0 bg-white border border-[#EBE2D4] rounded-2xl shadow-sm overflow-hidden flex flex-col h-full">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-[#F0E8DE] flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-[#E2701F] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#2C2723]">Dawn</div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#5F8A2E]" />
              <div className="text-[10px] text-[#9C9285]">Your BizConnect360 AI assistant</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowHistory(h => !h)}
              className={`flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border transition-colors ${showHistory ? "bg-[#FBEEE1] border-[#E2701F]/30 text-[#C75E12]" : "bg-white border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3]"}`}
              title="View past conversations"
            >
              <History className="w-3 h-3" /> History
            </button>
          </div>
        </div>

        {showHistory && (
          <div className="border-b border-[#F0E8DE] flex-shrink-0 max-h-72 overflow-hidden">
            <GraceChatHistoryPanel repCode={repCode} onClose={() => setShowHistory(false)} />
          </div>
        )}

        <AIChatBox
          api="/api/chat"
          chatId={chatId}
          initialMessages={stableInitialMessages}
          extraBody={{ repCode, userTz: Intl.DateTimeFormat().resolvedOptions().timeZone }}
          repCode={repCode}
          voiceAgent="dawn"
          renderToolPart={graceToolRenderer}
          placeholder="Ask Dawn anything…"
          emptyStateMessage="Hi! I'm Dawn. Ask me about your pipeline, commissions, upcoming events, or how BizConnect360 works."
          suggestedPrompts={QUICK_QUERIES.map(q => q.query)}
          className="border-none rounded-none shadow-none"
          onFinish={handleFinish}
          onFeedback={isQaReviewer ? handleFeedback : undefined}
        />
      </div>

      {/* Right: Try asking */}
      <div className="flex-shrink-0 hidden lg:flex flex-col gap-4" style={{ width: "272px" }}>
        <div className="bg-white border border-[#EBE2D4] rounded-2xl shadow-sm p-5">
          <div className="text-base font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>Try asking</div>
          <div className="space-y-2">
            {DAWN_SUGGESTIONS.map(s => (
              <button
                key={s}
                className="w-full text-left text-sm text-[#6E665A] px-3 py-2.5 rounded-xl border border-[#EBE2D4] hover:bg-[#FBEEE1] hover:border-[#E2701F]/30 hover:text-[#C75E12] transition-all flex items-center gap-2 group"
                onClick={() => {
                  // Focus the chat input with the suggestion pre-filled by clicking
                  const chatInput = document.querySelector<HTMLInputElement>(`input[placeholder="Ask Dawn anything…"], textarea[placeholder="Ask Dawn anything…"]`);
                  if (chatInput) { chatInput.value = s; chatInput.focus(); chatInput.dispatchEvent(new Event("input", { bubbles: true })); }
                }}
              >
                <Sparkles className="w-3 h-3 flex-shrink-0 text-[#E2701F] opacity-60 group-hover:opacity-100" />
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Rep Dashboard ──────────────────────────────────────────────────────────────────────────────
const REP_CODE_STORAGE_KEY = "bc360_agent_rep_code";

export default function RepDashboard() {
  const params = useParams<{ repCode?: string }>();
  const [repCode, setRepCodeState] = useState<string | null>(() => {
    if (params.repCode) return params.repCode;
    try { return localStorage.getItem(REP_CODE_STORAGE_KEY); } catch { return null; }
  });
  const setRepCode = (code: string | null) => {
    setRepCodeState(code);
    try { if (code) localStorage.setItem(REP_CODE_STORAGE_KEY, code); else localStorage.removeItem(REP_CODE_STORAGE_KEY); } catch {}
  };

  // Nav state: top-level section + per-vertical sub-tab
  type TopSection = "overview" | "allleads" | "commissions" | "certifications" | "appointments" | "vertical" | "account" | "myteam" | "referrallinks" | "assistant";
  const [activeSection, setActiveSection] = useState<TopSection>("overview");
  const [activeVertical, setActiveVertical] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<"clients" | "leads" | "pipeline" | "documents" | "reporting" | "referrallinks">("documents");
  const [announcementsExpanded, setAnnouncementsExpanded] = useState(true);
  const [expandedVerticals, setExpandedVerticals] = useState<Record<string, boolean>>({});
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [showFloatingDawn, setShowFloatingDawn] = useState(false);
  const [leadsSearch, setLeadsSearch] = useState("");
  const [leadsFilterVertical, setLeadsFilterVertical] = useState<string>("all");
  const [selectedLeadsPageId, setSelectedLeadsPageId] = useState<number | null>(null);
  const floatingDawnInitialMessages = useMemo(() => [], []);

  const repQuery = trpc.rep.getByCode.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const leadsQuery = trpc.lead.list.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const statsQuery = trpc.lead.pipelineStats.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const commissionsQuery = trpc.commission.list.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const commSummaryQuery = trpc.commission.summary.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const materialsQuery = trpc.marketing.list.useQuery({ repCode: repCode ?? undefined });
  const foldersQuery = trpc.marketing.listFolders.useQuery({ repCode: repCode ?? undefined });
  const certifiedVerticalsQuery = trpc.certification.getCertifiedVerticals.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const heroMessageQuery = (trpc as any).appSettings.get.useQuery({ key: "hero_message" });
  const inviteLinkQuery = trpc.agentSelfReg.getInviteLink.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const downlineQuery = trpc.agentSelfReg.getDownline.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const teamVisibilityQuery = trpc.agentSelfReg.getTeamVisibility.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const upgradeRequestsQuery = trpc.repUpgrade.getMyRequests.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const agreementQuery = trpc.agreement.hasAccepted.useQuery({ repCode: repCode ?? "" }, { enabled: !!repCode });
  const acceptAgreementMutation = trpc.agreement.accept.useMutation({
    onSuccess: () => agreementQuery.refetch(),
    onError: (e: any) => toast.error(e.message),
  });
  // The "I Agree" checkbox was removed at the client's request on 2026-09-15.
  // Drawing a signature replaces it as the act of assent.
  const [agreementPrintedName, setAgreementPrintedName] = useState("");
  const [agreementNameError, setAgreementNameError] = useState<string | null>(null);
  const [agreementSignature, setAgreementSignature] = useState<string | null>(null);
  const [agreementSignatureSource, setAgreementSignatureSource] = useState<SignatureSource>("drawn");
  const [agreementSignatureError, setAgreementSignatureError] = useState<string | null>(null);
  const utils = trpc.useUtils();
  // Welcome modal: must be before early returns (Rules of Hooks)
  const welcomeVideoRef = useRef<HTMLVideoElement>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  // Check per-rep welcome key after repCode is known
  useEffect(() => {
    if (!repCode) return;
    const key = `bc360_welcome_seen_${repCode}`;
    try { if (!localStorage.getItem(key)) setShowWelcome(true); } catch {}
  }, [repCode]);
  // Ensure video restarts from the beginning each time the modal opens
  useEffect(() => {
    if (!showWelcome || !welcomeVideoRef.current) return;
    welcomeVideoRef.current.currentTime = 0;
  }, [showWelcome]);
  // Listen for tab-bar navigation events fired by VerticalBucketContent — MUST be before early returns
  useEffect(() => {
    const handler = (e: Event) => {
      const { key } = (e as CustomEvent).detail;
      setActiveSubTab(key);
    };
    window.addEventListener("bc360-subtab", handler);
    return () => window.removeEventListener("bc360-subtab", handler);
  }, []);
  const dismissWelcome = () => {
    if (repCode) {
      try { localStorage.setItem(`bc360_welcome_seen_${repCode}`, "1"); } catch {}
    }
    setShowWelcome(false);
  };

  // MUST be here (before any early returns) to satisfy Rules of Hooks
  const upcomingBookingsQuery = trpc.trainingBookings.getUpcoming.useQuery(
    { repCode: repCode ?? "" },
    { enabled: !!repCode }
  );
  // Share360 enrollment status — used for sidebar badge + overview banner
  const share360StatusQuery = trpc.share360.getStatus.useQuery(
    { repId: repQuery.data?.id ?? 0 },
    { enabled: !!repCode && !!repQuery.data?.id, refetchInterval: 30_000 }
  );
  const share360FeeStatusQuery = trpc.share360.getMonthlyFeeStatus.useQuery(
    { repId: repQuery.data?.id ?? 0, repCode: repCode ?? "" },
    { enabled: !!repCode && !!repQuery.data?.id, staleTime: 60_000, refetchInterval: 120_000 }
  );
  // Overview hero data — must be declared before early returns (Rules of Hooks)
  const appointmentsOverviewQuery = trpc.ghlAppointments.getForAgentLeads.useQuery(
    { repCode: repCode ?? "" },
    { enabled: !!repCode, staleTime: 60_000, refetchInterval: 120_000 }
  );
  const announcementsOverviewQuery = trpc.announcements.list.useQuery();

  if (!repCode) return <RepLogin onLogin={setRepCode} />;

  const rep = repQuery.data;
  const stats = statsQuery.data;
  const commSummary = commSummaryQuery.data;

  if (repQuery.isLoading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-white/40 text-sm">Loading...</div>
      </div>
    );
  }

  if (!rep) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Rep not found</p>
          <button onClick={() => setRepCode(null)} className="text-[#84ab4a] text-sm hover:underline">Try again</button>
        </div>
      </div>
    );
  }

  // ── Access Denied Gate ──────────────────────────────────────────────────────
  // FAILS CLOSED: renders nothing until self/admin access is POSITIVELY
  // confirmed, rather than rendering the dashboard by default and hiding it
  // only once some other query is caught erroring. The previous version of
  // this gate inferred denial from commSummaryQuery/downlineQuery/
  // agreementQuery's error state — three separate, independently-retrying
  // React Query requests. That's a "shows dashboard unless proven denied"
  // design: on first paint, or during any of those queries' automatic
  // retry/refetch cycles, `.error` can be transiently unset even for a
  // genuinely denied caller, which visibly happened in production (2026-07-21
  // — a different associate's dashboard briefly showed "Access denied" then
  // flipped to rendering the target associate's real name/dashboard chrome a
  // few seconds later, before any of those side-queries had actually
  // succeeded). A dashboard is not a "guilty until proven innocent" surface.
  //
  // Fixed by keying authorization off a single, deterministic signal from
  // the SAME query that already gates `!rep` above: rep.getByCode
  // (server/routers.ts) returns the FULL record — which always includes the
  // `username` key, even when its value is null for a rep pending
  // credentials — only to the caller themselves or an admin; every other
  // caller gets a redacted subset that never contains that key at all (see
  // the redaction shape in rep.getByCode). Checking key PRESENCE (not
  // truthiness) means this is correct even for a legitimately self-viewing
  // rep who has no credentials yet. This can't flicker with query retries —
  // repQuery has already settled by the time `!rep` above passed.
  if (!isFullRepRecord(rep)) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <p className="text-red-400 mb-2 font-semibold">Access denied</p>
          <p className="text-white/50 text-sm mb-4">
            You don't have permission to view this associate dashboard.
          </p>
          <button onClick={() => setRepCode(null)} className="text-[#84ab4a] text-sm hover:underline">
            Go to my dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Agreement Gate ──────────────────────────────────────────────────────────
  if (!agreementQuery.isLoading && !agreementQuery.data?.accepted) {

    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-[#84ab4a]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Master Agent Participation Agreement</h1>
            <p className="text-white/50 text-sm">Please read and sign before accessing your dashboard</p>
          </div>
          <div className="bg-[#0D1E35] border border-white/10 rounded-2xl p-6 shadow-xl space-y-4">
            <AgreementRevisionNotice theme="dark" />

            {/* The approved PDF itself, so what is read is exactly what is
                signed. This supersedes any agreement signed previously, which
                is why associates who accepted the old one are asked again. */}
            <AgreementViewer theme="dark" />

            <AgreementSignature
              theme="dark"
              printedName={agreementPrintedName}
              onPrintedNameChange={v => { setAgreementPrintedName(v); setAgreementNameError(null); }}
              signatureDataUrl={agreementSignature}
              onSignatureChange={(v, src) => { setAgreementSignature(v); setAgreementSignatureSource(src); setAgreementSignatureError(null); }}
              nameError={agreementNameError}
              signatureError={agreementSignatureError}
              disabled={acceptAgreementMutation.isPending}
            />

            <Button
              disabled={!canSubmitSignature(agreementPrintedName, agreementSignature) || acceptAgreementMutation.isPending}
              onClick={() => {
                if (!isValidSignatureDataUrl(agreementSignature)) {
                  setAgreementSignatureError("Please add your signature to sign the Agreement.");
                  return;
                }
                if (!isValidPrintedName(agreementPrintedName)) {
                  setAgreementNameError("Please print your full legal name to sign.");
                  return;
                }
                acceptAgreementMutation.mutate({
                  repCode: rep.repCode,
                  printedName: agreementPrintedName.trim(),
                  signatureDataUrl: agreementSignature!,
                  signatureSource: agreementSignatureSource,
                });
              }}
              className="w-full bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-11 disabled:opacity-40"
            >
              {acceptAgreementMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Sign & Continue to Dashboard"
              )}
            </Button>
            <button onClick={() => setRepCode(null)} className="w-full text-center text-xs text-white/30 hover:text-white/50 transition-colors py-1">
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Parse allowed verticals
  let repAllowedVerticals: string[] | null = null;
  try { repAllowedVerticals = (rep as any).allowedVerticals ? JSON.parse((rep as any).allowedVerticals as string) : null; } catch { repAllowedVerticals = null; }
  // "Share360" in allowedVerticals covers both "Share360 Business" and "Share360 Nonprofit"
  const isVerticalAllowed = (v: string) => {
    if (v === "BizConnect360") return true; // company-wide, always visible
    if (repAllowedVerticals === null) return true;
    if (repAllowedVerticals.includes(v)) return true;
    if ((v === "Share360 Business" || v === "Share360 Nonprofit") && repAllowedVerticals.includes("Share360")) return true;
    return false;
  };

  const certifiedVerticals: string[] = certifiedVerticalsQuery.data ?? [];
  const share360EnrollmentStatus = share360StatusQuery.data?.share360EnrollmentStatus ?? "not_started";
  const share360Certified = certifiedVerticals.includes("Share360");
  // Show enrollment prompt for any Share360 agent who is not yet approved (cert no longer required first)
  const hasShare360Vertical = isVerticalAllowed("Share360 Business") || isVerticalAllowed("Share360 Nonprofit") || isVerticalAllowed("Share360");
  const share360EnrollmentPending = share360EnrollmentStatus !== "approved" && hasShare360Vertical;
  // Block access when the monthly fee is overdue and not waived by merchant count.
  // Only applies to fully-approved agents — enrollees in progress use the enrollment gate instead.
  const share360FeeBlocked =
    share360EnrollmentStatus === "approved" &&
    !!share360FeeStatusQuery.data &&
    !share360FeeStatusQuery.data.waived &&
    !share360FeeStatusQuery.data.paidUp;

  // Dead code removed: an unused paymentUrl for the $49.95 ONE-TIME
  // application fee (payment-link/69b6d407...) was built here and never
  // rendered. That link is harmless on its own, but leaving an unused
  // payment-link URL in this component invites someone to wire it to a button
  // without checking which of the two links it is.
  //
  // The dangerous one is the $24.95/month RECURRING link
  // (payment-link/69ccacca...). It does not update the card on an existing
  // subscription, it creates a second one on the same Stripe customer, which
  // is what caused the duplicate-charge reports of 2026-08-28. It is gone from
  // this file entirely and must not come back.
  //
  // Payment-method changes go through the Stripe Customer Portal via
  // share360.getBillingPortalUrl, which edits the existing subscription
  // in place.

  // "Share360" cert covers both sub-verticals.
  // DB may store "Share360", "Share360 Business", or "Share360 Nonprofit" — accept any.
  const isVerticalCertified = (v: string) => {
    if (!VERTICAL_CONFIG[v]?.certRequired) return true;
    if (certifiedVerticalsQuery.isLoading) return true; // don't flash "Cert required" while loading
    if (certifiedVerticals.includes(v)) return true;
    // Share360 variants: match any Share360 cert record regardless of how it was stored
    if (v.startsWith("Share360") && certifiedVerticals.some(c => c.startsWith("Share360"))) return true;
    return false;
  };

  // Verticals this agent can access — BizConnect360 is always shown (Resources tab only)
  const agentVerticals = ALL_VERTICALS.filter(v => isVerticalAllowed(v));

  const navigateToVertical = (v: string, sub: "clients" | "leads" | "pipeline" | "documents" | "reporting" | "referrallinks" = "documents") => {
    setActiveSection("vertical");
    setActiveVertical(v);
    setActiveSubTab(sub);
    setExpandedVerticals(prev => ({ ...prev, [v]: true }));
    setMobileSidebarOpen(false);
  };

  const toggleVertical = (v: string) => {
    setExpandedVerticals(prev => ({ ...prev, [v]: true }));
    setActiveSection("vertical");
    setActiveVertical(v);
    setActiveSubTab("documents");
    setMobileSidebarOpen(false);
  };

  const agentLevel = (rep as any).agentLevel ?? "Associate";

  const SUB_TABS: { key: "clients" | "leads" | "pipeline" | "documents" | "reporting" | "referrallinks"; label: string; icon: any }[] = [
    { key: "clients", label: "Clients", icon: Briefcase },
    { key: "leads", label: "Leads", icon: Users },
    { key: "pipeline", label: "Pipeline", icon: Activity },
    { key: "referrallinks", label: "Referral Links", icon: Link },
    { key: "documents", label: "Resources", icon: FolderOpen },
    { key: "reporting", label: "Reporting", icon: PieChart },
  ];

  // ── Overview computations ─────────────────────────────────────────────────
  const _now = new Date();
  const _currentPeriod = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;
  const _prevMonthNum  = _now.getMonth() === 0 ? 12 : _now.getMonth();
  const _prevYearNum   = _now.getMonth() === 0 ? _now.getFullYear() - 1 : _now.getFullYear();
  const _prevPeriod    = `${_prevYearNum}-${String(_prevMonthNum).padStart(2, "0")}`;
  const _allComms      = (commissionsQuery.data ?? []) as any[];
  const commissionMTD  = _allComms.filter(c => c.periodMonth === _currentPeriod).reduce((s, c) => s + Number(c.amount), 0);
  const _commPrevMo    = _allComms.filter(c => c.periodMonth === _prevPeriod).reduce((s, c) => s + Number(c.amount), 0);
  const commissionDelta = commissionMTD - _commPrevMo;
  const _allLeads = (leadsQuery.data ?? []) as LeadRow[];
  const _monthStart = new Date(_now.getFullYear(), _now.getMonth(), 1).getTime();
  const _qtrStart   = new Date(_now.getFullYear(), Math.floor(_now.getMonth() / 3) * 3, 1).getTime();
  const leadsThisMonth       = _allLeads.filter(l => new Date((l as any).createdAt).getTime() >= _monthStart).length;
  const newActiveThisQuarter = _allLeads.filter(l => l.stage === "active_client" && (l as any).activatedAt && new Date((l as any).activatedAt).getTime() >= _qtrStart).length;
  const greetingHour = _now.getHours();
  const greetingTime = greetingHour < 12 ? "morning" : greetingHour < 17 ? "afternoon" : "evening";
  const greetingDay  = _now.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
  const greetingDate = `${_now.toLocaleDateString("en-US", { month: "long" }).toUpperCase()} ${_now.getDate()}, ${_now.getFullYear()}`;
  const _overviewAppts = (appointmentsOverviewQuery.data ?? []) as any[];
  const upcomingOverviewAppts = _overviewAppts
    .filter(b => new Date(b.startTime).getTime() > _now.getTime() && b.status !== "cancelled")
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  const _announcementsAll = (announcementsOverviewQuery.data ?? []) as any[];
  const _activeAnnCount   = _announcementsAll.filter((a: any) => a.isActive !== false).length;
  const _vertStrengths = agentVerticals.filter(v => v !== "BizConnect360").map(v => {
    const vL = _allLeads.filter(l => {
      const r = (l as any).sh360Role;
      if ((l.verticalName ?? "").startsWith("Share360") || r === "nonprofit" || r === "business") {
        if (v === "Share360 Nonprofit") return r === "nonprofit" || l.verticalName === "Share360 Nonprofit";
        if (v === "Share360 Business")  return r === "business"  || (r == null && l.verticalName === "Share360 Business");
        return false;
      }
      return l.verticalName === v;
    });
    return { v, clients: vL.filter(l => l.stage === "active_client").length };
  }).filter(x => x.clients > 0).sort((a, b) => b.clients - a.clients);
  const _topVertical = _vertStrengths[0];
  const _heroLines: string[] = [];
  if (upcomingOverviewAppts.length > 0)
    _heroLines.push(`You have ${upcomingOverviewAppts.length} upcoming client appointment${upcomingOverviewAppts.length !== 1 ? "s" : ""} this week.`);
  if (_activeAnnCount > 0)
    _heroLines.push(`${_activeAnnCount} announcement${_activeAnnCount !== 1 ? "s" : ""} from BC360 await${_activeAnnCount === 1 ? "s" : ""} your review.`);
  if (_topVertical)
    _heroLines.push(`Your ${_topVertical.v} pipeline is your strongest with ${_topVertical.clients} active client${_topVertical.clients !== 1 ? "s" : ""}.`);
  if (_heroLines.length === 0)
    _heroLines.push("Your dashboard is ready. Check your verticals and stay on top of your pipeline.");
  const heroSummary = _heroLines.join(" ");

  return (
    <div className="flex min-h-screen bg-[#F6F1E9]">
      {/* ── Mobile sidebar overlay ── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* ── First-login Welcome Modal (Associates only) ── */}
      {showWelcome && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Welcome to BizConnect360!</h2>
              <p className="text-sm text-gray-500 mt-1">Watch this short intro to get started.</p>
            </div>
            <div className="bg-black aspect-video">
              <video
                ref={welcomeVideoRef}
                src="https://assets.cdn.filesafe.space/4NIo95IzKUVayysfr0PX/media/6a150e190397b3655e6b7190.mp4"
                controls
                autoPlay
                muted
                playsInline
                className="w-full h-full"
                onEnded={dismissWelcome}
              />
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                onClick={dismissWelcome}
                className="px-5 py-2.5 bg-[#84ab4a] hover:bg-[#6e9038] text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Got it, let's go!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar ── */}
      <aside className={`w-60 bg-white border-r border-[#EBE2D4] flex flex-col fixed inset-y-0 left-0 z-40 overflow-y-auto transition-transform duration-200 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        {/* Logo */}
        <div className="p-4 border-b border-[#EBE2D4] flex-shrink-0">
          <div className="flex items-center justify-between gap-2.5">
            <div>
              <img src="/associates/bizconnect360-logo.png" alt="BizConnect360" className="h-8 object-contain" />
              <div className="text-[9px] font-bold text-[#BCB2A4] uppercase tracking-widest mt-1.5">Agent Workspace</div>
            </div>
            <button
              className="lg:hidden text-[#9C9285] hover:text-[#2C2723] transition-colors p-1"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Agent card */}
        <div className="mx-3.5 mt-3 mb-1 rounded-[13px] p-3.5 flex-shrink-0" style={{ background: "linear-gradient(135deg,#FFF8EF,#FBF1E4)", border: "1px solid #F0E3CF" }}>
          <div className="flex items-center gap-3">
            {(rep as any).profilePictureUrl ? (
              <img src={(rep as any).profilePictureUrl} alt={rep.firstName} className="w-[42px] h-[42px] rounded-xl object-cover flex-shrink-0 border border-[#EBE2D4]" />
            ) : (
              <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center text-base font-bold text-white flex-shrink-0" style={{ fontFamily: "'Spectral',serif", background: "linear-gradient(150deg,#E2701F,#CF5247)", boxShadow: "0 4px 10px rgba(226,112,31,.28)" }}>
                {rep.firstName[0]}{rep.lastName?.[0] ?? ""}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-bold text-[#2C2723] truncate leading-snug">{rep.firstName} {rep.lastName}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7FA63E] flex-shrink-0" style={{ boxShadow: "0 0 0 3px #EFF4E2" }} />
                <span className="text-[11px] text-[#6E665A]"><span className="text-[#E2701F] font-semibold">{agentLevel}</span></span>
              </div>
            </div>
          </div>
          {/* Level badge — Associate/Senior Associate/Agency only (per client,
              2026-09-01). Super Agency/Super Team show no badge; no dedicated
              artwork exists for those levels yet. */}
          {AGENT_LEVEL_BADGE_SRC[agentLevel ?? ""] && (
            <img
              src={AGENT_LEVEL_BADGE_SRC[agentLevel ?? ""]}
              alt={`${agentLevel} badge`}
              className="w-full max-w-[140px] mx-auto mt-3 block"
            />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 mt-2">
          {/* MAIN */}
          <div className="pb-1">
            <div className="text-[9px] font-bold text-[#BCB2A4] uppercase tracking-widest px-3 mb-1">Main</div>
          </div>
          <button
            onClick={() => { setActiveSection("overview"); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all ${activeSection === "overview" ? "bg-[#FBEEE1] text-[#C75E12] font-semibold" : "text-[#6E665A] hover:text-[#2C2723] hover:bg-[#F4EEE3]"}`}
          >
            <Home className="w-4 h-4 flex-shrink-0" /> Dashboard
          </button>
          <button
            onClick={() => { setActiveSection("allleads"); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all ${activeSection === "allleads" ? "bg-[#FBEEE1] text-[#C75E12] font-semibold" : "text-[#6E665A] hover:text-[#2C2723] hover:bg-[#F4EEE3]"}`}
          >
            <Users className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 text-left">My Leads</span>
            {(leadsQuery.data ?? []).length > 0 && (
              <span className="text-[11px] font-bold bg-[#F4EEE3] text-[#6E665A] px-2 py-0.5 rounded-full">{(leadsQuery.data ?? []).length}</span>
            )}
          </button>
          <button
            onClick={() => { setActiveSection("appointments"); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all ${activeSection === "appointments" ? "bg-[#FBEEE1] text-[#C75E12] font-semibold" : "text-[#6E665A] hover:text-[#2C2723] hover:bg-[#F4EEE3]"}`}
          >
            <Calendar className="w-4 h-4 flex-shrink-0" /> Calendar
          </button>

          {/* MY VERTICALS */}
          <div className="pt-2 pb-1">
            <div className="text-[9px] font-bold text-[#BCB2A4] uppercase tracking-widest px-3 mb-1">My Verticals</div>
          </div>
          {agentVerticals.map(v => {
            const cfg = VERTICAL_CONFIG[v];
            const color = cfg?.color ?? "#84ab4a";
            const isExpanded = expandedVerticals[v];
            const isActive = activeSection === "vertical" && activeVertical === v;
            const isCert = isVerticalCertified(v);
            return (
              <div key={v}>
                <button
                  onClick={() => toggleVertical(v)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all ${isActive ? "bg-[#FBEEE1] text-[#C75E12] font-semibold" : "text-[#6E665A] hover:text-[#2C2723] hover:bg-[#F4EEE3]"}`}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: (isCert || v.startsWith("Share360")) ? color : "#6B7280" }} />
                  <span className="flex-1 text-left truncate text-xs">{v}</span>
                  {/* Cert status lives on the Dashboard vertical cards and Certifications page — no badge needed in the sidebar */}
                  {cfg?.certRequired && !isCert && !v.startsWith("Share360") && <Lock className="w-3 h-3 text-[#BCB2A4] flex-shrink-0" />}
                  {(v === "Share360 Business" || v === "Share360 Nonprofit") && share360EnrollmentPending && (
                    <span className="text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 leading-none">ENROLL</span>
                  )}
                  {isExpanded ? <ChevronDown className="w-3 h-3 flex-shrink-0 text-[#BCB2A4]" /> : <ChevronRight className="w-3 h-3 flex-shrink-0 text-[#BCB2A4]" />}
                </button>
                {/* Subtab navigation is handled by the center-screen tab bar — no sidebar submenu needed */}
              </div>
            );
          })}

          {/* ACCOUNT */}
          <div className="pt-2 pb-1">
            <div className="text-[9px] font-bold text-[#BCB2A4] uppercase tracking-widest px-3 mb-1">Account</div>
          </div>
          {[
            { key: "commissions"   as TopSection, label: "Commissions",      icon: DollarSign },
            { key: "myteam"        as TopSection, label: "My Team",          icon: Users2 },
            { key: "assistant"     as TopSection, label: "Dawn AI",          icon: MessageSquare },
            { key: "certifications" as TopSection, label: "Certifications",  icon: Award },
            { key: "account"       as TopSection, label: "Account Settings", icon: Settings },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setActiveSection(key); setMobileSidebarOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all ${activeSection === key ? "bg-[#FBEEE1] text-[#C75E12] font-semibold" : "text-[#6E665A] hover:text-[#2C2723] hover:bg-[#F4EEE3]"}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" /> {label}
            </button>
          ))}
        </nav>

        {/* Log out */}
        <div className="p-3 border-t border-[#EBE2D4] flex-shrink-0">
          <button onClick={() => setRepCode(null)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#9C9285] hover:text-[#6E665A] hover:bg-[#F4EEE3] transition-all">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="lg:ml-60 flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white/90 backdrop-blur-sm border-b border-[#EBE2D4] px-4 lg:px-6 py-3.5 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileSidebarOpen(v => !v)}
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
            <h1 className="text-[22px] font-semibold text-[#2C2723] leading-tight" style={{ fontFamily: "'Spectral',serif", letterSpacing: "-0.01em" }}>
              {activeSection === "overview" && "Dashboard"}
              {activeSection === "allleads" && "My Leads"}
              {activeSection === "commissions" && "Commissions"}
              {activeSection === "myteam" && "My Team"}
              {activeSection === "certifications" && "Certifications"}
              {activeSection === "appointments" && "Calendar"}
              {activeSection === "account" && "Account Settings"}
              {activeSection === "assistant" && "Dawn AI"}
              {activeSection === "vertical" && activeVertical && (
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: VERTICAL_CONFIG[activeVertical]?.color ?? "#84ab4a" }} />
                  {activeVertical}
                  <ChevronRight className="w-4 h-4 text-[#BCB2A4] inline" />
                  <span className="text-[18px] text-[#6E665A]">{SUB_TABS.find(t => t.key === activeSubTab)?.label ?? activeSubTab}</span>
                </span>
              )}
            </h1>
            <p className="text-[11.5px] text-[#9C9285] mt-0.5 font-medium">
              {activeSection === "overview" ? "Overview" :
               activeSection === "allleads" ? "All verticals" :
               activeSection === "appointments" ? "Appointments & booking" :
               activeSection === "account" ? "Profile & preferences" :
               activeSection === "certifications" ? "Training & badges" :
               activeSection === "assistant" ? "Your AI assistant" :
               activeSection === "myteam" ? "Associates" :
               activeSection === "commissions" ? "Earnings & payouts" :
               `${rep.repCode} · ${agentLevel}`}
            </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-[#6E665A] font-medium hidden sm:block pr-1">
              {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </span>
            <button
              onClick={() => setShowFloatingDawn(v => !v)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${showFloatingDawn ? "bg-[#FBEEE1] border-[#E2701F] text-[#E2701F]" : "bg-white border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3] hover:border-[#D4C9B8] hover:text-[#E2701F]"}`}
              title="Ask Dawn"
            >
              <Sparkles className="w-4 h-4" />
            </button>
            <ReferralMilestoneBell repCode={repCode ?? ""} />
            <AnnouncementBell />
          </div>
        </header>

        <div className="flex-1 p-5 lg:p-7">
          {/* ── OVERVIEW / DASHBOARD ── */}
          {activeSection === "overview" && (
            <div className="space-y-8">

              {/* Hero Banner */}
              <div className="rounded-2xl p-7 lg:p-8 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2E2722 0%, #3A2F25 50%, #46342A 100%)" }}>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 88% -40%, rgba(226,112,31,0.38) 0%, transparent 58%)" }} />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 10% 130%, rgba(127,166,62,0.22) 0%, transparent 55%)" }} />
                <div className="relative flex flex-col lg:flex-row items-start gap-6">
                  {/* Left: greeting */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-white/50 uppercase tracking-widest mb-3 font-semibold">{greetingDay} · {greetingDate}</p>
                    <h2 className="text-3xl font-bold text-white leading-snug mb-2" style={{ fontFamily: "'Spectral', serif" }}>
                      Good {greetingTime}, {rep.firstName}.<br />
                      {(() => {
                        const msg: string = (heroMessageQuery.data as string | null | undefined) || "Let's make today count.";
                        const lastWord = msg.trim().split(" ").pop() ?? "";
                        const rest = msg.trim().slice(0, msg.trim().length - lastWord.length).trimEnd();
                        return rest ? (
                          <><span>{rest} </span><em className="not-italic text-[#E2701F]">{lastWord}</em></>
                        ) : (
                          <em className="not-italic text-[#E2701F]">{msg}</em>
                        );
                      })()}
                    </h2>
                    <p className="text-sm text-white/68 max-w-lg leading-relaxed mb-6">{heroSummary}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => { setActiveSection("appointments"); setMobileSidebarOpen(false); }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-white text-[#2C2723] text-sm font-semibold rounded-xl hover:bg-[#FFF4E7] transition-colors shadow-sm"
                      >
                        <Calendar className="w-4 h-4" /> View Calendar
                      </button>
                      <button
                        onClick={() => { setActiveSection("allleads"); setMobileSidebarOpen(false); }}
                        className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors"
                        style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}
                      >
                        <Users className="w-4 h-4" /> My Leads
                      </button>
                    </div>
                  </div>
                  {/* Right: all 4 Associate Dashboard KPIs, merged into the hero panel
                      per client request (2026-09-07) so they're not duplicated in a
                      separate card row below. Values/calculations are copied verbatim
                      from the former KPI Cards row, not reimplemented. Do not
                      duplicate this block elsewhere. */}
                  <div className="flex-shrink-0 grid grid-cols-2 gap-3 w-full lg:w-auto lg:min-w-[320px]">
                    {[
                      { label: "Total Leads",      value: _allLeads.length,               icon: Users,      badge: leadsThisMonth > 0 ? `+${leadsThisMonth} this mo` : null },
                      { label: "Active Clients",   value: _allLeads.filter(l => l.stage === "active_client").length, icon: Briefcase, badge: newActiveThisQuarter > 0 ? `+${newActiveThisQuarter} this qtr` : null },
                      { label: "Conversion Rate",  value: _allLeads.length > 0 ? `${Math.round((_allLeads.filter(l => l.stage === "active_client").length / _allLeads.length) * 100)}%` : "0%", icon: TrendingUp, badge: null },
                      { label: "Commission · MTD", value: fmt(commissionMTD),              icon: DollarSign, badge: commissionDelta !== 0 ? `${commissionDelta > 0 ? "+" : ""}${fmt(Math.abs(commissionDelta))}` : null },
                    ].map(({ label, value, icon: Icon, badge }) => (
                      <div key={label} className="rounded-xl p-3.5" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.13)" }}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon className="w-3 h-3 text-white/50 flex-shrink-0" />
                          <div className="text-[10px] text-white/50 uppercase tracking-widest font-semibold truncate">{label}</div>
                        </div>
                        <div className="text-xl font-bold text-white" style={{ fontFamily: "'Spectral', serif" }}>{value}</div>
                        {badge && <div className="text-xs mt-0.5 font-semibold text-[#84ab4a]">{badge}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Associate Tools + Media Flywheel: two-column on desktop/tablet,
                  stacked on mobile. Associate Tools card is unchanged (Team Invite
                  Link plus the two BizConnect360 tool links); the flywheel is new
                  admin-managed promotional content. Do not duplicate either
                  card elsewhere. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
              {(() => {
                const inviteUrl = inviteLinkQuery.data?.inviteUrl;
                // "View all" on the shareable-links panel lands on the full per-vertical
                // Referral Links tab, using whichever vertical this associate actually has.
                const firstShareableVertical = agentVerticals.find(v => v !== "BizConnect360") ?? null;
                const quickAccessLinks = [
                  ...(inviteUrl
                    ? [{
                        key: "invite",
                        icon: UserPlus,
                        title: "Team Invite Link",
                        sub: "Share to invite new associates to your team",
                        url: inviteUrl,
                        onCopy: () => toast.success("Invite link copied!"),
                      }]
                    : []),
                  {
                    key: "calculator",
                    icon: Calculator,
                    title: "BizConnect360 Income Calculator",
                    sub: "Calculate your income across every BizConnect360 Vertical",
                    url: "https://calculator.bizconnect360.com/",
                    onCopy: () => toast.success("Calculator link copied!"),
                  },
                  {
                    key: "portfolio",
                    icon: LayoutGrid,
                    title: "Share360 Portfolio Calculator",
                    sub: "See what your Share360 portfolio could be worth",
                    url: "https://portfolio.bizconnect360.com/",
                    onCopy: () => toast.success("Portfolio link copied!"),
                  },
                ];
                return (
                  <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-5 flex flex-col h-full">
                    <div className="text-[14px] font-bold text-[#2C2723] mb-1" style={{ fontFamily: "'Spectral', serif" }}>Associate Tools</div>
                    <div className="text-[11px] text-[#9C9285] mb-3">Tools available to help you build and grow.</div>
                    <div className="divide-y divide-[#EBE2D4]">
                      {quickAccessLinks.map(({ key, icon: Icon, title, sub, url, onCopy }) => (
                        <div key={key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 first:pt-0 last:pb-0">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#E2701F18", border: "1.5px solid #E2701F35" }}>
                              <Icon className="w-5 h-5" style={{ color: "#E2701F" }} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[13.5px] font-bold text-[#2C2723]">{title}</div>
                              <div className="text-[11px] text-[#9C9285]">{sub}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-shrink-0">
                            <button
                              onClick={() => { navigator.clipboard.writeText(url); onCopy(); }}
                              className="flex items-center justify-center gap-1.5 text-[12px] font-semibold py-2 px-3 rounded-xl bg-[#E2701F] text-white hover:bg-[#C75E12] transition-colors"
                            >
                              <Copy className="w-3 h-3" /> Copy Link
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1.5 text-[12px] font-semibold py-2 px-3 rounded-xl border border-[#EBE2D4] text-[#6E665A] hover:bg-[#F4EEE3] transition-colors"
                            >
                              Open Link <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Trackable Shareable Links — replaces the Upcoming
                        Appointments panel that previously filled the lower half
                        of this card (client request, 2026-09-17). Appointments
                        themselves are untouched: they still have their own
                        Appointments section in the sidebar and still feed the
                        hero summary above. */}
                    <TrackableShareableLinks
                      repCode={rep.repCode}
                      verticals={agentVerticals}
                      onViewAll={firstShareableVertical ? () => navigateToVertical(firstShareableVertical, "referrallinks") : null}
                    />
                  </div>
                );
              })()}
              <FlywheelCarousel />
              </div>

              {/* This Week at BizConnect360 */}
              <div>
                <h2 className="text-lg font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>This Week at BizConnect360</h2>
                <TrainingDashboardSection
                  repCode={rep.repCode}
                  certifiedVerticals={certifiedVerticals}
                  allowedVerticals={repAllowedVerticals}
                  repFirstName={rep.firstName}
                  repLastName={rep.lastName}
                  repEmail={(rep as any).email}
                  repPhone={(rep as any).phone ?? undefined}
                />
              </div>

              {/* My Verticals grid */}
              {agentVerticals.filter(v => v !== "BizConnect360").length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-[#2C2723] mb-4" style={{ fontFamily: "'Spectral', serif" }}>My Verticals</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agentVerticals.filter(v => v !== "BizConnect360").map(v => {
                      const cfg = VERTICAL_CONFIG[v];
                      const color = cfg?.color ?? "#84ab4a";
                      const isCert = isVerticalCertified(v);
                      const isShare360 = v.startsWith("Share360");
                      const vLeads = ((leadsQuery.data ?? []) as LeadRow[]).filter(l => {
                        if (!isShare360) return l.verticalName === v;
                        // sh360Role is the canonical signal for Business vs Nonprofit routing.
                        // Using startsWith("Share360") here would double-count the same leads
                        // across both sub-vertical cards (the bug this fixes).
                        const r = (l as any).sh360Role as string | null | undefined;
                        const isS360Family = (l.verticalName ?? "").startsWith("Share360") || r === "nonprofit" || r === "business";
                        if (!isS360Family) return false;
                        if (v === "Share360 Nonprofit") return r === "nonprofit" || l.verticalName === "Share360 Nonprofit";
                        if (v === "Share360 Business")  return r === "business"  || (r == null && l.verticalName === "Share360 Business");
                        return false;
                      });
                      const activeClients = vLeads.filter(l => l.stage === "active_client").length;
                      const mtdMs = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
                      const mtdComm = ((commissionsQuery.data ?? []) as any[])
                        .filter(c => c.vertical === v && new Date(c.createdAt).getTime() >= mtdMs)
                        .reduce((s: number, c: any) => s + Number(c.amount ?? 0), 0);
                      return (
                        <button
                          key={v}
                          onClick={() => navigateToVertical(v)}
                          className="bg-white border border-[#EBE2D4] rounded-[18px] p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-[#E2D6C3] transition-all text-left relative overflow-hidden"
                        >
                          {/* Left accent bar */}
                          <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[18px]" style={{ background: color }} />
                          <div className="flex items-center gap-2.5 mb-4">
                            <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: color }}>
                              {v.charAt(0)}
                            </div>
                            <div>
                              <div className="text-[14px] font-bold text-[#2C2723] leading-snug">{v}</div>
                              <div className="text-[10.5px] text-[#9C9285] font-medium mt-0.5">{VERTICAL_TAGS[v] ?? ""}</div>
                            </div>
                          </div>
                          <div className="flex gap-6 mb-4">
                            <div>
                              <div className="text-[23px] font-semibold text-[#2C2723] leading-none" style={{ fontFamily: "'Spectral',serif" }}>{vLeads.length}</div>
                              <div className="text-[10px] font-bold text-[#9C9285] uppercase tracking-wide mt-1">Leads</div>
                            </div>
                            <div>
                              <div className="text-[23px] font-semibold text-[#2C2723] leading-none" style={{ fontFamily: "'Spectral',serif" }}>{activeClients}</div>
                              <div className="text-[10px] font-bold text-[#9C9285] uppercase tracking-wide mt-1">Clients</div>
                            </div>
                            <div>
                              <div className="text-[23px] font-semibold text-[#2C2723] leading-none" style={{ fontFamily: "'Spectral',serif" }}>{fmt(mtdComm)}</div>
                              <div className="text-[10px] font-bold text-[#9C9285] uppercase tracking-wide mt-1">MTD</div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-3.5 border-t border-[#F1EADD]">
                            <span className="text-[11.5px] font-semibold text-[#6E665A] flex items-center gap-1">View pipeline <ChevronRight className="w-3.5 h-3.5" /></span>
                            {cfg?.certRequired && isCert && (
                              <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#5F8A2E] bg-[#EFF4E2] px-2.5 py-1 rounded-[7px]">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg> Certified
                              </span>
                            )}
                            {cfg?.certRequired && !isCert && !isShare360 && (
                              <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#9C9285] bg-[#F4EEE3] px-2.5 py-1 rounded-[7px]">
                                <Lock className="w-3 h-3" /> Cert required
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Leads */}
              {((leadsQuery.data ?? []) as LeadRow[]).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>Recent Leads</h2>
                      <p className="text-sm text-gray-900 mt-0.5">Across all verticals · most recently added</p>
                    </div>
                    <button
                      onClick={() => { setActiveSection("allleads"); setMobileSidebarOpen(false); }}
                      className="text-sm font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1"
                    >
                      View all leads <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
                    {/* Leads table */}
                    <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#F1EADD] bg-[#FBF7F0]">
                            {["Contact", "Vertical", "Stage", "Added"].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-[11px] font-bold text-[#9C9285] uppercase tracking-wide">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {((leadsQuery.data ?? []) as LeadRow[]).slice(0, 6).map(lead => {
                            const lColor = VERTICAL_CONFIG[lead.verticalName ?? ""]?.color ?? "#84ab4a";
                            const contactName = stripRepCode([lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown", lead.repCode) ?? "Unknown";
                            const isS360 = (lead.verticalName ?? "").startsWith("Share360");
                            const rawOrg = isS360 ? ((lead as any).sh360OrgName || (lead as any).sh360BusinessName || null) : ((lead as any).f360BusinessName || lead.businessName || null);
                            const orgName = rawOrg ? (stripRepCode(String(rawOrg), lead.repCode) ?? String(rawOrg)) : null;
                            const addedDate = new Date((lead as any).createdAt ?? 0).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                            return (
                              <tr
                                key={lead.id}
                                className="border-b border-[#F1EADD] last:border-0 hover:bg-[#FBF7F0] cursor-pointer transition-colors"
                                onClick={() => { setActiveSection("allleads"); setSelectedLeadsPageId(lead.id); setMobileSidebarOpen(false); }}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-[9px] flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: lColor }}>
                                      {lead.firstName?.[0]}{lead.lastName?.[0] ?? ""}
                                    </div>
                                    <div>
                                      <div className="font-semibold text-[13px] text-[#2C2723]">{contactName}</div>
                                      {orgName && <div className="text-[11px] text-[#9C9285]">{orgName}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {lead.verticalName && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: lColor }}>{lead.verticalName}</span>
                                  )}
                                  <Legacy360SubtypeBadge lead={lead} />
                                </td>
                                <td className="px-4 py-3">
                                  <StageBadge stage={lead.stage} ghlStageName={(lead as any).ghlStageName} />
                                </td>
                                <td className="px-4 py-3 text-[12px] text-[#9C9285]">{addedDate}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    </div>
                    {/* Right: Pipeline breakdown */}
                    <div className="flex flex-col gap-4">
                      {(() => {
                        const primaryV = agentVerticals.find(v => v !== "BizConnect360") ?? "";
                        const primaryLeads = ((leadsQuery.data ?? []) as LeadRow[]).filter(l => l.verticalName === primaryV);
                        const stageGroups = STAGES
                          .map(s => ({ label: s.label, count: primaryLeads.filter(l => l.stage === s.key).length }))
                          .filter(s => s.count > 0);
                        const maxCount = Math.max(...stageGroups.map(s => s.count), 1);
                        return stageGroups.length > 0 ? (
                          <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-1">
                              <BarChart3 className="w-4 h-4 text-[#E2701F]" />
                              <span className="text-[14px] font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>Pipeline</span>
                            </div>
                            <div className="text-[11px] text-[#9C9285] mb-4">{primaryV} · stage breakdown</div>
                            <div className="space-y-3">
                              {stageGroups.map(s => (
                                <div key={s.label} className="flex items-center gap-3">
                                  <div className="text-[11px] text-[#6E665A] w-24 flex-shrink-0 truncate">{s.label.split(" ")[0]}</div>
                                  <div className="flex-1 h-1.5 bg-[#F1EADD] rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-[#84ab4a]" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                                  </div>
                                  <div className="text-[12px] font-bold text-[#2C2723] w-4 text-right">{s.count}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* ── ALL LEADS ── */}
          {activeSection === "allleads" && (() => {
            const allLeadsData = (leadsQuery.data ?? []) as LeadRow[];
            const filteredLeads = allLeadsData.filter(l => {
              const matchesVertical = leadsFilterVertical === "all" || l.verticalName === leadsFilterVertical;
              if (!matchesVertical) return false;
              if (!leadsSearch.trim()) return true;
              const q = leadsSearch.toLowerCase();
              const name = [l.firstName, l.lastName].filter(Boolean).join(" ").toLowerCase();
              const biz = ((l as any).businessName || (l as any).sh360OrgName || (l as any).sh360BusinessName || "").toLowerCase();
              return name.includes(q) || biz.includes(q) || (l.email ?? "").toLowerCase().includes(q);
            });
            const selectedLeadDetail = selectedLeadsPageId ? allLeadsData.find(l => l.id === selectedLeadsPageId) ?? null : null;

            return (
            <div>
              {/* Toolbar */}
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2 bg-white border border-[#EBE2D4] rounded-xl px-3.5 py-2.5 flex-1 min-w-[200px] max-w-[340px] shadow-sm focus-within:border-[#E2701F] focus-within:ring-1 focus-within:ring-[#E2701F]/20 transition-all">
                  <Search className="w-4 h-4 text-[#9C9285] flex-shrink-0" />
                  <input
                    className="flex-1 bg-transparent outline-none text-[13.5px] text-[#2C2723] placeholder-[#BCB2A4]"
                    placeholder="Search by name or business…"
                    value={leadsSearch}
                    onChange={e => setLeadsSearch(e.target.value)}
                  />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {(["all", ...Array.from(new Set(allLeadsData.map(l => l.verticalName).filter(Boolean)))] as string[]).map(v => {
                    const vColor = v === "all" ? null : VERTICAL_CONFIG[v]?.color;
                    const isActive = leadsFilterVertical === v;
                    return (
                      <button
                        key={v}
                        onClick={() => setLeadsFilterVertical(v)}
                        className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-[10px] border transition-all ${isActive ? "bg-[#2C2723] text-white border-[#2C2723]" : "bg-white text-[#6E665A] border-[#EBE2D4] hover:border-[#D4C9B8] hover:text-[#2C2723]"}`}
                      >
                        {vColor && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: vColor }} />}
                        {v === "all" ? `All ${allLeadsData.length}` : v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Master-detail layout */}
              <div className="flex flex-col lg:flex-row gap-4 items-start">
                {/* Lead list */}
                <div className="w-full lg:w-[380px] flex-shrink-0 bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm overflow-hidden">
                  {filteredLeads.length === 0 ? (
                    <div className="py-16 text-center">
                      <Users className="w-10 h-10 mx-auto mb-3 text-[#BCB2A4]" />
                      <p className="font-semibold text-[#6E665A] text-sm">{leadsSearch || leadsFilterVertical !== "all" ? "No matching leads" : "No leads yet"}</p>
                      <p className="text-xs text-[#9C9285] mt-1">{leadsSearch || leadsFilterVertical !== "all" ? "Try clearing filters" : "Share your referral link to start capturing leads!"}</p>
                    </div>
                  ) : filteredLeads.map(lead => {
                    const lColor = VERTICAL_CONFIG[lead.verticalName ?? ""]?.color ?? "#84ab4a";
                    const contactName = stripRepCode([lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown", lead.repCode) ?? "Unknown";
                    const isS360 = (lead.verticalName ?? "").startsWith("Share360");
                    const meta = (lead as any).metadata as Record<string, unknown> | null | undefined;
                    const metaOrg = meta ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || "").trim() : "";
                    const rawOrg = isS360 ? ((lead as any).sh360OrgName || (lead as any).sh360BusinessName || metaOrg || null) : ((lead as any).f360BusinessName || lead.businessName || null);
                    const orgName = rawOrg ? (stripRepCode(String(rawOrg), lead.repCode) ?? String(rawOrg)) : null;
                    const isSelected = selectedLeadsPageId === lead.id;
                    const addedAgo = (() => {
                      const ms = Date.now() - new Date((lead as any).createdAt ?? 0).getTime();
                      const h = Math.floor(ms / 3600000);
                      if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
                      if (h < 24) return `${h}h ago`;
                      return `${Math.floor(h / 24)}d ago`;
                    })();
                    return (
                      <button
                        key={lead.id}
                        onClick={() => {
                          setSelectedLeadsPageId(isSelected ? null : lead.id);
                          // Open the full detail drawer to show all form data from the landing page
                          setSelectedLeadId(lead.id);
                        }}
                        className={`w-full flex gap-3 items-center text-left px-4 py-3.5 border-b border-[#F1EADD] last:border-0 transition-all ${isSelected ? "bg-[#FBEEE1]" : "hover:bg-[#FBF7F0]"}`}
                      >
                        <div className="w-9 h-9 rounded-[11px] flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ fontFamily: "'Spectral',serif", background: lColor }}>
                          {lead.firstName[0]}{lead.lastName?.[0] ?? ""}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="font-semibold text-[13.5px] text-[#2C2723] truncate">{contactName}</span>
                            <span className="text-[11px] text-[#BCB2A4] flex-shrink-0">{addedAgo}</span>
                          </div>
                          {orgName && <div className="text-[11.5px] text-[#9C9285] truncate mb-1.5">{orgName}</div>}
                          <div className="flex gap-1.5 flex-wrap">
                            {lead.verticalName && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: lColor }}>{lead.verticalName}</span>
                            )}
                            <Legacy360SubtypeBadge lead={lead} />
                            <StageBadge stage={lead.stage} ghlStageName={(lead as any).ghlStageName} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Detail panel */}
                <div className="flex-1 min-w-0">
                  {!selectedLeadDetail ? (
                    <div className="h-64 flex flex-col items-center justify-center text-center bg-white border border-dashed border-[#EBE2D4] rounded-[18px]">
                      <Users className="w-10 h-10 text-[#BCB2A4] mb-3" />
                      <p className="font-semibold text-[#6E665A] text-sm">Select a lead to view details</p>
                      <p className="text-xs text-[#9C9285] mt-1">Click any row on the left</p>
                    </div>
                  ) : (() => {
                    const dl = selectedLeadDetail;
                    const dlColor = VERTICAL_CONFIG[dl.verticalName ?? ""]?.color ?? "#84ab4a";
                    const dlName = stripRepCode([dl.firstName, dl.lastName].filter(Boolean).join(" ") || dl.email?.split("@")[0] || "Unknown", dl.repCode) ?? "Unknown";
                    const isS360d = (dl.verticalName ?? "").startsWith("Share360");
                    const metad = (dl as any).metadata as Record<string, unknown> | null | undefined;
                    const metaOrgd = metad ? String(metad["SH360 Org Name"] || metad["SH360 Business Name"] || metad["Organization Name"] || metad["Business Name"] || "").trim() : "";
                    const rawOrgd = isS360d ? ((dl as any).sh360OrgName || (dl as any).sh360BusinessName || metaOrgd || null) : ((dl as any).f360BusinessName || dl.businessName || null);
                    const orgNameD = rawOrgd ? (stripRepCode(String(rawOrgd), dl.repCode) ?? String(rawOrgd)) : null;
                    const _ghlStage = (dl as any).ghlStageName as string | null | undefined;
                    return (
                      <div className="flex flex-col gap-4">
                        {/* Header card */}
                        <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-5">
                          <div className="flex items-start gap-4 mb-5">
                            <div className="w-14 h-14 rounded-[14px] flex items-center justify-center text-xl font-bold text-white flex-shrink-0" style={{ fontFamily: "'Spectral',serif", background: dlColor }}>
                              {dl.firstName[0]}{dl.lastName?.[0] ?? ""}
                            </div>
                            <div className="flex-1 min-w-0 mt-0.5">
                              <div className="text-[21px] font-semibold text-[#2C2723] leading-snug" style={{ fontFamily: "'Spectral',serif" }}>{dlName}</div>
                              {orgNameD && <div className="text-[13px] text-[#6E665A] mt-0.5 mb-1">{orgNameD}</div>}
                              <div className="flex items-center gap-2">
                                {dl.verticalName && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: dlColor }}>{dl.verticalName}</span>}
                                <Legacy360SubtypeBadge lead={dl} />
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <ScheduleConsultationButton lead={dl} displayName={dlName} />
                              <button onClick={() => setSelectedLeadId(dl.id)} className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6E665A] bg-[#F4EEE3] hover:bg-[#EBE2D4] px-3 py-2 rounded-[10px] transition-colors">
                                <FileText className="w-3.5 h-3.5" /> Full Details
                              </button>
                            </div>
                          </div>
                          {/* Pipeline stage — live GHL stages via LeadPipelineBar */}
                          <LeadPipelineBar
                            verticalName={dl.verticalName}
                            ghlStageName={_ghlStage}
                            color={dlColor}
                            l360LeadType={(dl as any).l360LeadType}
                            ghlPipelineId={(dl as any).ghlPipelineId}
                          />
                        </div>
                        {/* Legacy360 Living Trust — pre-appointment checklist (server-enforced consultation gate) */}
                        {dl.verticalName === "Legacy360" && resolveLegacy360Subtype(dl as any) === "living_trust" && (
                          <Legacy360PreAppointmentChecklist
                            leadId={dl.id}
                            checklist={((dl as any).metadata?.l360PreAppointmentChecklist) ?? null}
                          />
                        )}
                        {/* Share360 Business/Nonprofit - Associate's Next Steps (server-enforced consultation gate) */}
                        {(dl.verticalName === "Share360 Business" || dl.verticalName === "Share360 Nonprofit") && (
                          <Share360NextStepsChecklist leadId={dl.id} isNonprofit={dl.verticalName === "Share360 Nonprofit"} />
                        )}
                        {/* Dawn AI Lead Intelligence */}
                        <div className="rounded-[18px] overflow-hidden shadow-sm" style={{ background: "linear-gradient(135deg, #2E2722 0%, #46342A 100%)" }}>
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#F4A35A" }} />
                              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#F4A35A" }}>Dawn AI · Lead Intelligence</span>
                            </div>
                            <p className="text-[13.5px] leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                              {(() => {
                                const name = [dl.firstName, dl.lastName].filter(Boolean).join(" ");
                                const stageName = _ghlStage || STAGES.find(s => s.key === dl.stage)?.label || dl.stage;
                                // "just entered" only when GHL confirms the lead is still at the initial capture stage
                                const isAtStart = !_ghlStage
                                  ? dl.stage === "lead_captured"
                                  : _ghlStage === "Lead Captured" || _ghlStage === "New Lead";
                                return `${name} ${isAtStart ? "just entered" : `is at the "${stageName}" stage in`} the ${dl.verticalName ?? "vertical"} pipeline.`;
                              })()}
                            </p>
                            <div className="mt-4 rounded-xl p-3.5" style={{ background: "rgba(244,163,90,0.12)", border: "1px solid rgba(244,163,90,0.28)" }}>
                              <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-3 h-3 flex-shrink-0" style={{ color: "#F4A35A" }} />
                                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#F4A35A" }}>Recommended Action</span>
                              </div>
                              <p className="text-[13px] leading-relaxed" style={{ color: "rgba(255,255,255,0.90)" }}>
                                {(() => {
                                  const firstName = dl.firstName ?? "this lead";
                                  const vertical = dl.verticalName ?? "vertical";
                                  // Scale360: full 10-stage pipeline with accurate next-step copy per stage.
                                  // Match on _ghlStage (live GHL string) first — it is always more current
                                  // than dl.stage (DB field, only updated on webhook). Fall back to dl.stage
                                  // key labels when ghlStageName is absent.
                                  if (vertical === "Scale360") {
                                    const g = (_ghlStage ?? "").toLowerCase();
                                    // Stage 1 — Lead Captured / New Lead
                                    if (!_ghlStage
                                        ? dl.stage === "lead_captured"
                                        : g === "lead captured" || g === "new lead")
                                      return `Reach out to ${firstName} to introduce Scale360 and send them the audit form link. Their business profile hasn't been submitted yet.`;
                                    // Stage 2 — Audit Submitted
                                    if (g === "audit submitted" || (!_ghlStage && dl.stage === "audit_submitted"))
                                      return `${firstName} has submitted the audit form. Review the submission and prepare the business analysis — do not ask them to redo the audit.`;
                                    // Stage 3 — Analysis Report (internal review)
                                    if (g === "analysis report")
                                      return `The analysis report for ${firstName} is ready for internal review. Verify the findings before presenting to the client.`;
                                    // Stage 4 — Analysis Refinement Complete
                                    if (g.includes("analysis refinement"))
                                      return `The analysis for ${firstName} has been refined and is ready to present. Schedule a call to walk them through the results and answer questions.`;
                                    // Stage 5 — Analysis Submitted (delivered to client)
                                    if (g === "analysis submitted" || (!_ghlStage && dl.stage === "analysis_submitted"))
                                      return `The analysis has been delivered to ${firstName}. Follow up to make sure they received it, answer any questions, and guide them toward choosing a plan.`;
                                    // Stage 6 — Follow-Up Active
                                    if (g === "follow-up active" || g === "follow up active" || (!_ghlStage && dl.stage === "follow_up_active"))
                                      return `${firstName} is actively engaged. Keep the conversation going — address objections, clarify the plan options, and move them toward a decision.`;
                                    // Stage 7 — Payment Received
                                    if (g === "payment received" || (!_ghlStage && dl.stage === "payment_received"))
                                      return `${firstName} has made their payment. Begin the onboarding process and confirm next steps with them promptly.`;
                                    // Stage 8 — Onboarding Submitted / In Progress
                                    if (g.includes("onboarding") || (!_ghlStage && dl.stage === "onboarding_submitted"))
                                      return `${firstName} is in onboarding. Confirm all required documents are submitted and keep them informed of the timeline.`;
                                    // Stage 9 — Active Client
                                    if (g === "active client" || g === "funded" || (!_ghlStage && dl.stage === "active_client"))
                                      return `${firstName} is an active Scale360 client. Check in periodically, ask for referrals, and flag any issues to your upline early.`;
                                    // Stage 10 — Lost / Inactive / Closed
                                    if (g.includes("lost") || g.includes("inactive") || g.includes("closed"))
                                      return `${firstName} is marked as lost or inactive. If appropriate, consider a re-engagement outreach with new context or a different angle.`;
                                    // Unknown GHL stage — generic Scale360 fallback
                                    return `Continue the Scale360 process with ${firstName}. Check the pipeline for their current stage and follow up accordingly.`;
                                  }
                                  // Share360 Nonprofit — full stage-aware logic
                                  if (vertical === "Share360 Nonprofit" || vertical === "Share360") {
                                    const g = (_ghlStage ?? "").toLowerCase();
                                    if (g === "appointment booked")
                                      return `${firstName} has a consultation booked. Prepare to walk them through the Share360 Nonprofit program and what to expect on the call.`;
                                    if (g === "booked follow up" || g === "follow up booked")
                                      return `A follow-up is scheduled with ${firstName}. Confirm the details and come prepared to address any outstanding questions.`;
                                    if (g.includes("charity document"))
                                      return `${firstName} needs to submit their charity documents. Follow up to confirm they received the secure portal link and guide them through what's required.`;
                                    if (g.includes("charity package"))
                                      return `${firstName}'s charity package has been submitted. Follow up on the processing status and keep them informed of next steps.`;
                                    if (g === "collateral review")
                                      return `${firstName} is reviewing the Share360 collateral. Follow up to confirm they've had a chance to review it and address any questions before moving forward.`;
                                    if (g.includes("onboarded") || g.includes("receiving donation"))
                                      return `${firstName} is an active Share360 Nonprofit client. Check in periodically and ask for referrals when the time is right.`;
                                    if (g === "inactive")
                                      return `${firstName} is marked inactive. Consider a re-engagement outreach with a fresh angle if the timing is appropriate.`;
                                    return `Schedule a Share360 Nonprofit consultation using the booking link in the lead drawer. Pre-populate their contact details to reduce friction.`;
                                  }

                                  // Share360 Business — full stage-aware logic
                                  if (vertical === "Share360 Business") {
                                    const g = (_ghlStage ?? "").toLowerCase();
                                    if (g === "appointment booked")
                                      return `${firstName} has a consultation booked. Prepare to walk them through the Share360 Business program and what to expect.`;
                                    if (g === "statements")
                                      return `${firstName} needs to submit 3 months of merchant processing statements. Follow up and remind them to send these in to keep things moving.`;
                                    if (g === "rate review")
                                      return `Review ${firstName}'s current processing rates and build a cost comparison showing Share360 savings. Be ready to walk them through it.`;
                                    if (g.includes("merch info"))
                                      return `${firstName} needs to complete the merchant info form. Follow up and offer to walk them through it step by step.`;
                                    if (g === "application")
                                      return `${firstName}'s application has been submitted. Follow up to confirm receipt and set expectations for the underwriting timeline.`;
                                    if (g.includes("underwriting"))
                                      return `Underwriting is reviewing ${firstName}'s application. Monitor for any document requests and respond promptly if additional information is needed.`;
                                    if (g.includes("var") || g.includes("deployment"))
                                      return `${firstName} is in the terminal deployment phase. Coordinate setup details and confirm they know what to expect during the process.`;
                                    if (g.includes("onboarded") || g.includes("processing"))
                                      return `${firstName} is an active Share360 Business merchant. Check in periodically and ask for referrals when the time is right.`;
                                    if (g === "declined")
                                      return `${firstName}'s application was declined. If appropriate, discuss alternative options or a path to reapply in the future.`;
                                    if (g === "inactive")
                                      return `${firstName} is marked inactive. Consider a re-engagement outreach if the timing is right.`;
                                    return `Schedule a Share360 Business consultation using the booking link in the lead drawer. Pre-populate their contact details to reduce friction.`;
                                  }

                                  // All other verticals with consultation calendars — stage-aware to avoid re-booking already booked leads
                                  if (CONSULTATION_CALENDARS[vertical]) {
                                    const g = (_ghlStage ?? "").toLowerCase();
                                    if (g.includes("appointment booked") || g === "strategy session scheduled")
                                      return `${firstName} already has an appointment scheduled. Confirm the details and send any prep materials ahead of the call.`;
                                    if (g.includes("appointment completed") || g === "strategy session completed" || g === "consultation completed")
                                      return `The consultation with ${firstName} is complete. Follow up with a clear summary and defined next steps to keep momentum going.`;
                                    if (g === "sent information" || g === "proposal sent")
                                      return `Information has already been sent to ${firstName}. Follow up to confirm they received it and address any questions.`;
                                    if (g === "booked follow up" || g.includes("follow up"))
                                      return `A follow-up is scheduled with ${firstName}. Confirm the appointment and come prepared to address any outstanding questions.`;
                                    if (g.includes("enrolled") || g.includes("active client") || g.includes("funded") || g.includes("trust funded") || g.includes("trust recorded"))
                                      return `${firstName} is enrolled and active. Check in to ensure a smooth experience and ask for referrals when the time is right.`;
                                    if (g.includes("inactive") || g.includes("lost") || g.includes("closed"))
                                      return `${firstName} is marked inactive or lost. Consider a re-engagement outreach with fresh context if the timing is appropriate.`;
                                    return `Schedule a ${vertical} consultation call using the booking link in the lead drawer. Pre-populate their contact details to reduce friction.`;
                                  }

                                  // No calendar and no vertical-specific logic — generic resource follow-up
                                  return `Follow up with ${firstName} and share the ${vertical} overview deck from your Resources tab.`;
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Contact info grid */}
                        <div className="bg-white border border-[#EBE2D4] rounded-[18px] shadow-sm p-5">
                          <div className="text-[11px] font-bold text-[#9C9285] uppercase tracking-wide mb-4">Contact Information</div>
                          <div className="grid grid-cols-2 gap-2.5">
                            {[
                              { label: "First Name",  value: dl.firstName },
                              { label: "Last Name",   value: dl.lastName },
                              { label: "Email",       value: dl.email },
                              { label: "Phone",       value: (dl as any).phone },
                              { label: "Business",    value: orgNameD },
                              { label: "Stage",       value: _ghlStage || STAGES.find(s => s.key === dl.stage)?.label },
                            ].map(({ label, value }) => value ? (
                              <div key={label} className="bg-[#FBF7F0] rounded-[10px] px-3.5 py-2.5">
                                <div className="text-[10px] font-bold text-[#9C9285] uppercase tracking-wide mb-1">{label}</div>
                                <div className="text-[13px] font-semibold text-[#2C2723] truncate">{value}</div>
                              </div>
                            ) : null)}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            );
          })()}

          {/* ── VERTICAL BUCKET ── */}
          {activeSection === "vertical" && activeVertical && (
            <>
              {/* Monthly fee card — visible to approved Share360 RAs on their Share360 verticals */}
              {(activeVertical === "Share360 Business" || activeVertical === "Share360 Nonprofit") &&
                share360EnrollmentStatus === "approved" && (
                  <Share360MonthlyFeeCard
                    repId={(rep as any).id}
                    repCode={rep.repCode}
                    repFirstName={(rep as any).firstName ?? null}
                    repLastName={(rep as any).lastName ?? null}
                    repEmail={(rep as any).email ?? null}
                  />
                )}
              <VerticalBucketContent
                vertical={activeVertical}
                subTab={activeSubTab}
                repCode={rep.repCode}
                agentLevel={agentLevel}
                leads={(leadsQuery.data ?? []) as LeadRow[]}
                allLeads={(leadsQuery.data ?? []) as LeadRow[]}
                onLeadClick={setSelectedLeadId}
                onSubmitLead={() => {}}
                materialsQuery={materialsQuery}
                foldersQuery={foldersQuery}
                certifiedVerticals={certifiedVerticals}
                repId={(rep as any).id}
                repFirstName={(rep as any).firstName ?? null}
                repLastName={(rep as any).lastName ?? null}
                repEmail={(rep as any).email ?? null}
                share360EnrollmentStatus={share360EnrollmentStatus}
                share360FeeBlocked={share360FeeBlocked}
              />
            </>
          )}

          {/* ── COMMISSIONS ── */}
          {activeSection === "commissions" && (
            <SegmentedCommissionsView
              repCode={rep.repCode}
              agentLevel={agentLevel}
              agentVerticals={agentVerticals}
              isVerticalAllowed={isVerticalAllowed}
            />
          )}

          {/* ── CERTIFICATIONS ── */}
          {activeSection === "certifications" && (
            <CertificationQuizTab repCode={rep.repCode} ghlContactId={(rep as any).ghlContactId ?? null} repFirstName={(rep as any).firstName ?? null} repLastName={(rep as any).lastName ?? null} repEmail={(rep as any).email ?? null} />
          )}

          {/* ── APPOINTMENTS ── */}
          {activeSection === "appointments" && (
            <AgentAppointmentsTab repCode={rep.repCode} allowedVerticals={repAllowedVerticals} />
          )}

          {/* ── MY TEAM ── */}
          {activeSection === "myteam" && (
            <MyTeamTab
              repCode={rep.repCode}
              agentLevel={rep.agentLevel ?? "Associate"}
              uplineRepCode={(rep as any).uplineRepCode ?? null}
              inviteLink={inviteLinkQuery.data?.inviteUrl ?? ""}
              maxLevel="Associate"
              downline={downlineQuery.data?.downline ?? []}
              pendingRequests={downlineQuery.data?.pendingRequests ?? []}
              upgradeRequests={upgradeRequestsQuery.data ?? []}
              teamVisibility={teamVisibilityQuery.data ?? null}
              isLoading={inviteLinkQuery.isLoading || downlineQuery.isLoading || teamVisibilityQuery.isLoading}
              onUpgradeRequestSent={() => upgradeRequestsQuery.refetch()}
            />
          )}

          {/* ── ACCOUNT SETTINGS ── */}
          {activeSection === "account" && (
            <AccountSettingsTab repCode={rep.repCode} username={(rep as any).username ?? rep.repCode} repId={rep.id} profilePictureUrl={(rep as any).profilePictureUrl} repFirstName={(rep as any).firstName ?? null} repLastName={(rep as any).lastName ?? null} repEmail={(rep as any).email ?? null} agentLevel={agentLevel} ghlContactId={(rep as any).ghlContactId ?? null} />
          )}

          {/* ── BC360 ASSISTANT ── Keep mounted so the chat thread persists across tab switches */}
          <div className={activeSection === "assistant" ? "" : "hidden"}>
            <AssistantChatPanel repCode={rep.repCode} />
          </div>
        </div>
      </main>

      {/* Lead Detail Drawer */}
      {selectedLeadId !== null && (() => {
        const lead = ((leadsQuery.data ?? []) as LeadRow[]).find((l) => l.id === selectedLeadId) ?? null;
        return (
          <LeadDetailDrawer
            lead={lead as any}
            onClose={() => setSelectedLeadId(null)}
            authorName={`${rep.firstName} ${rep.lastName}`}
            repCode={rep.repCode}
            calendarUrls={lead ? consultationCalendarsForLead(lead as any) : []}
          />
        );
      })()}

      {/* Dawn floating launcher — always accessible without navigating to the Assistant tab */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-3">
        {showFloatingDawn && (
          <div className="w-[calc(100vw-2rem)] sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden max-h-[70vh]" style={{ height: "520px" }}>
            {/* Floating panel header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#0A1628] to-[#0D1E35] flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#84ab4a]/20 flex items-center justify-center">
                  <MessageSquare className="w-3.5 h-3.5 text-[#84ab4a]" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm leading-tight">Dawn</div>
                  <div className="text-white/40 text-[10px]">Your BizConnect360 portal assistant</div>
                </div>
              </div>
              <button onClick={() => setShowFloatingDawn(false)} className="text-white/40 hover:text-white transition-colors p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Chat — uses a separate chatId so history doesn't mix with the full-page tab */}
            <div className="flex-1 min-h-0">
              <AIChatBox
                api="/api/chat"
                chatId={`dawn-float-${rep.repCode}`}
                initialMessages={floatingDawnInitialMessages}
                extraBody={{ repCode: rep.repCode, userTz: Intl.DateTimeFormat().resolvedOptions().timeZone }}
                repCode={rep.repCode}
                voiceAgent="dawn"
                renderToolPart={graceToolRenderer}
                placeholder="Ask about your leads, appointments, or training…"
                emptyStateMessage="Hi! I'm Dawn. Ask me about your pipeline, commissions, upcoming events, or how BizConnect360 works."
                suggestedPrompts={QUICK_QUERIES.map(q => q.query)}
                className="border-none rounded-none shadow-none h-full"
              />
            </div>
          </div>
        )}
        {/* Launcher button — hidden when already on the full-page Assistant tab */}
        {activeSection !== "assistant" && (
          <button
            onClick={() => setShowFloatingDawn(v => !v)}
            className="w-14 h-14 rounded-full bg-[#84ab4a] hover:bg-[#6e9038] text-white shadow-lg flex items-center justify-center transition-all"
            title="Ask Dawn"
          >
            {showFloatingDawn ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
          </button>
        )}
      </div>

    </div>
  );
}
